// Dynamic import keeps pptxgenjs out of the main bundle until export is used.
// Bundled (not CDN) so it works under CSP script-src 'self'.
const loadPptxGenJS = async (): Promise<any> => {
  const mod = await import('pptxgenjs');
  return (mod as any).default ?? mod;
};

export interface StoryTimelineEvent {
  id: string;
  type: 'travel' | 'dwell';
  startTime: Date;
  endTime: Date;
  entityId: string;
  deviceType: string;
  distance?: number;
  pointCount?: number;
  startLocation?: [number, number];
  endLocation?: [number, number];
  tripCount?: number;
  location?: [number, number];
  duration?: number;
  address?: string;
  visitCount?: number;
}

export interface ChartData {
  dailyRhythm: { hour: string; activity: number }[];
  weeklyActivity: { day: string; activity: number }[];
  topLocations: { location: string; visits: number; duration: number }[];
  stats: {
    totalPoints: number;
    uniqueDevices: number;
    totalDwells: number;
    peakHour: number | null;
    avgPointsPerDay: number;
  };
}

export type PowerPointTheme = 'standard' | 'tactical' | 'executive' | 'minimal' | 'midnight' | 'sandstorm';

export interface ThemeColors {
  titleBg: string;
  contentBg: string;
  primary: string;
  secondary: string;
  text: string;
  lightText: string;
  accent: string;
  titleText: string;
  headerBar: string;
}

export const THEME_DEFINITIONS: Record<PowerPointTheme, { name: string; description: string; colors: ThemeColors }> = {
  standard: {
    name: 'Standard Briefing',
    description: 'Professional navy and white',
    colors: {
      titleBg: '1a2e44',
      contentBg: 'ffffff',
      primary: '1a2e44',
      secondary: '5a6978',
      text: '1f2937',
      lightText: '6b7280',
      accent: '2563eb',
      titleText: 'ffffff',
      headerBar: '1a2e44'
    }
  },
  tactical: {
    name: 'Tactical',
    description: 'Dark slate with OD green accents',
    colors: {
      titleBg: '1c2127',
      contentBg: 'f8f9fa',
      primary: '374151',
      secondary: '6b7280',
      text: '1f2937',
      lightText: '6b7280',
      accent: '4b5e40',
      titleText: 'e5e7eb',
      headerBar: '374151'
    }
  },
  executive: {
    name: 'Executive',
    description: 'Navy with copper accent for leadership',
    colors: {
      titleBg: '0f1a2b',
      contentBg: 'fafbfc',
      primary: '1e3a5f',
      secondary: '64748b',
      text: '1e293b',
      lightText: '64748b',
      accent: 'a0714f',
      titleText: 'f1f5f9',
      headerBar: '1e3a5f'
    }
  },
  minimal: {
    name: 'Minimal',
    description: 'High contrast black and white',
    colors: {
      titleBg: '111827',
      contentBg: 'ffffff',
      primary: '111827',
      secondary: '6b7280',
      text: '111827',
      lightText: '6b7280',
      accent: '374151',
      titleText: 'ffffff',
      headerBar: '111827'
    }
  },
  midnight: {
    name: 'Midnight',
    description: 'Deep indigo with silver accents',
    colors: {
      titleBg: '0f0a1e',
      contentBg: 'f8f7fc',
      primary: '312e81',
      secondary: '6366f1',
      text: '1e1b4b',
      lightText: '6b7280',
      accent: '818cf8',
      titleText: 'e0e7ff',
      headerBar: '1e1b4b'
    }
  },
  sandstorm: {
    name: 'Sandstorm',
    description: 'Desert tan with burnt orange accents',
    colors: {
      titleBg: '292017',
      contentBg: 'faf7f2',
      primary: '78350f',
      secondary: '92400e',
      text: '451a03',
      lightText: '78716c',
      accent: 'd97706',
      titleText: 'fef3c7',
      headerBar: '78350f'
    }
  }
};

/** One PTT transmission for the "Radio transcript" slides. */
export interface RadioTranscriptLine {
  sender: string;
  team?: string | null;
  startedAt: Date;
  transcript: string;
}

export interface PowerPointExportOptions {
  title: string;
  subtitle: string;
  events: StoryTimelineEvent[];
  entities: string[];
  mapScreenshot?: MapScreenshotResult;
  timelineMapScreenshots?: Array<MapScreenshotResult | undefined>;
  chartData?: ChartData;
  aiNarrative?: string;
  eventNotes?: Record<string, { id: number; content: string }[]>;
  /** PTT captions in the exported range; adds "Radio transcript" slides after the timeline. */
  radioTranscript?: RadioTranscriptLine[];
  includeMap?: boolean;
  includeSummary?: boolean;
  includeTimeline?: boolean;
  includeCharts?: boolean;
  theme?: PowerPointTheme;
}

export const RADIO_LINES_PER_SLIDE = 16;

const SLIDE_W = 13.333;
const SLIDE_H = 7.5;
const TEMPLATE_ASSETS = { logo: '/pptx-template-pathfinder/assets/logo-pyramid.jpeg' } as const;
export const TIMELINE_EVENTS_PER_SLIDE = 22;

async function fetchAsDataUrl(path: string): Promise<string> {
  const res = await fetch(path);
  if (!res.ok) throw new Error(`Failed to fetch asset: ${path} (${res.status})`);
  const blob = await res.blob();
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error(`Failed to read asset as data URL: ${path}`));
    reader.readAsDataURL(blob);
  });
}

/**
 * Generate smooth gradient background using canvas - no banding artifacts
 */
function generateGradientBackground(width: number, height: number, type: 'title' | 'content', theme: PowerPointTheme): string {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  const colors = THEME_DEFINITIONS[theme].colors;

  if (type === 'title') {
    const baseColor = colors.titleBg;
    const r = parseInt(baseColor.slice(0, 2), 16);
    const g = parseInt(baseColor.slice(2, 4), 16);
    const b = parseInt(baseColor.slice(4, 6), 16);

    ctx.fillStyle = `#${baseColor}`;
    ctx.fillRect(0, 0, width, height);

    // Subtle lighter area in upper center
    const gradient = ctx.createRadialGradient(width * 0.5, height * 0.3, 0, width * 0.5, height * 0.3, width * 0.6);
    gradient.addColorStop(0, `rgba(${Math.min(r + 30, 255)}, ${Math.min(g + 40, 255)}, ${Math.min(b + 50, 255)}, 0.2)`);
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    if (theme === 'executive') {
      const warmGradient = ctx.createRadialGradient(width * 0.5, height * 1.1, 0, width * 0.5, height * 1.1, width * 0.7);
      warmGradient.addColorStop(0, 'rgba(160, 113, 79, 0.1)');
      warmGradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = warmGradient;
      ctx.fillRect(0, 0, width, height);
    }

    if (theme === 'midnight') {
      // Deep purple glow from bottom corner
      const purpleGlow = ctx.createRadialGradient(width * 0.8, height * 0.9, 0, width * 0.8, height * 0.9, width * 0.6);
      purpleGlow.addColorStop(0, 'rgba(99, 102, 241, 0.15)');
      purpleGlow.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = purpleGlow;
      ctx.fillRect(0, 0, width, height);
      // Subtle silver shimmer from top
      const silverShimmer = ctx.createLinearGradient(0, 0, width, height * 0.3);
      silverShimmer.addColorStop(0, 'rgba(224, 231, 255, 0.05)');
      silverShimmer.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = silverShimmer;
      ctx.fillRect(0, 0, width, height * 0.4);
    }

    if (theme === 'sandstorm') {
      // Warm orange glow from lower right - like desert sun
      const sunGlow = ctx.createRadialGradient(width * 0.9, height * 0.7, 0, width * 0.9, height * 0.7, width * 0.5);
      sunGlow.addColorStop(0, 'rgba(217, 119, 6, 0.12)');
      sunGlow.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = sunGlow;
      ctx.fillRect(0, 0, width, height);
      // Dust haze from top
      const dustHaze = ctx.createLinearGradient(0, 0, 0, height * 0.5);
      dustHaze.addColorStop(0, 'rgba(254, 243, 199, 0.08)');
      dustHaze.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = dustHaze;
      ctx.fillRect(0, 0, width, height);
    }
  } else {
    ctx.fillStyle = `#${colors.contentBg}`;
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = `#${colors.headerBar}`;
    ctx.fillRect(0, 0, width, 6);
    ctx.fillStyle = `#${colors.accent}`;
    ctx.globalAlpha = 0.2;
    ctx.fillRect(0, height - 3, width, 3);
    ctx.globalAlpha = 1.0;
  }

  return canvas.toDataURL('image/png');
}

function addFullBleedBackground(slide: any, dataUrl: string) {
  slide.addImage({ data: dataUrl, x: 0, y: 0, w: SLIDE_W, h: SLIDE_H });
}

function addCornerLogo(slide: any, dataUrl: string, opts?: { x?: number; y?: number; w?: number; h?: number }) {
  const { x = SLIDE_W - 1.1, y = 0.15, w = 0.75, h = 0.75 } = opts || {};
  slide.addImage({ data: dataUrl, x, y, w, h });
}

function addFooter(slide: any, footerText: string, textColor = '666666') {
  slide.addText(footerText, { x: 0.5, y: SLIDE_H - 0.35, w: SLIDE_W - 1.0, h: 0.25, fontSize: 9, color: textColor, align: 'right' });
}

function addSlideNumber(slide: any, num: number, total: number, textColor = '999999') {
  slide.addText(`${num} / ${total}`, { x: 0.5, y: SLIDE_H - 0.35, w: 1.0, h: 0.25, fontSize: 9, color: textColor, align: 'left' });
}

function sanitizeAiNarrative(text: string): string {
  return text.replace(/\*\*/g, '').replace(/^\s*#{1,6}\s+/gm, '').replace(/^\s*[-*]\s+/gm, '')
    .split(/\r?\n/).map(l => l.trim()).filter((l, idx) => !(idx === 0 && /^executive summary$/i.test(l)))
    .filter(Boolean).map(l => l.replace(/\s+/g, ' ').trim()).join('\n').trim();
}

function splitTextForSlides(text: string, maxCharsPerSlide: number): string[] {
  const t = text.trim();
  if (!t) return [''];
  const paras = t.split(/\n{2,}/).map(p => p.trim()).filter(Boolean);
  const chunks: string[] = [];
  let cur = '';
  const pushCur = () => { if (cur.trim()) chunks.push(cur.trim()); cur = ''; };

  for (const p of paras) {
    const candidate = cur ? `${cur}\n\n${p}` : p;
    if (candidate.length <= maxCharsPerSlide) { cur = candidate; continue; }
    if (cur) { pushCur(); if (p.length <= maxCharsPerSlide) { cur = p; continue; } }
    const sentences = p.split(/(?<=[.!?])\s+/).map(s => s.trim()).filter(Boolean);
    for (const s of sentences) {
      const cand2 = cur ? `${cur} ${s}` : s;
      if (cand2.length <= maxCharsPerSlide) { cur = cand2; }
      else { if (cur) pushCur(); if (s.length <= maxCharsPerSlide) { cur = s; } else { let i = 0; while (i < s.length) { chunks.push(s.slice(i, i + maxCharsPerSlide).trim()); i += maxCharsPerSlide; } cur = ''; } }
    }
  }
  pushCur();
  return chunks.length ? chunks : [''];
}

function narrativeToBullets(text: string, maxBullets: number): string[] {
  const clean = sanitizeAiNarrative(text);
  if (!clean) return [];
  const parts = clean.split(/\n+/).flatMap(line => line.split(/(?<=[.!?])\s+/)).map(s => s.trim()).filter(s => s.length >= 25);
  const seen = new Set<string>();
  const bullets: string[] = [];
  for (const p of parts) { const key = p.toLowerCase(); if (seen.has(key)) continue; seen.add(key); bullets.push(p); if (bullets.length >= maxBullets) break; }
  return bullets;
}

function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m`;
  return `${Math.floor(seconds)}s`;
}

export const exportToPowerPoint = async (options: PowerPointExportOptions): Promise<void> => {
  const {
    title, subtitle, events, entities, mapScreenshot, timelineMapScreenshots, chartData, aiNarrative, eventNotes,
    radioTranscript = [],
    includeMap = true, includeSummary = true, includeTimeline = true, includeCharts = true, theme = 'standard'
  } = options;

  const PptxGenJS = await loadPptxGenJS();
  const pptx = new PptxGenJS();
  pptx.layout = 'LAYOUT_WIDE';
  pptx.author = 'Pathfinder Analysis System';
  pptx.company = 'Quiet Professionals';
  pptx.subject = 'Route Analysis Report';
  pptx.title = title;

  const colors = THEME_DEFINITIONS[theme].colors;
  const titleBgDataUrl = generateGradientBackground(1920, 1080, 'title', theme);
  const contentBgDataUrl = generateGradientBackground(1920, 1080, 'content', theme);

  let logoDataUrl = '';
  try { logoDataUrl = await fetchAsDataUrl(TEMPLATE_ASSETS.logo); } catch (e) { console.warn('Failed to load logo:', e); }

  // Calculate total slides
  let totalSlides = 2;
  if (includeSummary && events.length > 0) totalSlides++;
  if (includeMap && mapScreenshot) { totalSlides++; if (aiNarrative && splitTextForSlides(sanitizeAiNarrative(aiNarrative), 1900).length > 1) { totalSlides++; } }
  if (includeCharts && chartData) { totalSlides++; if (chartData.topLocations.length > 0) totalSlides++; }
  if (includeTimeline && events.length > 0) totalSlides += Math.ceil(events.length / TIMELINE_EVENTS_PER_SLIDE);
  if (radioTranscript.length > 0) totalSlides += Math.ceil(radioTranscript.length / RADIO_LINES_PER_SLIDE);
  if (eventNotes && Object.keys(eventNotes).length > 0) totalSlides++;

  let currentSlide = 0;

  // TITLE SLIDE
  currentSlide++;
  const titleSlide = pptx.addSlide();
  if (titleBgDataUrl) addFullBleedBackground(titleSlide, titleBgDataUrl);
  else titleSlide.background = { color: colors.titleBg };
  if (logoDataUrl) addCornerLogo(titleSlide, logoDataUrl, { x: SLIDE_W - 1.5, y: SLIDE_H - 1.5, w: 1.1, h: 1.1 });

  titleSlide.addText(title, { x: 0.75, y: 2.2, w: SLIDE_W - 1.5, h: 1.0, fontSize: 40, bold: true, color: colors.titleText, align: 'left' });
  titleSlide.addText(subtitle, { x: 0.75, y: 3.3, w: SLIDE_W - 1.5, h: 0.5, fontSize: 22, color: colors.titleText, align: 'left' });
  titleSlide.addText(`ENTITIES: ${entities.join(', ').toUpperCase()}`, { x: 0.75, y: 4.1, w: SLIDE_W - 1.5, h: 0.4, fontSize: 14, color: colors.accent, align: 'left', bold: true });
  titleSlide.addText(`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`, { x: 0.75, y: 5.5, w: SLIDE_W - 1.5, h: 0.3, fontSize: 12, color: colors.titleText, align: 'left' });

  // OBSERVATIONS SLIDE
  currentSlide++;
  const obsSlide = pptx.addSlide();
  if (contentBgDataUrl) addFullBleedBackground(obsSlide, contentBgDataUrl);
  else obsSlide.background = { color: colors.contentBg };
  addFooter(obsSlide, 'PATHFINDER • UNCLASSIFIED', colors.lightText);
  addSlideNumber(obsSlide, currentSlide, totalSlides, colors.lightText);
  if (logoDataUrl) addCornerLogo(obsSlide, logoDataUrl);

  obsSlide.addText('Observations and Recommendations', { x: 0.5, y: 0.4, w: 12.0, h: 0.6, fontSize: 28, bold: true, color: colors.primary });
  obsSlide.addText('ANALYST COMMENTS', { x: 0.5, y: 1.2, w: 12.3, h: 0.3, fontSize: 11, bold: true, color: colors.primary });
  obsSlide.addText('[Enter analyst observations and assessments here]', { x: 0.5, y: 1.55, w: 12.3, h: 0.8, fontSize: 11, italic: true, color: '888888', line: { color: 'cccccc', width: 1 }, margin: 0.1, valign: 'top' });

  const bullets = aiNarrative ? narrativeToBullets(aiNarrative, 10) : [];
  const keyFindings = bullets.slice(0, 5);
  const actionable = bullets.slice(5, 10);

  obsSlide.addText('AI GENERATED KEY FINDINGS', { x: 0.5, y: 2.6, w: 5.9, h: 0.35, fontSize: 12, bold: true, color: colors.primary });
  obsSlide.addText((keyFindings.length ? keyFindings : ['[Add key findings from analysis]']).join('\n'), { x: 0.5, y: 3.0, w: 5.9, h: 4.0, fontSize: 10, color: colors.text, valign: 'top', bullet: { indent: 14 }, lineSpacing: 13, margin: 0.08 });
  obsSlide.addText('AI GENERATED ACTIONABLE CONCLUSIONS', { x: 6.8, y: 2.6, w: 5.9, h: 0.35, fontSize: 12, bold: true, color: colors.primary });
  obsSlide.addText((actionable.length ? actionable : ['[Add recommended actions]']).join('\n'), { x: 6.8, y: 3.0, w: 5.9, h: 4.0, fontSize: 10, color: colors.text, valign: 'top', bullet: { indent: 14 }, lineSpacing: 13, margin: 0.08 });

  // EXECUTIVE SUMMARY
  if (includeSummary && events.length > 0) {
    currentSlide++;
    const summarySlide = pptx.addSlide();
    if (contentBgDataUrl) addFullBleedBackground(summarySlide, contentBgDataUrl);
    else summarySlide.background = { color: colors.contentBg };
    addFooter(summarySlide, 'PATHFINDER • UNCLASSIFIED', colors.lightText);
    addSlideNumber(summarySlide, currentSlide, totalSlides, colors.lightText);
    if (logoDataUrl) addCornerLogo(summarySlide, logoDataUrl);

    summarySlide.addText('Executive Summary', { x: 0.5, y: 0.4, w: 12.0, h: 0.6, fontSize: 28, bold: true, color: colors.primary });

    const travelEvents = events.filter(e => e.type === 'travel');
    const dwellEvents = events.filter(e => e.type === 'dwell');
    const totalTravelDistance = travelEvents.reduce((sum, e) => sum + (e.distance || 0), 0);
    const totalTravelTime = travelEvents.reduce((sum, e) => sum + (e.endTime.getTime() - e.startTime.getTime()) / 1000, 0);
    const totalDwellTime = dwellEvents.reduce((sum, e) => sum + (e.endTime.getTime() - e.startTime.getTime()) / 1000, 0);

    const stats = [
      { label: 'Total Events', value: events.length.toString() },
      { label: 'Travel Segments', value: travelEvents.length.toString() },
      { label: 'Dwell Locations', value: dwellEvents.length.toString() },
      { label: 'Total Distance', value: `${(totalTravelDistance * 0.621371).toFixed(2)} mi` },
      { label: 'Travel Time', value: formatDuration(totalTravelTime) },
      { label: 'Dwell Time', value: formatDuration(totalDwellTime) }
    ];

    const summaryData = stats.map((stat, idx) => [
      { text: stat.label, options: { bold: true, color: colors.text, fill: { color: idx % 2 === 0 ? 'f3f4f6' : 'ffffff' } } },
      { text: stat.value, options: { color: colors.secondary, align: 'right' as const, fill: { color: idx % 2 === 0 ? 'f3f4f6' : 'ffffff' } } }
    ]);

    summarySlide.addTable(summaryData, { x: 0.5, y: 1.3, w: 5.5, rowH: 0.45, fontSize: 14, border: { type: 'solid', pt: 1, color: 'e5e7eb' }, colW: [3.5, 2.0] });

    const firstEvent = events[0];
    const lastEvent = events[events.length - 1];
    summarySlide.addText('ANALYSIS PERIOD', { x: 6.5, y: 1.3, w: 6.0, h: 0.3, fontSize: 11, bold: true, color: colors.primary });
    summarySlide.addText(`${firstEvent.startTime.toLocaleDateString()} – ${lastEvent.endTime.toLocaleDateString()}`, { x: 6.5, y: 1.65, w: 6.0, h: 0.3, fontSize: 14, color: colors.text });
    summarySlide.addText('ENTITIES ANALYZED', { x: 6.5, y: 2.3, w: 6.0, h: 0.3, fontSize: 11, bold: true, color: colors.primary });
    summarySlide.addText(entities.join(', '), { x: 6.5, y: 2.65, w: 6.0, h: 0.5, fontSize: 12, color: colors.text });
  }

  // ROUTE VISUALIZATION SLIDE
  if (includeMap && mapScreenshot) {
    currentSlide++;
    const mapSlide = pptx.addSlide();
    if (contentBgDataUrl) addFullBleedBackground(mapSlide, contentBgDataUrl);
    else mapSlide.background = { color: colors.contentBg };
    addFooter(mapSlide, 'PATHFINDER • UNCLASSIFIED', colors.lightText);
    addSlideNumber(mapSlide, currentSlide, totalSlides, colors.lightText);
    if (logoDataUrl) addCornerLogo(mapSlide, logoDataUrl);

    let timelineStr = '';
    if (events.length > 0) {
      const firstEvent = events[0];
      const lastEvent = events[events.length - 1];
      timelineStr = ` (${firstEvent.startTime.toLocaleDateString()} – ${lastEvent.endTime.toLocaleDateString()})`;
    }

    mapSlide.addText(`Route Visualization: ${entities.join(', ')}${timelineStr}`, { x: 0.5, y: 0.35, w: 11.5, h: 0.5, fontSize: 22, bold: true, color: colors.primary });

    const mapMaxWidth = 5.8, mapMaxHeight = 5.5;
    const aspectRatio = mapScreenshot.aspectRatio;
    let imgWidth: number, imgHeight: number;
    if (aspectRatio > (mapMaxWidth / mapMaxHeight)) { imgWidth = mapMaxWidth; imgHeight = mapMaxWidth / aspectRatio; }
    else { imgHeight = mapMaxHeight; imgWidth = mapMaxHeight * aspectRatio; }
    const mapX = 0.5, mapY = 1.0 + (mapMaxHeight - imgHeight) / 2;

    mapSlide.addImage({ data: mapScreenshot.dataUrl, x: mapX, y: mapY, w: imgWidth, h: imgHeight });

    const rightX = 6.6, rightW = 6.2;

    if (aiNarrative) {
      mapSlide.addText('ANALYST COMMENTS', { x: rightX, y: 1.0, w: rightW, h: 0.3, fontSize: 11, bold: true, color: colors.primary });
      mapSlide.addText('[Enter comments]', { x: rightX, y: 1.35, w: rightW, h: 0.6, fontSize: 10, color: '888888', italic: true, line: { color: 'cccccc', width: 1 }, margin: 0.08, valign: 'top' });
      mapSlide.addText('AI-GENERATED ANALYSIS', { x: rightX, y: 2.15, w: rightW, h: 0.3, fontSize: 11, bold: true, color: colors.primary });

      const cleanNarrative = sanitizeAiNarrative(aiNarrative);
      const aiChunks = splitTextForSlides(cleanNarrative, 1900);
      mapSlide.addText(aiChunks[0] || '', { x: rightX, y: 2.5, w: rightW, h: 4.0, fontSize: 9, color: colors.text, align: 'left', valign: 'top', lineSpacing: 12, margin: 0.08 });

      // Combine remaining AI chunks into a single continuation slide with title and map
      if (aiChunks.length > 1) {
        currentSlide++;
        const cont = pptx.addSlide();
        if (contentBgDataUrl) addFullBleedBackground(cont, contentBgDataUrl);
        else cont.background = { color: colors.contentBg };
        addFooter(cont, 'PATHFINDER • UNCLASSIFIED', colors.lightText);
        addSlideNumber(cont, currentSlide, totalSlides, colors.lightText);
        if (logoDataUrl) addCornerLogo(cont, logoDataUrl);

        // Add same title as the main route visualization slide
        let contTimelineStr = '';
        if (events.length > 0) {
          const firstEvent = events[0];
          const lastEvent = events[events.length - 1];
          contTimelineStr = ` (${firstEvent.startTime.toLocaleDateString()} – ${lastEvent.endTime.toLocaleDateString()})`;
        }
        cont.addText(`Route Visualization: ${entities.join(', ')}${contTimelineStr}`, { x: 0.5, y: 0.35, w: 11.5, h: 0.5, fontSize: 22, bold: true, color: colors.primary });

        // Add map on the left side (same as main route visualization slide)
        const contMapMaxWidth = 5.8, contMapMaxHeight = 5.5;
        const contAspectRatio = mapScreenshot.aspectRatio;
        let contImgWidth: number, contImgHeight: number;
        if (contAspectRatio > (contMapMaxWidth / contMapMaxHeight)) { contImgWidth = contMapMaxWidth; contImgHeight = contMapMaxWidth / contAspectRatio; }
        else { contImgHeight = contMapMaxHeight; contImgWidth = contMapMaxHeight * contAspectRatio; }
        const contMapX = 0.5, contMapY = 1.0 + (contMapMaxHeight - contImgHeight) / 2;
        cont.addImage({ data: mapScreenshot.dataUrl, x: contMapX, y: contMapY, w: contImgWidth, h: contImgHeight });

        // Combine all remaining chunks and display on the right side
        const combinedRemainingText = aiChunks.slice(1).join('\n\n');
        const contRightX = 6.6, contRightW = 6.2;
        cont.addText('AI-GENERATED ANALYSIS (CONTINUED)', { x: contRightX, y: 1.0, w: contRightW, h: 0.3, fontSize: 11, bold: true, color: colors.primary });
        cont.addText(combinedRemainingText, { x: contRightX, y: 1.35, w: contRightW, h: 5.15, fontSize: 9, color: colors.text, align: 'left', valign: 'top', lineSpacing: 12, margin: 0.08 });
      }
    } else {
      const travelEvents = events.filter(e => e.type === 'travel');
      const dwellEvents = events.filter(e => e.type === 'dwell');
      const totalDistance = travelEvents.reduce((sum, e) => sum + (e.distance || 0), 0);
      mapSlide.addText('SUMMARY STATISTICS', { x: rightX, y: 1.0, w: rightW, h: 0.35, fontSize: 12, bold: true, color: colors.primary });
      mapSlide.addText(`Total Events: ${events.length}\nTravel Segments: ${travelEvents.length}\nDwell Locations: ${dwellEvents.length}\nTotal Distance: ${(totalDistance * 0.621371).toFixed(2)} miles`, { x: rightX, y: 1.5, w: rightW, h: 3.0, fontSize: 12, color: colors.text, lineSpacing: 20 });
    }
  }

  // ACTIVITY PATTERNS
  if (includeCharts && chartData) {
    currentSlide++;
    const activitySlide = pptx.addSlide();
    if (contentBgDataUrl) addFullBleedBackground(activitySlide, contentBgDataUrl);
    else activitySlide.background = { color: colors.contentBg };
    addFooter(activitySlide, 'PATHFINDER • UNCLASSIFIED', colors.lightText);
    addSlideNumber(activitySlide, currentSlide, totalSlides, colors.lightText);
    if (logoDataUrl) addCornerLogo(activitySlide, logoDataUrl);

    activitySlide.addText('Activity Patterns', { x: 0.5, y: 0.4, w: 12.0, h: 0.5, fontSize: 28, bold: true, color: colors.primary });

    // Generate text summary of the activity patterns (placed at top of slide)
    const summaryParts: string[] = [];
    
    // Intro
    summaryParts.push(`This slide summarizes activity patterns based on ${chartData.stats.totalPoints.toLocaleString()} GPS data points from ${chartData.stats.uniqueDevices} device(s).`);
    
    // Peak hour insight
    if (chartData.stats.peakHour !== null) {
      const peakHourFormatted = `${chartData.stats.peakHour.toString().padStart(2, '0')}:00`;
      const peakHourEnd = `${((chartData.stats.peakHour + 1) % 24).toString().padStart(2, '0')}:00`;
      summaryParts.push(`Peak activity occurs between ${peakHourFormatted}–${peakHourEnd}.`);
    }
    
    // Weekly pattern insight
    if (chartData.weeklyActivity.length > 0) {
      const maxWeeklyActivity = Math.max(...chartData.weeklyActivity.map(d => d.activity));
      const peakDays = chartData.weeklyActivity.filter(d => d.activity === maxWeeklyActivity).map(d => d.day);
      const activeDays = chartData.weeklyActivity.filter(d => d.activity > 0).map(d => d.day);
      if (peakDays.length > 0 && maxWeeklyActivity > 0) {
        summaryParts.push(`Most active day(s): ${peakDays.join(', ')}.`);
      }
      if (activeDays.length < 7 && activeDays.length > 0) {
        summaryParts.push(`Activity recorded on ${activeDays.length} of 7 days.`);
      }
    }
    
    // Dwell insight
    if (chartData.stats.totalDwells > 0) {
      summaryParts.push(`${chartData.stats.totalDwells} dwell location(s) identified during the analysis period.`);
    }
    
    // Daily average
    if (chartData.stats.avgPointsPerDay > 0) {
      summaryParts.push(`Average of ${chartData.stats.avgPointsPerDay.toLocaleString()} data points collected per day.`);
    }
    
    const summaryText = summaryParts.join(' ');
    
    activitySlide.addText(summaryText, { x: 0.5, y: 0.95, w: 12.3, h: 0.9, fontSize: 11, color: colors.text, valign: 'top', lineSpacing: 14 });

    const statsData = [
      ['Total GPS Points', chartData.stats.totalPoints.toLocaleString()],
      ['Unique Devices', chartData.stats.uniqueDevices.toString()],
      ['Total Dwells', chartData.stats.totalDwells.toString()],
      ['Peak Activity Hour', chartData.stats.peakHour !== null ? `${chartData.stats.peakHour.toString().padStart(2, '0')}:00` : 'N/A'],
      ['Avg Points/Day', chartData.stats.avgPointsPerDay.toString()]
    ].map(([label, value], idx) => [
      { text: label, options: { bold: true, color: colors.text, fill: { color: idx % 2 === 0 ? 'f3f4f6' : 'ffffff' } } },
      { text: value, options: { color: colors.secondary, align: 'right' as const, fill: { color: idx % 2 === 0 ? 'f3f4f6' : 'ffffff' } } }
    ]);

    activitySlide.addText('STATISTICS', { x: 0.5, y: 2.0, w: 4.5, h: 0.3, fontSize: 11, bold: true, color: colors.primary });
    activitySlide.addTable(statsData, { x: 0.5, y: 2.35, w: 4.5, rowH: 0.35, fontSize: 11, border: { type: 'solid', pt: 1, color: 'e5e7eb' }, colW: [2.8, 1.7] });

    if (chartData.weeklyActivity.length > 0) {
      activitySlide.addText('WEEKLY ACTIVITY', { x: 5.5, y: 2.0, w: 4.5, h: 0.3, fontSize: 11, bold: true, color: colors.primary });
      const maxWeekly = Math.max(...chartData.weeklyActivity.map(d => d.activity)) || 1;
      const weeklyRows = chartData.weeklyActivity.map((d, idx) => {
        const barWidth = Math.round((d.activity / maxWeekly) * 15);
        const bar = '▓'.repeat(barWidth) + '░'.repeat(15 - barWidth);
        return [
          { text: d.day.slice(0, 3), options: { bold: true, color: colors.text, fill: { color: idx % 2 === 0 ? 'f3f4f6' : 'ffffff' } } },
          { text: bar, options: { color: colors.accent, fontFace: 'Consolas', fill: { color: idx % 2 === 0 ? 'f3f4f6' : 'ffffff' } } },
          { text: d.activity.toString(), options: { color: colors.secondary, align: 'right' as const, fill: { color: idx % 2 === 0 ? 'f3f4f6' : 'ffffff' } } }
        ];
      });
      activitySlide.addTable(weeklyRows, { x: 5.5, y: 2.35, w: 4.5, rowH: 0.3, fontSize: 9, border: { type: 'solid', pt: 1, color: 'e5e7eb' }, colW: [0.6, 2.9, 1.0] });
    }

    if (chartData.dailyRhythm.length > 0) {
      activitySlide.addText('HOURLY ACTIVITY DISTRIBUTION', { x: 0.5, y: 6.15, w: 9.0, h: 0.25, fontSize: 10, bold: true, color: colors.primary });
      
      const maxHourly = Math.max(...chartData.dailyRhythm.map(d => d.activity)) || 1;
      
      // Create a horizontal heatmap bar across the bottom
      // Color intensity based on activity level, using theme accent color
      const accentR = parseInt(colors.accent.slice(0, 2), 16);
      const accentG = parseInt(colors.accent.slice(2, 4), 16);
      const accentB = parseInt(colors.accent.slice(4, 6), 16);
      
      const getHeatmapColor = (activity: number): string => {
        if (activity === 0) return 'f3f4f6'; // Light gray for zero
        const intensity = activity / maxHourly;
        // Scale from light tint of accent to full accent color
        // Start: light version (blend with white), End: full accent
        const lightR = 240, lightG = 240, lightB = 240; // Very light gray as base
        const r = Math.round(lightR + (accentR - lightR) * intensity);
        const g = Math.round(lightG + (accentG - lightG) * intensity);
        const b = Math.round(lightB + (accentB - lightB) * intensity);
        return r.toString(16).padStart(2, '0') + g.toString(16).padStart(2, '0') + b.toString(16).padStart(2, '0');
      };
      
      // Determine if text should be light or dark based on background intensity
      const getTextColor = (activity: number): string => {
        if (activity === 0) return colors.lightText;
        const intensity = activity / maxHourly;
        // Calculate luminance of the blended color to determine text contrast
        const r = 240 + (accentR - 240) * intensity;
        const g = 240 + (accentG - 240) * intensity;
        const b = 240 + (accentB - 240) * intensity;
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return luminance < 0.5 ? 'ffffff' : colors.text;
      };
      
      // Build hour labels row and values row for horizontal heatmap
      const hourLabels = chartData.dailyRhythm.map((d) => ({
        text: d.hour.replace(':00', '').padStart(2, '0'),
        options: { 
          fontSize: 7, 
          color: getTextColor(d.activity),
          fill: { color: getHeatmapColor(d.activity) },
          align: 'center' as const,
          valign: 'middle' as const
        }
      }));
      
      const activityValues = chartData.dailyRhythm.map((d) => ({
        text: d.activity > 0 ? d.activity.toString() : '',
        options: { 
          fontSize: 6, 
          color: getTextColor(d.activity),
          fill: { color: getHeatmapColor(d.activity) },
          align: 'center' as const,
          valign: 'middle' as const
        }
      }));
      
      // Create the horizontal heatmap as a 2-row table spanning full width
      const heatmapX = 0.5;
      const heatmapW = 12.3;
      const cellW = heatmapW / 24;
      
      activitySlide.addTable(
        [[...hourLabels], [...activityValues]],
        { 
          x: heatmapX, 
          y: 6.45, 
          w: heatmapW, 
          rowH: 0.25, 
          fontSize: 7, 
          border: { type: 'solid', pt: 0.5, color: 'ffffff' },
          colW: Array(24).fill(cellW)
        }
      );
    }

    // TOP LOCATIONS
    if (chartData.topLocations.length > 0) {
      currentSlide++;
      const locationsSlide = pptx.addSlide();
      if (contentBgDataUrl) addFullBleedBackground(locationsSlide, contentBgDataUrl);
      else locationsSlide.background = { color: colors.contentBg };
      addFooter(locationsSlide, 'PATHFINDER • UNCLASSIFIED', colors.lightText);
      addSlideNumber(locationsSlide, currentSlide, totalSlides, colors.lightText);
      if (logoDataUrl) addCornerLogo(locationsSlide, logoDataUrl);

      locationsSlide.addText('Top Dwell Locations', { x: 0.5, y: 0.4, w: 12.0, h: 0.5, fontSize: 28, bold: true, color: colors.primary });
      locationsSlide.addText(`Ranked by total dwell time (top ${Math.min(chartData.topLocations.length, 12)})`, { x: 0.5, y: 0.9, w: 12.0, h: 0.25, fontSize: 11, color: colors.lightText });

      const locTableData: any[] = [[
        { text: '#', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
        { text: 'Location', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
        { text: 'Visits', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
        { text: 'Total Time', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'right' as const } }
      ]];

      chartData.topLocations.slice(0, 12).forEach((loc, idx) => {
        const hours = Math.floor(loc.duration / 60);
        const mins = loc.duration % 60;
        const timeStr = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
        const rowColor = idx % 2 === 0 ? 'f9fafb' : 'ffffff';
        locTableData.push([
          { text: (idx + 1).toString(), options: { fill: { color: rowColor }, align: 'center' as const } },
          { text: loc.location, options: { fill: { color: rowColor }, fontSize: 10 } },
          { text: loc.visits.toString(), options: { fill: { color: rowColor }, align: 'center' as const } },
          { text: timeStr, options: { fill: { color: rowColor }, align: 'right' as const } }
        ]);
      });

      locationsSlide.addTable(locTableData, { x: 0.5, y: 1.3, w: 12.3, rowH: 0.4, fontSize: 11, border: { type: 'solid', pt: 1, color: 'e5e7eb' }, colW: [0.6, 8.0, 1.2, 2.5] });
    }
  }

  // TIMELINE SLIDES
  if (includeTimeline && events.length > 0) {
    const timelineEvents = events.slice().sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
    const eventsPerSlide = TIMELINE_EVENTS_PER_SLIDE;
    const numberOfSlides = Math.ceil(timelineEvents.length / eventsPerSlide);

    for (let slideIndex = 0; slideIndex < numberOfSlides; slideIndex++) {
      currentSlide++;
      const slide = pptx.addSlide();
      if (contentBgDataUrl) addFullBleedBackground(slide, contentBgDataUrl);
      else slide.background = { color: colors.contentBg };
      addFooter(slide, 'PATHFINDER • UNCLASSIFIED', colors.lightText);
      addSlideNumber(slide, currentSlide, totalSlides, colors.lightText);
      if (logoDataUrl) addCornerLogo(slide, logoDataUrl);

      const startIndex = slideIndex * eventsPerSlide;
      const endIndex = Math.min(startIndex + eventsPerSlide, timelineEvents.length);
      const slideEvents = timelineEvents.slice(startIndex, endIndex);

      const dwellNumberById = new Map<string, number>();
      slideEvents.filter(e => e.type === 'dwell' && e.location).forEach((e, idx) => dwellNumberById.set(e.id, idx + 1));

      slide.addText(`Timeline Events (${startIndex + 1}–${endIndex} of ${events.length})`, { x: 0.5, y: 0.35, w: 12.0, h: 0.5, fontSize: 22, bold: true, color: colors.primary });

      const mapAreaX = 0.5, mapAreaY = 1.0, mapAreaW = 6.0, mapAreaH = 5.5;

      const timelineShot = (timelineMapScreenshots && timelineMapScreenshots[slideIndex]) || mapScreenshot;
      if (timelineShot) {
        const ar = timelineShot.aspectRatio;
        let mapImgW = mapAreaW, mapImgH = mapAreaW / ar;
        if (mapImgH > mapAreaH) { mapImgH = mapAreaH; mapImgW = mapAreaH * ar; }
        const mapImgX = mapAreaX + (mapAreaW - mapImgW) / 2;
        const mapImgY = mapAreaY + (mapAreaH - mapImgH) / 2;

        slide.addImage({ data: timelineShot.dataUrl, x: mapImgX, y: mapImgY, w: mapImgW, h: mapImgH });

        const proj = timelineShot.projectedPositions;
        if (proj) {
          for (const e of slideEvents.filter(ev => ev.type === 'dwell' && ev.location)) {
            const n = dwellNumberById.get(e.id);
            const p = proj[e.id];
            if (!n || !p) continue;
            const sx = mapImgX + (p.x / timelineShot.width) * mapImgW;
            const sy = mapImgY + (p.y / timelineShot.height) * mapImgH;
            const badgeSize = 0.2;
            slide.addShape('ellipse', { x: sx - badgeSize / 2, y: sy - badgeSize / 2, w: badgeSize, h: badgeSize, fill: { color: colors.accent }, line: { color: 'ffffff', width: 1 } });
            slide.addText(`${n}`, { x: sx - badgeSize / 2, y: sy - badgeSize / 2, w: badgeSize, h: badgeSize, fontSize: 7, bold: true, color: 'ffffff', align: 'center', valign: 'middle' });
          }
        }
      } else {
        slide.addText('Map not available', { x: mapAreaX, y: mapAreaY + 2.5, w: mapAreaW, h: 0.4, fontSize: 12, color: colors.secondary, italic: true, align: 'center' });
      }

      // Compact single-line format for each event with Dwell/Travel labels
      const bulletLines = slideEvents.map(ev => {
        const startStr = ev.startTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        const endStr = ev.endTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        const duration = (ev.endTime.getTime() - ev.startTime.getTime()) / 1000;
        const durationStr = formatDuration(duration);

        if (ev.type === 'dwell') {
          const n = dwellNumberById.get(ev.id);
          const label = n ? `[${n}] Dwell: ` : '• Dwell: ';
          const loc = ev.address ? (ev.address.length > 35 ? ev.address.slice(0, 35) + '…' : ev.address) : (ev.location ? `${ev.location[0].toFixed(4)}, ${ev.location[1].toFixed(4)}` : '');
          const visits = ev.visitCount && ev.visitCount > 1 ? ` ×${ev.visitCount}` : '';
          return `${label}${startStr}–${endStr} (${durationStr}) ${loc}${visits}`;
        }
        const miles = (ev.distance || 0) * 0.621371;
        const dist = miles > 0 ? `${miles.toFixed(1)} mi` : `${ev.pointCount || 0} pts`;
        const trips = ev.tripCount && ev.tripCount > 1 ? ` ×${ev.tripCount}` : '';
        return `• Travel: ${startStr}–${endStr} (${durationStr}) ${dist}${trips}`;
      });

      slide.addText(bulletLines.join('\n'), { x: 6.8, y: 1.0, w: 6.0, h: 5.8, fontSize: 8, color: colors.text, align: 'left', valign: 'top', lineSpacing: 10, margin: 0.05 });
    }
  }

  // RADIO TRANSCRIPT SLIDES (PTT captions, chronological)
  if (radioTranscript.length > 0) {
    const lines = radioTranscript.slice().sort((a, b) => a.startedAt.getTime() - b.startedAt.getTime());
    const pages = Math.ceil(lines.length / RADIO_LINES_PER_SLIDE);
    for (let page = 0; page < pages; page++) {
      currentSlide++;
      const slide = pptx.addSlide();
      if (contentBgDataUrl) addFullBleedBackground(slide, contentBgDataUrl);
      else slide.background = { color: colors.contentBg };
      addFooter(slide, 'PATHFINDER • UNCLASSIFIED', colors.lightText);
      addSlideNumber(slide, currentSlide, totalSlides, colors.lightText);
      if (logoDataUrl) addCornerLogo(slide, logoDataUrl);

      const startIdx = page * RADIO_LINES_PER_SLIDE;
      const endIdx = Math.min(startIdx + RADIO_LINES_PER_SLIDE, lines.length);
      slide.addText(`Radio Transcript (${startIdx + 1}–${endIdx} of ${lines.length})`, { x: 0.5, y: 0.35, w: 12.0, h: 0.5, fontSize: 22, bold: true, color: colors.primary });

      const head = (text: string) => ({ text, options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } });
      const rows: any[] = [[head('Time'), head('Sender'), head('Team'), head('Transcript')]];
      lines.slice(startIdx, endIdx).forEach((l, idx) => {
        const rowColor = idx % 2 === 0 ? 'f9fafb' : 'ffffff';
        const timeStr = `${l.startedAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${l.startedAt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
        rows.push([
          { text: timeStr, options: { fill: { color: rowColor }, fontSize: 9 } },
          { text: l.sender, options: { fill: { color: rowColor }, fontSize: 9, bold: true } },
          { text: l.team || '', options: { fill: { color: rowColor }, fontSize: 9 } },
          { text: l.transcript || '(no transcript)', options: { fill: { color: rowColor }, fontSize: 9, italic: !l.transcript } },
        ]);
      });
      slide.addTable(rows, { x: 0.5, y: 1.0, w: 12.3, rowH: 0.33, fontSize: 9, border: { type: 'solid', pt: 1, color: 'e5e7eb' }, colW: [1.7, 1.8, 1.1, 7.7], valign: 'middle' });
    }
  }

  // NOTES SLIDE
  if (eventNotes && Object.keys(eventNotes).length > 0) {
    currentSlide++;
    const notesSlide = pptx.addSlide();
    if (contentBgDataUrl) addFullBleedBackground(notesSlide, contentBgDataUrl);
    else notesSlide.background = { color: colors.contentBg };
    addFooter(notesSlide, 'PATHFINDER • UNCLASSIFIED', colors.lightText);
    addSlideNumber(notesSlide, currentSlide, totalSlides, colors.lightText);
    if (logoDataUrl) addCornerLogo(notesSlide, logoDataUrl);

    notesSlide.addText('Analyst Comments', { x: 0.5, y: 0.4, w: 12.0, h: 0.5, fontSize: 28, bold: true, color: colors.primary });

    const rows: any[] = [[
      { text: 'Event', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
      { text: 'Type', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
      { text: 'Time', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
      { text: 'Comment', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } }
    ]];

    const eventIndexById = new Map<string, number>();
    events.forEach((e, idx) => eventIndexById.set(e.id, idx + 1));

    const noteItems = Object.entries(eventNotes).flatMap(([eventId, notes]) => notes.map(n => ({ eventId, note: n }))).sort((a, b) => (eventIndexById.get(a.eventId) ?? 999999) - (eventIndexById.get(b.eventId) ?? 999999));

    noteItems.slice(0, 18).forEach(({ eventId, note }, idx) => {
      const ev = events.find(e => e.id === eventId);
      const num = eventIndexById.get(eventId);
      const rowColor = idx % 2 === 0 ? 'f9fafb' : 'ffffff';
      const timeStr = ev ? `${ev.startTime.toLocaleDateString()} ${ev.startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : '';
      rows.push([
        { text: num ? `#${num}` : eventId, options: { fill: { color: rowColor } } },
        { text: ev ? (ev.type === 'travel' ? 'Travel' : 'Dwell') : '', options: { fill: { color: rowColor } } },
        { text: timeStr, options: { fill: { color: rowColor }, fontSize: 9 } },
        { text: note.content, options: { fill: { color: rowColor }, fontSize: 10 } }
      ]);
    });

    notesSlide.addTable(rows, { x: 0.5, y: 1.1, w: 12.3, rowH: 0.35, fontSize: 10, border: { type: 'solid', pt: 1, color: 'e5e7eb' }, colW: [0.8, 0.8, 2.0, 8.7] });
    if (noteItems.length > 18) {
      notesSlide.addText(`Showing 18 of ${noteItems.length} comments`, { x: 0.5, y: SLIDE_H - 0.6, w: 4.0, h: 0.25, fontSize: 9, color: colors.lightText, italic: true });
    }
  }

  const filename = `pathfinder-report-${entities.join('-')}-${new Date().toISOString().split('T')[0]}.pptx`;
  await pptx.writeFile({ fileName: filename });
};

export interface MapScreenshotResult {
  dataUrl: string;
  width: number;
  height: number;
  aspectRatio: number;
  projectedPositions?: Record<string, { x: number; y: number }>;
}

/**
 * Wait for map to be ready (idle + tiles loaded)
 */
const waitForMapReady = async (map: any, timeoutMs: number = 2000): Promise<void> => {
  if (!map) return;
  
  return new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, timeoutMs);
    
    try {
      const isReady = map.isStyleLoaded?.() && map.areTilesLoaded?.() && !map.isMoving?.();
      if (isReady) {
        clearTimeout(timer);
        resolve();
        return;
      }
      
      map.once('idle', () => {
        clearTimeout(timer);
        resolve();
      });
    } catch {
      // If methods don't exist, just wait for timeout
    }
  });
};

/**
 * Capture the WebGL canvas synchronously during a render frame.
 * This is the most reliable way to capture Mapbox GL content.
 */
const captureWebGLCanvas = (map: any, canvas: HTMLCanvasElement, timeoutMs: number = 3000): Promise<string | null> => {
  return new Promise((resolve) => {
    // If no map reference, try direct capture
    if (!map) {
      try {
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(null);
      }
      return;
    }

    let resolved = false;
    const finish = (dataUrl: string | null) => {
      if (resolved) return;
      resolved = true;
      clearTimeout(timer);
      try { map.off('render', onRender); } catch {}
      resolve(dataUrl);
    };

    const timer = setTimeout(() => {
      console.warn('[Map Capture] Render timeout, trying direct capture');
      try {
        finish(canvas.toDataURL('image/png'));
      } catch {
        finish(null);
      }
    }, timeoutMs);

    // Capture during the render callback - this is when the buffer is guaranteed to have content
    const onRender = () => {
      try {
        const dataUrl = canvas.toDataURL('image/png');
        if (dataUrl && dataUrl.length > 10000) {
          finish(dataUrl);
        }
      } catch (e) {
        console.warn('[Map Capture] Render capture failed:', e);
      }
    };

    try {
      map.on('render', onRender);
      // Force a repaint to trigger the render event
      map.triggerRepaint();
    } catch {
      // If events don't work, try direct capture
      try {
        finish(canvas.toDataURL('image/png'));
      } catch {
        finish(null);
      }
    }
  });
};

export const captureMapScreenshot = async (
  mapContainerId: string = 'pathfinder-map-container',
  projectPoints?: Array<{ id: string; lat: number; lon: number }>,
  includeOverlays: boolean = true
): Promise<MapScreenshotResult | undefined> => {
  try {
    const mapContainer = document.getElementById(mapContainerId);
    if (!mapContainer) {
      console.error('[Map Capture] Map container not found');
      return undefined;
    }

    // Get the Mapbox map instance - this is set in PathfinderMapGL.handleMapLoad
    const map: any = (window as any).__pathfinderMap;
    if (!map) {
      console.warn('[Map Capture] No map instance found on window.__pathfinderMap');
    }

    // Get the map canvas - MUST use map.getCanvas() for react-map-gl
    let mapCanvas: HTMLCanvasElement | null = null;
    if (map?.getCanvas) {
      try {
        mapCanvas = map.getCanvas();
      } catch (e) {
        console.warn('[Map Capture] Could not get canvas from map:', e);
      }
    }
    // Fallback to querySelector only if getCanvas failed
    if (!mapCanvas) {
      mapCanvas = mapContainer.querySelector('canvas.mapboxgl-canvas') as HTMLCanvasElement;
    }
    if (!mapCanvas) {
      console.error('[Map Capture] Map canvas not found');
      return undefined;
    }

    // Get dimensions from the canvas itself (more accurate for WebGL)
    const width = mapCanvas.clientWidth || mapCanvas.width;
    const height = mapCanvas.clientHeight || mapCanvas.height;
    const aspectRatio = width / height;

    // Ensure map is idle before capture
    if (map) {
      try {
        // Wait for any ongoing animations/movements to complete
        if (map.isMoving?.()) {
          await new Promise<void>(resolve => {
            map.once('moveend', resolve);
            setTimeout(resolve, 2000); // timeout fallback
          });
        }
        // Wait for tiles to load
        if (!map.areTilesLoaded?.()) {
          await new Promise<void>(resolve => {
            map.once('idle', resolve);
            setTimeout(resolve, 3000); // timeout fallback
          });
        }
      } catch (e) {
        console.warn('[Map Capture] Error waiting for map:', e);
      }
    }

    // Small delay to ensure GPU has flushed
    await new Promise(r => setTimeout(r, 200));

    // Capture the WebGL canvas during a render frame
    const mapDataUrl = await captureWebGLCanvas(map, mapCanvas, 3000);
    
    if (!mapDataUrl || mapDataUrl.length < 10000) {
      console.error('[Map Capture] WebGL capture failed or returned minimal data');
      // Try one more time with a longer wait
      await new Promise(r => setTimeout(r, 500));
      if (map?.triggerRepaint) map.triggerRepaint();
      await new Promise(r => setTimeout(r, 100));
    }

    let dataUrl: string;

    if (includeOverlays && mapDataUrl) {
      try {
        const html2canvas = (await import('html2canvas')).default;
        const scale = 2;
        const compositeCanvas = document.createElement('canvas');
        compositeCanvas.width = width * scale;
        compositeCanvas.height = height * scale;
        const ctx = compositeCanvas.getContext('2d');

        if (!ctx) {
          dataUrl = mapDataUrl;
        } else {
          // First draw the captured map
          const mapImg = new Image();
          await new Promise<void>((resolve, reject) => {
            mapImg.onload = () => resolve();
            mapImg.onerror = () => reject(new Error('Failed to load map image'));
            mapImg.src = mapDataUrl;
          });
          ctx.drawImage(mapImg, 0, 0, width * scale, height * scale);

          // Then capture HTML overlay elements (legend, controls, etc.)
          // We ignore the canvas and the container background
          const overlayCanvas = await html2canvas(mapContainer, {
            useCORS: true,
            allowTaint: true,
            backgroundColor: null,  // Transparent - we don't want container bg
            scale: scale,
            logging: false,
            width: width,
            height: height,
            ignoreElements: (element) => {
              // Ignore all canvas elements - we already drew the map
              if (element.tagName === 'CANVAS') return true;
              // Ignore the map container's direct div that has the background color
              if (element.classList?.contains('mapboxgl-map')) return false; // Keep this for controls
              return false;
            },
            onclone: (_doc, clonedEl) => {
              // Make the cloned container background transparent
              clonedEl.style.backgroundColor = 'transparent';
              // Also make any wrapper divs transparent
              const innerDiv = clonedEl.querySelector('.mapboxgl-map') as HTMLElement;
              if (innerDiv) innerDiv.style.backgroundColor = 'transparent';
            }
          });

          // Composite overlay on top of map
          ctx.drawImage(overlayCanvas, 0, 0);
          dataUrl = compositeCanvas.toDataURL('image/png');
        }
      } catch (error) {
        console.warn('[Map Capture] Overlay capture failed:', error);
        dataUrl = mapDataUrl;
      }
    } else if (mapDataUrl) {
      dataUrl = mapDataUrl;
    } else {
      // Last resort - try direct canvas capture
      console.warn('[Map Capture] All methods failed, trying direct toDataURL');
      try {
        dataUrl = mapCanvas.toDataURL('image/png');
      } catch (e) {
        console.error('[Map Capture] Direct capture also failed:', e);
        return undefined;
      }
    }

    // Project points to pixel coordinates
    let projectedPositions: Record<string, { x: number; y: number }> | undefined;
    if (projectPoints && projectPoints.length > 0 && map?.project) {
      projectedPositions = {};
      for (const p of projectPoints) {
        try {
          const pt = map.project([p.lon, p.lat]);
          if (pt && typeof pt.x === 'number' && typeof pt.y === 'number') {
            projectedPositions[p.id] = { x: pt.x, y: pt.y };
          }
        } catch {
          // Skip invalid points
        }
      }
    }

    return { dataUrl, width, height, aspectRatio, projectedPositions };
  } catch (error) {
    console.error('[Map Capture] Failed:', error);
    return undefined;
  }
};

/**
 * Temporarily zooms the live map to the provided points, captures a screenshot, and restores the previous view.
 * Intended for PowerPoint timeline "close-up" exports when dwells cluster tightly.
 */
export const captureMapScreenshotZoomedToPoints = async (opts: {
  mapContainerId?: string;
  fitPoints: Array<{ lat: number; lon: number }>;
  projectPoints?: Array<{ id: string; lat: number; lon: number }>;
  includeOverlays?: boolean;
  paddingPx?: number;
  maxZoom?: number;
  timeoutMs?: number;
}): Promise<MapScreenshotResult | undefined> => {
  const {
    mapContainerId = 'pathfinder-map-container',
    fitPoints,
    projectPoints,
    includeOverlays = true,
    paddingPx = 90,
    maxZoom = 16,
    timeoutMs = 1500,
  } = opts;

  const map: any = (window as any).__pathfinderMap;
  if (!map?.fitBounds || !map?.getCenter || !map?.jumpTo) {
    return captureMapScreenshot(mapContainerId, projectPoints, includeOverlays);
  }

  if (!fitPoints || fitPoints.length === 0) {
    return captureMapScreenshot(mapContainerId, projectPoints, includeOverlays);
  }

  let minLat = Infinity, maxLat = -Infinity, minLon = Infinity, maxLon = -Infinity;
  for (const p of fitPoints) {
    if (!Number.isFinite(p.lat) || !Number.isFinite(p.lon)) continue;
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lon < minLon) minLon = p.lon;
    if (p.lon > maxLon) maxLon = p.lon;
  }
  if (!Number.isFinite(minLat) || !Number.isFinite(minLon) || !Number.isFinite(maxLat) || !Number.isFinite(maxLon)) {
    return captureMapScreenshot(mapContainerId, projectPoints, includeOverlays);
  }

  const prevCenter = map.getCenter();
  const prev = {
    center: [prevCenter.lng, prevCenter.lat] as [number, number],
    zoom: map.getZoom?.(),
    bearing: map.getBearing?.(),
    pitch: map.getPitch?.(),
  };

  try {
    map.fitBounds([[minLon, minLat], [maxLon, maxLat]], { padding: paddingPx, duration: 0, maxZoom });
    await waitForMapReady(map, timeoutMs);
    return await captureMapScreenshot(mapContainerId, projectPoints, includeOverlays);
  } finally {
    try {
      map.jumpTo({ center: prev.center, zoom: prev.zoom, bearing: prev.bearing, pitch: prev.pitch });
      await waitForMapReady(map, timeoutMs);
    } catch {}
  }
};

// ==========================================
// LINK ANALYSIS POWERPOINT EXPORT
// ==========================================

export interface LinkAnalysisNode {
  id: string;
  type: 'device' | 'location' | 'phone' | 'url' | 'app' | 'website';
  label?: string;
  entity_id?: string;
  entity_type?: string;
  place_name?: string;
  latitude?: number;
  longitude?: number;
  app_name?: string;
  package_name?: string;
}

export interface LinkAnalysisEdge {
  source: string;
  target: string;
  type?: string;
  connection_type?: string;
  timestamp?: string;
  duration?: number;
  weight?: number;
  // SMS-specific
  sms_number?: string;
  sms_message?: string;
  comms_direction?: string;
  // Call-specific
  call_logs_number?: string;
  call_logs_duration?: number;
  // Browsing-specific
  url?: string;
  // App-specific
  package_name?: string;
  total_time?: number;
}

export interface LinkAnalysisStats {
  totalNodes: number;
  totalLinks: number;
  deviceCount: number;
  locationCount: number;
  phoneCount: number;
  websiteCount: number;
  appCount: number;
  callCount: number;
  smsCount: number;
  browsingCount: number;
  appUsageCount: number;
  dwellCount: number;
  sharedLocationsCount?: number;
  dateRange?: { start: Date | null; end: Date | null };
  selectedEntities?: string[];
}

export interface LinkAnalysisExportOptions {
  title: string;
  subtitle?: string;
  nodes: LinkAnalysisNode[];
  links: LinkAnalysisEdge[];
  stats: LinkAnalysisStats;
  graphScreenshot?: { dataUrl: string; width: number; height: number };
  mapScreenshot?: { dataUrl: string; width: number; height: number };
  aiNarrative?: string;
  theme?: PowerPointTheme;
  includeGraph?: boolean;
  includeMap?: boolean;
  includeStats?: boolean;
}

/**
 * Capture the network graph canvas from react-force-graph-2d at higher resolution
 */
export const captureNetworkGraph = async (
  containerId: string = 'network-graph-container',
  scale: number = 2 // Default 2x resolution for better quality
): Promise<{ dataUrl: string; width: number; height: number } | undefined> => {
  try {
    const container = document.getElementById(containerId);
    if (!container) {
      console.error('[Graph Capture] Container not found:', containerId);
      return undefined;
    }

    // react-force-graph-2d renders to a canvas element inside the container
    const canvas = container.querySelector('canvas') as HTMLCanvasElement;
    if (!canvas) {
      console.error('[Graph Capture] Canvas not found in container');
      return undefined;
    }

    const width = canvas.clientWidth || canvas.width;
    const height = canvas.clientHeight || canvas.height;

    // Create a scaled canvas for higher resolution capture
    const scaledCanvas = document.createElement('canvas');
    scaledCanvas.width = width * scale;
    scaledCanvas.height = height * scale;
    const ctx = scaledCanvas.getContext('2d');

    if (!ctx) {
      console.error('[Graph Capture] Could not get 2D context');
      return undefined;
    }

    // Scale and draw the original canvas content
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.scale(scale, scale);
    ctx.drawImage(canvas, 0, 0, width, height);

    let dataUrl: string;
    try {
      dataUrl = scaledCanvas.toDataURL('image/png');
    } catch (e) {
      console.error('[Graph Capture] Failed to capture scaled canvas, trying original:', e);
      // Fallback to original canvas
      try {
        dataUrl = canvas.toDataURL('image/png');
        return { dataUrl, width: canvas.width, height: canvas.height };
      } catch (e2) {
        console.error('[Graph Capture] Failed to capture canvas:', e2);
        return undefined;
      }
    }

    return { dataUrl, width: scaledCanvas.width, height: scaledCanvas.height };
  } catch (error) {
    console.error('[Graph Capture] Failed:', error);
    return undefined;
  }
};

/**
 * Capture the Link Analysis map (NetworkGraphMap component)
 */
export const captureLinkAnalysisMap = async (
  containerId: string = 'network-map-container'
): Promise<{ dataUrl: string; width: number; height: number } | undefined> => {
  // Use the same capture logic as Pathfinder map
  const result = await captureMapScreenshot(containerId, undefined, true);
  if (result) {
    return {
      dataUrl: result.dataUrl,
      width: result.width,
      height: result.height
    };
  }
  return undefined;
};

/**
 * Generate AI narrative for Link Analysis data
 */
export const generateLinkAnalysisNarrative = async (
  nodes: LinkAnalysisNode[],
  links: LinkAnalysisEdge[],
  stats: LinkAnalysisStats,
  selectedEntities?: string[]
): Promise<string> => {
  try {
    // Build context in the format expected by the backend
    const networkStats = {
      totalNodes: stats.totalNodes,
      totalLinks: stats.totalLinks,
      deviceCount: stats.deviceCount,
      locationCount: stats.locationCount,
      phoneCount: stats.phoneCount,
      nodeTypes: {
        device: stats.deviceCount,
        location: stats.locationCount,
        phone: stats.phoneCount,
        website: stats.websiteCount,
        app: stats.appCount,
      },
      connectionTypes: {
        call: stats.callCount,
        sms: stats.smsCount,
        browsing: stats.browsingCount,
        app: stats.appUsageCount,
        dwell: stats.dwellCount,
      },
    };

    const deviceDetails = nodes
      .filter(n => n.type === 'device')
      .slice(0, 20)
      .map(n => ({
        id: n.id,
        label: n.label || n.id,
        entityType: n.entity_type || 'Device',
        connections: links.filter(l => l.source === n.id || l.target === n.id).length,
      }));

    const locationDetails = nodes
      .filter(n => n.type === 'location')
      .slice(0, 20)
      .map(n => ({
        id: n.id,
        name: n.place_name || n.label || n.id,
        type: 'Location',
        lat: n.latitude,
        lon: n.longitude,
      }));

    const apiContext = {
      networkStats,
      deviceDetails,
      locationDetails,
      selectedEntities: selectedEntities || [],
    };

    const response = await fetch('/api/link-analysis-chat/ask', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`,
      },
      body: JSON.stringify({
        question: 'Generate a comprehensive executive summary and analysis of this link analysis network data for a PowerPoint briefing. Include: 1) Key findings about the network structure, 2) Notable connection patterns between entities, 3) Significant locations or phone numbers, 4) Communication pattern insights, 5) Actionable recommendations for further investigation. Format with clear sections and bullet points for easy reading in a presentation.',
        context: apiContext,
      }),
    });

    if (response.ok) {
      const data = await response.json();
      return data.answer || '';
    }
    return '';
  } catch (error) {
    console.error('[Link Analysis] Failed to generate AI narrative:', error);
    return '';
  }
};

// ─────────────────────────────────────────────────────────────
// Per-channel slide builders (Calls / SMS / Browsing / App Usage)
// ─────────────────────────────────────────────────────────────

type ChannelKey = 'call' | 'sms' | 'browsing' | 'app';

interface ChannelSlideConfig {
  key: ChannelKey;
  matchTypes: string[];      // edge type / connection_type values that belong to this channel
  title: string;             // slide title (e.g. "Calls")
  subtitle: string;          // descriptive line under title
  counterpartHeader: string; // "Number" | "Site" | "App"
  counterpartGroupLabel: string; // "PHONE NUMBERS" | "WEBSITES" | "APPLICATIONS"
  showDuration: boolean;     // include total talk-time / app-time
  showDirection: boolean;    // include inbound/outbound split
  durationLabel: string;     // "Total talk time" | "Total app time"
}

const CHANNEL_CONFIGS: ChannelSlideConfig[] = [
  {
    key: 'call',
    matchTypes: ['call', 'calls', 'call_log', 'call_logs'],
    title: 'Calls',
    subtitle: 'Voice communication patterns',
    counterpartHeader: 'Number',
    counterpartGroupLabel: 'TOP PHONE NUMBERS',
    showDuration: true,
    showDirection: true,
    durationLabel: 'Total talk time',
  },
  {
    key: 'sms',
    matchTypes: ['sms', 'mms', 'text'],
    title: 'SMS Messages',
    subtitle: 'Text messaging patterns',
    counterpartHeader: 'Number',
    counterpartGroupLabel: 'TOP PHONE NUMBERS',
    showDuration: false,
    showDirection: true,
    durationLabel: '',
  },
  {
    key: 'browsing',
    matchTypes: ['browsing', 'browse', 'web', 'url'],
    title: 'Browsing Activity',
    subtitle: 'Web visit patterns',
    counterpartHeader: 'Site',
    counterpartGroupLabel: 'TOP WEBSITES',
    showDuration: false,
    showDirection: false,
    durationLabel: '',
  },
  {
    key: 'app',
    matchTypes: ['app', 'app_use', 'app_usage', 'application'],
    title: 'App Usage',
    subtitle: 'Application activity patterns',
    counterpartHeader: 'App',
    counterpartGroupLabel: 'TOP APPLICATIONS',
    showDuration: true,
    showDirection: false,
    durationLabel: 'Total app time',
  },
];

interface ChannelAggregations {
  totalCount: number;
  uniqueCounterparts: number;
  uniqueDevices: number;
  totalDurationS: number;
  inboundCount: number;
  outboundCount: number;
  unknownDirCount: number;
  earliest: Date | null;
  latest: Date | null;
  topCounterparts: Array<{ label: string; count: number; durationS: number }>;
  topDevices: Array<{ label: string; count: number }>;
  peakHourLabel: string;
}

function _formatChannelDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function _formatPeakHours(hourCounts: number[]): string {
  const total = hourCounts.reduce((a, b) => a + b, 0);
  if (total === 0) return 'No timestamp data';
  // Pick top 3 hours, then collapse contiguous ranges
  const ranked = hourCounts
    .map((c, h) => ({ h, c }))
    .filter(x => x.c > 0)
    .sort((a, b) => b.c - a.c)
    .slice(0, 3)
    .map(x => x.h)
    .sort((a, b) => a - b);
  if (ranked.length === 0) return 'No timestamp data';
  const ranges: Array<[number, number]> = [];
  let start = ranked[0];
  let end = ranked[0];
  for (let i = 1; i < ranked.length; i++) {
    if (ranked[i] === end + 1) end = ranked[i];
    else { ranges.push([start, end]); start = ranked[i]; end = ranked[i]; }
  }
  ranges.push([start, end]);
  const fmt = (h: number) => {
    const ampm = h < 12 ? 'AM' : 'PM';
    const hr = h % 12 === 0 ? 12 : h % 12;
    return `${hr}${ampm}`;
  };
  return ranges.map(([a, b]) => a === b ? fmt(a) : `${fmt(a)}–${fmt(b + 1)}`).join(', ');
}

function _resolveEndpointLabel(
  edge: LinkAnalysisEdge,
  endpointId: string,
  nodeMap: Map<string, LinkAnalysisNode>,
  channel: ChannelKey,
  side: 'counterpart' | 'device',
): string {
  const node = nodeMap.get(endpointId);
  if (node) {
    if (side === 'device') return node.label || node.entity_id || node.id;
    if (channel === 'app') return node.app_name || node.label || node.package_name || node.id;
    return node.label || node.place_name || node.id;
  }
  // Fall back to channel-specific edge fields if the node wasn't included
  if (side === 'counterpart') {
    if (channel === 'call' && edge.call_logs_number) return edge.call_logs_number;
    if (channel === 'sms' && edge.sms_number) return edge.sms_number;
    if (channel === 'browsing' && edge.url) return edge.url;
    if (channel === 'app' && edge.package_name) return edge.package_name;
  }
  return endpointId;
}

function buildChannelAggregations(
  edges: LinkAnalysisEdge[],
  nodes: LinkAnalysisNode[],
  channel: ChannelSlideConfig,
): ChannelAggregations {
  const nodeMap = new Map<string, LinkAnalysisNode>(nodes.map(n => [n.id, n]));
  const isMatch = (e: LinkAnalysisEdge) => {
    const t = (e.connection_type || e.type || '').toLowerCase();
    return channel.matchTypes.includes(t);
  };

  const counterpartCounts = new Map<string, { label: string; count: number; durationS: number }>();
  const deviceCounts = new Map<string, { label: string; count: number }>();
  const hourHist = new Array(24).fill(0);
  let totalDurationS = 0;
  let inbound = 0;
  let outbound = 0;
  let unknownDir = 0;
  let earliest: Date | null = null;
  let latest: Date | null = null;
  let total = 0;

  for (const edge of edges) {
    if (!isMatch(edge)) continue;
    total += 1;

    // Identify device side and counterpart side
    const sNode = nodeMap.get(edge.source);
    const tNode = nodeMap.get(edge.target);
    let deviceId: string | null = null;
    let counterpartId: string | null = null;
    if (sNode?.type === 'device' && tNode && tNode.type !== 'device') {
      deviceId = edge.source; counterpartId = edge.target;
    } else if (tNode?.type === 'device' && sNode && sNode.type !== 'device') {
      deviceId = edge.target; counterpartId = edge.source;
    } else if (sNode?.type === 'device') {
      deviceId = edge.source; counterpartId = edge.target;
    } else if (tNode?.type === 'device') {
      deviceId = edge.target; counterpartId = edge.source;
    } else {
      counterpartId = edge.target;
      deviceId = edge.source;
    }

    // Duration accumulation (calls or apps)
    let durationS = 0;
    if (channel.key === 'call') durationS = edge.call_logs_duration ?? edge.duration ?? 0;
    else if (channel.key === 'app') durationS = edge.total_time ?? edge.duration ?? 0;
    if (durationS > 0) totalDurationS += durationS;

    // Direction split
    const dir = (edge.comms_direction || '').toLowerCase();
    if (dir === 'incoming' || dir === 'in' || dir === 'inbound') inbound += 1;
    else if (dir === 'outgoing' || dir === 'out' || dir === 'outbound') outbound += 1;
    else unknownDir += 1;

    // Timestamps + peak hour
    if (edge.timestamp) {
      const d = new Date(edge.timestamp);
      if (!isNaN(d.getTime())) {
        if (!earliest || d < earliest) earliest = d;
        if (!latest || d > latest) latest = d;
        hourHist[d.getHours()] += 1;
      }
    }

    // Aggregate counterpart
    if (counterpartId) {
      const existing = counterpartCounts.get(counterpartId);
      if (existing) {
        existing.count += 1;
        existing.durationS += durationS;
      } else {
        counterpartCounts.set(counterpartId, {
          label: _resolveEndpointLabel(edge, counterpartId, nodeMap, channel.key, 'counterpart'),
          count: 1,
          durationS,
        });
      }
    }

    // Aggregate device
    if (deviceId) {
      const existing = deviceCounts.get(deviceId);
      if (existing) existing.count += 1;
      else deviceCounts.set(deviceId, {
        label: _resolveEndpointLabel(edge, deviceId, nodeMap, channel.key, 'device'),
        count: 1,
      });
    }
  }

  const topCounterparts = Array.from(counterpartCounts.values())
    .sort((a, b) => b.count - a.count || b.durationS - a.durationS)
    .slice(0, 10);
  const topDevices = Array.from(deviceCounts.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  return {
    totalCount: total,
    uniqueCounterparts: counterpartCounts.size,
    uniqueDevices: deviceCounts.size,
    totalDurationS,
    inboundCount: inbound,
    outboundCount: outbound,
    unknownDirCount: unknownDir,
    earliest,
    latest,
    topCounterparts,
    topDevices,
    peakHourLabel: _formatPeakHours(hourHist),
  };
}

interface ChannelSlideVisuals {
  contentBgDataUrl: string;
  logoDataUrl: string;
  colors: ThemeColors;
}

function addLinkAnalysisChannelSlide(
  pptx: any,
  channel: ChannelSlideConfig,
  agg: ChannelAggregations,
  visuals: ChannelSlideVisuals,
): void {
  const { contentBgDataUrl, logoDataUrl, colors } = visuals;
  const slide = pptx.addSlide();
  if (contentBgDataUrl) addFullBleedBackground(slide, contentBgDataUrl);
  else slide.background = { color: colors.contentBg };
  addFooter(slide, 'LINK ANALYSIS • UNCLASSIFIED', colors.lightText);
  if (logoDataUrl) addCornerLogo(slide, logoDataUrl);

  slide.addText(channel.title, { x: 0.5, y: 0.4, w: 12.0, h: 0.55, fontSize: 28, bold: true, color: colors.primary });
  slide.addText(channel.subtitle, { x: 0.5, y: 0.95, w: 12.0, h: 0.3, fontSize: 12, color: colors.lightText, italic: true });

  // ---- KPI band: 4 cards across the top ----
  const dateSpan = (() => {
    if (!agg.earliest || !agg.latest) return '—';
    const ms = agg.latest.getTime() - agg.earliest.getTime();
    const days = Math.max(1, Math.round(ms / (1000 * 60 * 60 * 24)));
    return days === 1 ? '1 day' : `${days} days`;
  })();

  const kpis: Array<{ value: string; label: string }> = [
    { value: agg.totalCount.toLocaleString(), label: 'Total events' },
    { value: agg.uniqueCounterparts.toLocaleString(), label: `Unique ${channel.counterpartHeader.toLowerCase()}s` },
    { value: agg.uniqueDevices.toLocaleString(), label: 'Devices involved' },
    { value: dateSpan, label: 'Period covered' },
  ];

  const kpiY = 1.4;
  const kpiH = 0.95;
  const kpiW = 2.95;
  const kpiGap = 0.15;
  const kpiStartX = 0.5;
  kpis.forEach((kpi, i) => {
    const x = kpiStartX + i * (kpiW + kpiGap);
    slide.addShape('rect', { x, y: kpiY, w: kpiW, h: kpiH, fill: { color: 'f3f4f6' }, line: { color: 'e5e7eb', width: 1 } });
    slide.addText(kpi.value, { x, y: kpiY + 0.05, w: kpiW, h: 0.5, fontSize: 22, bold: true, color: colors.primary, align: 'center' });
    slide.addText(kpi.label, { x, y: kpiY + 0.55, w: kpiW, h: 0.35, fontSize: 10, color: colors.text, align: 'center' });
  });

  if (agg.totalCount === 0) {
    slide.addText('No events recorded for this channel in the selected period.', {
      x: 0.5, y: 3.5, w: 12.0, h: 0.5,
      fontSize: 14, italic: true, color: colors.lightText, align: 'center',
    });
    return;
  }

  // ---- Top counterparts table (left) ----
  const tableY = 2.65;
  const tableH = 3.5;
  const counterpartHeaderRow = [
    { text: '#', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'left' as const } },
    { text: channel.counterpartHeader, options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'left' as const } },
    { text: 'Count', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'right' as const } },
  ];
  if (channel.showDuration) {
    counterpartHeaderRow.push({
      text: 'Duration',
      options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'right' as const },
    });
  }
  const counterpartRows = agg.topCounterparts.map((c, idx) => {
    const stripe = idx % 2 === 0 ? 'f3f4f6' : 'ffffff';
    const row: Array<{ text: string; options: any }> = [
      { text: String(idx + 1), options: { color: colors.lightText, fill: { color: stripe } } },
      { text: c.label.length > 38 ? c.label.slice(0, 35) + '…' : c.label, options: { color: colors.text, fill: { color: stripe } } },
      { text: c.count.toLocaleString(), options: { color: colors.secondary, align: 'right' as const, fill: { color: stripe } } },
    ];
    if (channel.showDuration) {
      row.push({ text: _formatChannelDuration(c.durationS), options: { color: colors.secondary, align: 'right' as const, fill: { color: stripe } } });
    }
    return row;
  });

  const counterpartTable = [counterpartHeaderRow, ...counterpartRows];
  slide.addText(channel.counterpartGroupLabel, { x: 0.5, y: 2.55, w: 6.2, h: 0.3, fontSize: 11, bold: true, color: colors.primary });
  slide.addTable(counterpartTable, {
    x: 0.5,
    y: 2.9,
    w: 6.2,
    h: tableH,
    fontSize: 10,
    colW: channel.showDuration ? [0.45, 3.1, 1.1, 1.55] : [0.45, 3.95, 1.8],
    border: { type: 'solid', pt: 1, color: 'e5e7eb' },
  });

  // ---- Top devices table (right) ----
  const deviceTable: any[][] = [
    [
      { text: '#', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'left' as const } },
      { text: 'Device / Entity', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'left' as const } },
      { text: 'Events', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'right' as const } },
    ],
    ...agg.topDevices.map((d, idx) => {
      const stripe = idx % 2 === 0 ? 'f3f4f6' : 'ffffff';
      return [
        { text: String(idx + 1), options: { color: colors.lightText, fill: { color: stripe } } },
        { text: d.label.length > 30 ? d.label.slice(0, 27) + '…' : d.label, options: { color: colors.text, fill: { color: stripe } } },
        { text: d.count.toLocaleString(), options: { color: colors.secondary, align: 'right' as const, fill: { color: stripe } } },
      ];
    }),
  ];
  slide.addText('TOP DEVICES', { x: 6.95, y: 2.55, w: 5.9, h: 0.3, fontSize: 11, bold: true, color: colors.primary });
  slide.addTable(deviceTable, {
    x: 6.95,
    y: 2.9,
    w: 5.9,
    h: tableH,
    fontSize: 10,
    colW: [0.45, 3.85, 1.6],
    border: { type: 'solid', pt: 1, color: 'e5e7eb' },
  });

  // ---- Footer band: peak hours, duration total, direction split ----
  const footerY = 6.55;
  const footerParts: string[] = [];
  footerParts.push(`Peak activity: ${agg.peakHourLabel}`);
  if (channel.showDuration && agg.totalDurationS > 0) {
    footerParts.push(`${channel.durationLabel}: ${_formatChannelDuration(agg.totalDurationS)}`);
  }
  if (channel.showDirection && (agg.inboundCount > 0 || agg.outboundCount > 0)) {
    const known = agg.inboundCount + agg.outboundCount;
    if (known > 0) {
      const outPct = Math.round((agg.outboundCount / known) * 100);
      const inPct = 100 - outPct;
      footerParts.push(`Direction: ${outPct}% outbound / ${inPct}% inbound`);
    }
  }
  if (agg.earliest && agg.latest) {
    const fmtDate = (d: Date) => d.toLocaleDateString();
    footerParts.push(`Range: ${fmtDate(agg.earliest)} – ${fmtDate(agg.latest)}`);
  }

  slide.addText(footerParts.join('   |   '), {
    x: 0.5, y: footerY, w: 12.35, h: 0.4,
    fontSize: 10, color: colors.text,
    align: 'left', valign: 'middle',
    fill: { color: 'f8f9fa' },
    margin: 0.1,
  });
}

/**
 * Export Link Analysis data to PowerPoint
 */
export const exportLinkAnalysisToPowerPoint = async (options: LinkAnalysisExportOptions): Promise<void> => {
  const {
    title,
    subtitle,
    nodes,
    links,
    stats,
    graphScreenshot,
    mapScreenshot,
    aiNarrative,
    theme = 'standard',
    includeGraph = true,
    includeMap = true,
    includeStats = true,
  } = options;

  const PptxGenJS = await loadPptxGenJS();
  const pptx = new PptxGenJS();
  pptx.layout = 'LAYOUT_WIDE';
  pptx.author = 'Link Analysis System';
  pptx.company = 'Quiet Professionals';
  pptx.subject = 'Link Analysis Report';
  pptx.title = title;

  const colors = THEME_DEFINITIONS[theme].colors;
  const titleBgDataUrl = generateGradientBackground(1920, 1080, 'title', theme);
  const contentBgDataUrl = generateGradientBackground(1920, 1080, 'content', theme);

  let logoDataUrl = '';
  try {
    logoDataUrl = await fetchAsDataUrl('/pptx-template-pathfinder/assets/logo-pyramid.jpeg');
  } catch (e) {
    console.warn('Failed to load logo:', e);
  }

  // TITLE SLIDE
  const titleSlide = pptx.addSlide();
  if (titleBgDataUrl) addFullBleedBackground(titleSlide, titleBgDataUrl);
  else titleSlide.background = { color: colors.titleBg };
  if (logoDataUrl) addCornerLogo(titleSlide, logoDataUrl, { x: SLIDE_W - 1.5, y: SLIDE_H - 1.5, w: 1.1, h: 1.1 });

  titleSlide.addText(title, { x: 0.75, y: 2.2, w: SLIDE_W - 1.5, h: 1.0, fontSize: 40, bold: true, color: colors.titleText, align: 'left' });
  if (subtitle) {
    titleSlide.addText(subtitle, { x: 0.75, y: 3.3, w: SLIDE_W - 1.5, h: 0.5, fontSize: 22, color: colors.titleText, align: 'left' });
  }
  
  const entityList = stats.selectedEntities?.join(', ') || 'All Entities';
  titleSlide.addText(`ENTITIES: ${entityList.toUpperCase()}`, { x: 0.75, y: 4.1, w: SLIDE_W - 1.5, h: 0.4, fontSize: 14, color: colors.accent, align: 'left', bold: true });
  
  if (stats.dateRange?.start && stats.dateRange?.end) {
    const dateStr = `${stats.dateRange.start.toLocaleDateString()} – ${stats.dateRange.end.toLocaleDateString()}`;
    titleSlide.addText(`Analysis Period: ${dateStr}`, { x: 0.75, y: 4.6, w: SLIDE_W - 1.5, h: 0.3, fontSize: 12, color: colors.titleText, align: 'left' });
  }
  
  titleSlide.addText(`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`, { x: 0.75, y: 5.5, w: SLIDE_W - 1.5, h: 0.3, fontSize: 12, color: colors.titleText, align: 'left' });

  // STATISTICS SLIDE
  if (includeStats) {
    const statsSlide = pptx.addSlide();
    if (contentBgDataUrl) addFullBleedBackground(statsSlide, contentBgDataUrl);
    else statsSlide.background = { color: colors.contentBg };
    addFooter(statsSlide, 'LINK ANALYSIS • UNCLASSIFIED', colors.lightText);
    if (logoDataUrl) addCornerLogo(statsSlide, logoDataUrl);

    statsSlide.addText('Network Statistics', { x: 0.5, y: 0.4, w: 12.0, h: 0.6, fontSize: 28, bold: true, color: colors.primary });

    // Network overview stats
    const networkStats = [
      ['Total Nodes', stats.totalNodes.toLocaleString()],
      ['Total Connections', stats.totalLinks.toLocaleString()],
      ['Devices', stats.deviceCount.toLocaleString()],
      ['Locations', stats.locationCount.toLocaleString()],
      ['Phone Numbers', stats.phoneCount.toLocaleString()],
      ['Websites', stats.websiteCount.toLocaleString()],
      ['Apps', stats.appCount.toLocaleString()],
    ].map(([label, value], idx) => [
      { text: label, options: { bold: true, color: colors.text, fill: { color: idx % 2 === 0 ? 'f3f4f6' : 'ffffff' } } },
      { text: value, options: { color: colors.secondary, align: 'right' as const, fill: { color: idx % 2 === 0 ? 'f3f4f6' : 'ffffff' } } }
    ]);

    statsSlide.addText('NETWORK OVERVIEW', { x: 0.5, y: 1.1, w: 5.5, h: 0.3, fontSize: 12, bold: true, color: colors.primary });
    statsSlide.addTable(networkStats, { x: 0.5, y: 1.45, w: 5.5, rowH: 0.4, fontSize: 12, border: { type: 'solid', pt: 1, color: 'e5e7eb' }, colW: [3.5, 2.0] });

    // Connection type stats
    const connectionStats = [
      ['Call Logs', stats.callCount.toLocaleString()],
      ['SMS Messages', stats.smsCount.toLocaleString()],
      ['Browsing History', stats.browsingCount.toLocaleString()],
      ['App Usage', stats.appUsageCount.toLocaleString()],
      ['Dwell Locations', stats.dwellCount.toLocaleString()],
    ].map(([label, value], idx) => [
      { text: label, options: { bold: true, color: colors.text, fill: { color: idx % 2 === 0 ? 'f3f4f6' : 'ffffff' } } },
      { text: value, options: { color: colors.secondary, align: 'right' as const, fill: { color: idx % 2 === 0 ? 'f3f4f6' : 'ffffff' } } }
    ]);

    statsSlide.addText('CONNECTION TYPES', { x: 6.8, y: 1.1, w: 5.5, h: 0.3, fontSize: 12, bold: true, color: colors.primary });
    statsSlide.addTable(connectionStats, { x: 6.8, y: 1.45, w: 5.5, rowH: 0.4, fontSize: 12, border: { type: 'solid', pt: 1, color: 'e5e7eb' }, colW: [3.5, 2.0] });

    if (stats.sharedLocationsCount && stats.sharedLocationsCount > 0) {
      statsSlide.addText(`Shared Locations: ${stats.sharedLocationsCount} locations visited by multiple entities`, { x: 0.5, y: 5.5, w: 12.0, h: 0.4, fontSize: 14, color: colors.accent, bold: true });
    }
  }

  // COMBINED NETWORK GRAPH + MAP SLIDE
  if ((includeGraph && graphScreenshot) || (includeMap && mapScreenshot)) {
    const vizSlide = pptx.addSlide();
    if (contentBgDataUrl) addFullBleedBackground(vizSlide, contentBgDataUrl);
    else vizSlide.background = { color: colors.contentBg };
    addFooter(vizSlide, 'LINK ANALYSIS • UNCLASSIFIED', colors.lightText);
    if (logoDataUrl) addCornerLogo(vizSlide, logoDataUrl);

    vizSlide.addText('Network Analysis', { x: 0.5, y: 0.4, w: 12.0, h: 0.5, fontSize: 28, bold: true, color: colors.primary });

    // Build context summary paragraph from stats
    const contextParts: string[] = [];
    const entityNames = stats.selectedEntities?.join(', ') || 'selected entities';
    
    // Core network summary
    contextParts.push(`This analysis visualizes the digital footprint of ${entityNames}, comprising ${stats.totalNodes.toLocaleString()} nodes and ${stats.totalLinks.toLocaleString()} connections.`);
    
    // Node breakdown
    const nodeTypes: string[] = [];
    if (stats.deviceCount > 0) nodeTypes.push(`${stats.deviceCount} device${stats.deviceCount !== 1 ? 's' : ''}`);
    if (stats.locationCount > 0) nodeTypes.push(`${stats.locationCount} location${stats.locationCount !== 1 ? 's' : ''}`);
    if (stats.phoneCount > 0) nodeTypes.push(`${stats.phoneCount} phone number${stats.phoneCount !== 1 ? 's' : ''}`);
    if (stats.websiteCount > 0) nodeTypes.push(`${stats.websiteCount} website${stats.websiteCount !== 1 ? 's' : ''}`);
    if (stats.appCount > 0) nodeTypes.push(`${stats.appCount} app${stats.appCount !== 1 ? 's' : ''}`);
    if (nodeTypes.length > 0) {
      contextParts.push(`The network includes ${nodeTypes.join(', ')}.`);
    }
    
    // Connection types
    const connTypes: string[] = [];
    if (stats.callCount > 0) connTypes.push(`${stats.callCount} call${stats.callCount !== 1 ? 's' : ''}`);
    if (stats.smsCount > 0) connTypes.push(`${stats.smsCount} SMS message${stats.smsCount !== 1 ? 's' : ''}`);
    if (stats.dwellCount > 0) connTypes.push(`${stats.dwellCount} dwell event${stats.dwellCount !== 1 ? 's' : ''}`);
    if (stats.browsingCount > 0) connTypes.push(`${stats.browsingCount} web visit${stats.browsingCount !== 1 ? 's' : ''}`);
    if (connTypes.length > 0) {
      contextParts.push(`Activity captured: ${connTypes.join(', ')}.`);
    }
    
    // Shared locations insight
    if (stats.sharedLocationsCount && stats.sharedLocationsCount > 0) {
      contextParts.push(`${stats.sharedLocationsCount} shared location${stats.sharedLocationsCount !== 1 ? 's were' : ' was'} identified where multiple entities co-located.`);
    }
    
    const contextSummary = contextParts.join(' ');

    // If we have both, show side by side with context at bottom
    if (includeGraph && graphScreenshot && includeMap && mapScreenshot) {
      // Left side: Network Graph
      const leftX = 0.4, leftY = 0.95;
      const halfW = 6.2, halfH = 5.0;
      
      vizSlide.addText('Network Graph', { x: leftX, y: leftY, w: halfW, h: 0.25, fontSize: 10, bold: true, color: colors.primary });
      
      const graphAr = graphScreenshot.width / graphScreenshot.height;
      let graphW = halfW, graphH = halfW / graphAr;
      if (graphH > halfH - 0.3) { graphH = halfH - 0.3; graphW = graphH * graphAr; }
      const graphX = leftX + (halfW - graphW) / 2;
      const graphY = leftY + 0.28 + (halfH - 0.3 - graphH) / 2;
      
      vizSlide.addImage({ data: graphScreenshot.dataUrl, x: graphX, y: graphY, w: graphW, h: graphH });

      // Right side: Map
      const rightX = 6.7, rightY = 0.95;
      
      vizSlide.addText('Location Analysis', { x: rightX, y: rightY, w: halfW, h: 0.25, fontSize: 10, bold: true, color: colors.primary });
      
      const mapAr = mapScreenshot.width / mapScreenshot.height;
      let mapW = halfW, mapH = halfW / mapAr;
      if (mapH > halfH - 0.3) { mapH = halfH - 0.3; mapW = mapH * mapAr; }
      const mapX = rightX + (halfW - mapW) / 2;
      const mapY = rightY + 0.28 + (halfH - 0.3 - mapH) / 2;
      
      vizSlide.addImage({ data: mapScreenshot.dataUrl, x: mapX, y: mapY, w: mapW, h: mapH });
      
      // Context summary at bottom
      vizSlide.addText(contextSummary, { 
        x: 0.4, y: 6.1, w: 12.5, h: 0.9, 
        fontSize: 9, color: colors.text, 
        align: 'left', valign: 'top', 
        lineSpacing: 11,
        fill: { color: 'f8f9fa' },
        margin: 0.1
      });
    } else if (includeGraph && graphScreenshot) {
      // Only graph - show full width with context at bottom
      const maxW = 12.0, maxH = 4.8;
      const ar = graphScreenshot.width / graphScreenshot.height;
      let imgW = maxW, imgH = maxW / ar;
      if (imgH > maxH) { imgH = maxH; imgW = maxH * ar; }
      const imgX = 0.5 + (maxW - imgW) / 2;
      const imgY = 0.95 + (maxH - imgH) / 2;

      vizSlide.addImage({ data: graphScreenshot.dataUrl, x: imgX, y: imgY, w: imgW, h: imgH });
      
      // Context summary at bottom
      vizSlide.addText(contextSummary, { 
        x: 0.4, y: 5.9, w: 12.5, h: 1.1, 
        fontSize: 10, color: colors.text, 
        align: 'left', valign: 'top', 
        lineSpacing: 12,
        fill: { color: 'f8f9fa' },
        margin: 0.12
      });
    } else if (includeMap && mapScreenshot) {
      // Only map - show full width with context at bottom
      const maxW = 12.0, maxH = 4.8;
      const ar = mapScreenshot.width / mapScreenshot.height;
      let imgW = maxW, imgH = maxW / ar;
      if (imgH > maxH) { imgH = maxH; imgW = maxH * ar; }
      const imgX = 0.5 + (maxW - imgW) / 2;
      const imgY = 0.95 + (maxH - imgH) / 2;

      vizSlide.addImage({ data: mapScreenshot.dataUrl, x: imgX, y: imgY, w: imgW, h: imgH });
      
      // Context summary at bottom
      vizSlide.addText(contextSummary, { 
        x: 0.4, y: 5.9, w: 12.5, h: 1.1, 
        fontSize: 10, color: colors.text, 
        align: 'left', valign: 'top', 
        lineSpacing: 12,
        fill: { color: 'f8f9fa' },
        margin: 0.12
      });
    }
  }

  // PER-CHANNEL DEEP-DIVE SLIDES (Calls / SMS / Browsing / App Usage)
  // One slide per channel; channels with zero events are skipped so we don't
  // pad the deck with empty pages.
  const channelVisuals: ChannelSlideVisuals = {
    contentBgDataUrl,
    logoDataUrl,
    colors,
  };
  for (const channelCfg of CHANNEL_CONFIGS) {
    const agg = buildChannelAggregations(links, nodes, channelCfg);
    if (agg.totalCount === 0) continue;
    addLinkAnalysisChannelSlide(pptx, channelCfg, agg, channelVisuals);
  }

  // AI ANALYSIS SLIDE
  if (aiNarrative) {
    const cleanNarrative = sanitizeAiNarrative(aiNarrative);
    const chunks = splitTextForSlides(cleanNarrative, 2200);

    for (let i = 0; i < chunks.length; i++) {
      const aiSlide = pptx.addSlide();
      if (contentBgDataUrl) addFullBleedBackground(aiSlide, contentBgDataUrl);
      else aiSlide.background = { color: colors.contentBg };
      addFooter(aiSlide, 'LINK ANALYSIS • UNCLASSIFIED', colors.lightText);
      if (logoDataUrl) addCornerLogo(aiSlide, logoDataUrl);

      const slideTitle = i === 0 ? 'AI-Generated Analysis' : 'AI-Generated Analysis (continued)';
      aiSlide.addText(slideTitle, { x: 0.5, y: 0.4, w: 12.0, h: 0.6, fontSize: 28, bold: true, color: colors.primary });

      if (i === 0) {
        // First slide has analyst comments box
        aiSlide.addText('ANALYST COMMENTS', { x: 0.5, y: 1.1, w: 12.3, h: 0.3, fontSize: 11, bold: true, color: colors.primary });
        aiSlide.addText('[Enter analyst observations and assessments here]', { x: 0.5, y: 1.45, w: 12.3, h: 0.7, fontSize: 11, italic: true, color: '888888', line: { color: 'cccccc', width: 1 }, margin: 0.1, valign: 'top' });

        aiSlide.addText(chunks[i], { x: 0.5, y: 2.4, w: 12.3, h: 4.5, fontSize: 10, color: colors.text, align: 'left', valign: 'top', lineSpacing: 12, margin: 0.1 });
      } else {
        aiSlide.addText(chunks[i], { x: 0.5, y: 1.1, w: 12.3, h: 5.8, fontSize: 10, color: colors.text, align: 'left', valign: 'top', lineSpacing: 12, margin: 0.1 });
      }
    }
  }

  // OBSERVATIONS SLIDE
  const obsSlide = pptx.addSlide();
  if (contentBgDataUrl) addFullBleedBackground(obsSlide, contentBgDataUrl);
  else obsSlide.background = { color: colors.contentBg };
  addFooter(obsSlide, 'LINK ANALYSIS • UNCLASSIFIED', colors.lightText);
  if (logoDataUrl) addCornerLogo(obsSlide, logoDataUrl);

  obsSlide.addText('Observations and Recommendations', { x: 0.5, y: 0.4, w: 12.0, h: 0.6, fontSize: 28, bold: true, color: colors.primary });
  
  // Cadre/Analyst comments block at top (full width)
  obsSlide.addText('CADRE COMMENTS', { x: 0.5, y: 1.1, w: 12.3, h: 0.3, fontSize: 11, bold: true, color: colors.primary });
  obsSlide.addText('[Enter cadre observations and assessments here]', { x: 0.5, y: 1.45, w: 12.3, h: 0.8, fontSize: 11, italic: true, color: '888888', line: { color: 'cccccc', width: 1 }, margin: 0.1, valign: 'top' });

  // Extract AI-generated bullets from narrative
  const bullets = aiNarrative ? narrativeToBullets(aiNarrative, 10) : [];
  const keyFindings = bullets.slice(0, 5);
  const actionable = bullets.slice(5, 10);

  obsSlide.addText('AI GENERATED KEY FINDINGS', { x: 0.5, y: 2.5, w: 5.9, h: 0.35, fontSize: 12, bold: true, color: colors.primary });
  obsSlide.addText(
    (keyFindings.length ? keyFindings : ['[Add key findings from analysis]']).join('\n'), 
    { x: 0.5, y: 2.9, w: 5.9, h: 4.0, fontSize: 10, color: keyFindings.length ? colors.text : '888888', valign: 'top', bullet: keyFindings.length ? { indent: 14 } : undefined, lineSpacing: 13, margin: 0.08, italic: !keyFindings.length }
  );

  obsSlide.addText('AI GENERATED ACTIONABLE CONCLUSIONS', { x: 6.8, y: 2.5, w: 5.9, h: 0.35, fontSize: 12, bold: true, color: colors.primary });
  obsSlide.addText(
    (actionable.length ? actionable : ['[Add recommended actions]']).join('\n'), 
    { x: 6.8, y: 2.9, w: 5.9, h: 4.0, fontSize: 10, color: actionable.length ? colors.text : '888888', valign: 'top', bullet: actionable.length ? { indent: 14 } : undefined, lineSpacing: 13, margin: 0.08, italic: !actionable.length }
  );

  // Generate filename
  const entityStr = stats.selectedEntities?.slice(0, 3).join('-') || 'network';
  const filename = `link-analysis-${entityStr}-${new Date().toISOString().split('T')[0]}.pptx`;
  await pptx.writeFile({ fileName: filename });
};


// ==========================================
// PATTERN OF LIFE POWERPOINT EXPORT
// ==========================================

export interface POLEntityProfile {
  entity_id: string;
  mobility_score: number;
  social_score: number;
  routine_score: number;
  anomaly_score: number;
  unique_locations: number;
  total_dwells: number;
  total_dwell_duration_minutes: number;
  total_routes: number;
  total_distance_km: number;
  total_calls: number;
  total_sms: number;
  unique_contacts: number;
  top_locations: Array<{ location_name: string; visit_count: number; total_duration_minutes: number }>;
  active_hours: number[];
  hourly_heatmap?: Array<{ day_of_week: number; hour: number; count: number }>;
}

export interface POLPatternSummary {
  pattern_type: string;
  location_name: string;
  frequency: string;
  confidence_score: number;
  occurrence_count: number;
  days_of_week: string[];
  time_window_start: string;
  time_window_end: string;
  avg_duration_minutes: number;
}

export interface POLCorrelationSummary {
  other_entity_id: string;
  correlation_type: string;
  correlation_strength: number;
  occurrence_count: number;
  shared_locations: Array<{ name: string; count: number }>;
  avg_time_delta_minutes: number;
}

export interface POLAnomalySummary {
  anomaly_type: string;
  severity: string;
  expected_behavior: string;
  actual_behavior: string;
  deviation_score: number;
  detected_at: string;
}

/**
 * Daily atypicality rollup (Outlier Rankings tab). One row per (entity, day) computed by
 * pipeline Step 12 — combines mobility metrics with detector hit-counts into a
 * single 0..1 score so analysts can prioritise which days to investigate.
 */
export interface POLDailyAtypicalSummary {
  entity_id: string;
  date: string;
  atypicality_score: number;
  total_anomaly_count?: number;
  total_dwells?: number;
  unique_locations?: number;
  total_distance_km?: number;
  anomaly_breakdown?: Record<string, number>;
}

/**
 * Pretty-print map for raw anomaly_type strings (matches POL Tab UI).
 * Falls back to title-cased snake_case if a type is not registered.
 */
const POL_ANOMALY_TYPE_LABELS: Record<string, string> = {
  ml_digital_anomaly: 'Digital Behavior Anomaly',
  ml_physical_anomaly: 'Physical Movement Anomaly',
  time_shift: 'Off-Schedule Visit',
  dwell_duration_anomaly: 'Unusual Dwell Duration',
  lapse_break: 'Missed Recurring Visit',
  route_deviation: 'Route Deviation',
  new_relationship: 'New Relationship',
  unusual_location: 'Unusual Location',
  routine_break: 'Routine Break',
  high_activity: 'Unusually High Activity',
  low_activity: 'Unusually Low Activity',
  new_location: 'New Location Visited',
  unusual_time: 'Activity at Unusual Time',
  no_activity: 'No Activity Detected',
  new_pattern: 'New Pattern Emerging',
};

const formatPolAnomalyType = (raw: string): string => {
  if (!raw) return 'Unknown';
  const key = raw.toLowerCase();
  if (POL_ANOMALY_TYPE_LABELS[key]) return POL_ANOMALY_TYPE_LABELS[key];
  return key
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

/** Severity color hex codes (hex without leading '#' — pptxgenjs convention). */
const POL_SEVERITY_COLOR: Record<string, string> = {
  critical: '9c27b0',
  high: 'f85149',
  medium: 'f59e0b',
  low: '3fb950',
};

export interface POLRouteSummary {
  origin_name: string;
  destination_name: string;
  usage_count: number;
  avg_duration_seconds: number;
  behavioral_tags: string[];
}

export interface POLDigitalPatternSummary {
  category: string;
  name: string;
  description: string;
  occurrence_count: number;
  confidence: number;
  time_of_day: string | null;
}

export interface POLDigitalFingerprint {
  category: string;
  name: string;
  description: string;
  confidence: number;
}

export interface POLExportOptions {
  entityId: string;
  profile: POLEntityProfile;
  patterns: POLPatternSummary[];
  correlations: POLCorrelationSummary[];
  anomalies: POLAnomalySummary[];
  routes: POLRouteSummary[];
  digitalPatterns: POLDigitalPatternSummary[];
  digitalFingerprints: POLDigitalFingerprint[];
  mapScreenshot?: MapScreenshotResult;
  aiNarrative?: string;
  daysBack: number;
  /** Inclusive date range actually loaded (overrides “Last N days” on the title). */
  periodLabel?: string;
  courseName?: string;
  theme?: PowerPointTheme;
  /** Top atypical days for this entity (Outlier Rankings tab). Sorted by atypicality_score DESC. */
  topAtypicalDays?: POLDailyAtypicalSummary[];
  /** Counts keyed by anomaly_type for the chosen window (for the breakdown badge row). */
  anomalyTypeBreakdown?: Record<string, number>;
}

function formatMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatHour12(hour: number): string {
  if (hour === 0) return '12a';
  if (hour < 12) return `${hour}a`;
  if (hour === 12) return '12p';
  return `${hour - 12}p`;
}

export const exportPOLToPowerPoint = async (options: POLExportOptions): Promise<void> => {
  const {
    entityId, profile, patterns, correlations, anomalies, routes,
    digitalPatterns, digitalFingerprints, mapScreenshot, aiNarrative,
    daysBack, periodLabel, courseName, theme = 'standard',
    topAtypicalDays = [], anomalyTypeBreakdown = {},
  } = options;

  const PptxGenJS = await loadPptxGenJS();
  const pptx = new PptxGenJS();
  pptx.layout = 'LAYOUT_WIDE';
  pptx.author = 'Pattern of Life Analysis System';
  pptx.company = 'Quiet Professionals';
  pptx.subject = `Pattern of Life Report — ${entityId}`;
  pptx.title = `POL Report: ${entityId}`;

  const colors = THEME_DEFINITIONS[theme].colors;
  const titleBgDataUrl = generateGradientBackground(1920, 1080, 'title', theme);
  const contentBgDataUrl = generateGradientBackground(1920, 1080, 'content', theme);

  let logoDataUrl = '';
  try { logoDataUrl = await fetchAsDataUrl(TEMPLATE_ASSETS.logo); } catch { /* optional */ }

  const FOOTER = 'PATTERN OF LIFE • UNCLASSIFIED';

  const addContentSlide = (titleText: string) => {
    const slide = pptx.addSlide();
    if (contentBgDataUrl) addFullBleedBackground(slide, contentBgDataUrl);
    else slide.background = { color: colors.contentBg };
    addFooter(slide, FOOTER, colors.lightText);
    if (logoDataUrl) addCornerLogo(slide, logoDataUrl);
    slide.addText(titleText, { x: 0.5, y: 0.4, w: 12.0, h: 0.6, fontSize: 28, bold: true, color: colors.primary });
    return slide;
  };

  // ── SLIDE 1: TITLE ──
  const titleSlide = pptx.addSlide();
  if (titleBgDataUrl) addFullBleedBackground(titleSlide, titleBgDataUrl);
  else titleSlide.background = { color: colors.titleBg };
  if (logoDataUrl) addCornerLogo(titleSlide, logoDataUrl, { x: SLIDE_W - 1.5, y: SLIDE_H - 1.5, w: 1.1, h: 1.1 });

  titleSlide.addText('Pattern of Life Report', { x: 0.75, y: 1.8, w: SLIDE_W - 1.5, h: 0.8, fontSize: 38, bold: true, color: colors.titleText });
  titleSlide.addText(entityId, { x: 0.75, y: 2.7, w: SLIDE_W - 1.5, h: 0.7, fontSize: 30, color: colors.accent, bold: true });
  if (courseName) {
    titleSlide.addText(courseName, { x: 0.75, y: 3.5, w: SLIDE_W - 1.5, h: 0.4, fontSize: 18, color: colors.titleText });
  }
  titleSlide.addText(`Analysis Period: ${periodLabel || `Last ${daysBack} days`}`, { x: 0.75, y: 4.3, w: SLIDE_W - 1.5, h: 0.3, fontSize: 14, color: colors.titleText });
  titleSlide.addText(`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`, {
    x: 0.75, y: 5.5, w: SLIDE_W - 1.5, h: 0.3, fontSize: 12, color: colors.titleText,
  });

  // ── SLIDE 2: ENTITY PROFILE ──
  const profileSlide = addContentSlide(`Entity Profile: ${entityId}`);

  const n = (v: number | undefined | null, decimals?: number) => {
    if (v == null || isNaN(v)) return decimals != null ? '0' : '0';
    return decimals != null ? v.toFixed(decimals) : v.toLocaleString();
  };

  const profileStats = [
    ['Mobility Score', n(profile.mobility_score, 1)],
    ['Social Score', n(profile.social_score, 1)],
    ['Routine Score', n(profile.routine_score, 1)],
    ['Anomaly Score', n(profile.anomaly_score, 1)],
    ['Unique Locations', n(profile.unique_locations)],
    ['Total Dwells', n(profile.total_dwells)],
    ['Total Dwell Time (all devices)', formatMinutes(profile.total_dwell_duration_minutes || 0)],
    ['Total Routes', n(profile.total_routes)],
    ['Distance Traveled (all devices)', `${n(profile.total_distance_km || 0, 1)} mi`],
    ['Total Calls', n(profile.total_calls)],
    ['Total SMS', n(profile.total_sms)],
    ['Unique Contacts', n(profile.unique_contacts)],
  ].map(([label, value], idx) => [
    { text: label, options: { bold: true, color: colors.text, fill: { color: idx % 2 === 0 ? 'f3f4f6' : 'ffffff' } } },
    { text: value, options: { color: colors.secondary, align: 'right' as const, fill: { color: idx % 2 === 0 ? 'f3f4f6' : 'ffffff' } } },
  ]);

  profileSlide.addText('BEHAVIORAL SCORES & STATISTICS', { x: 0.5, y: 1.1, w: 5.5, h: 0.3, fontSize: 12, bold: true, color: colors.primary });
  profileSlide.addTable(profileStats, { x: 0.5, y: 1.45, w: 5.5, rowH: 0.38, fontSize: 11, border: { type: 'solid', pt: 1, color: 'e5e7eb' }, colW: [3.5, 2.0] });

  // Top locations
  if ((profile.top_locations || []).length > 0) {
    profileSlide.addText('TOP LOCATIONS', { x: 6.8, y: 1.1, w: 6.0, h: 0.3, fontSize: 12, bold: true, color: colors.primary });
    const locRows: any[] = [[
      { text: '#', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
      { text: 'Location', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
      { text: 'Visits', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
      { text: 'Duration', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'right' as const } },
    ]];
    (profile.top_locations || []).slice(0, 10).forEach((loc, idx) => {
      const rc = idx % 2 === 0 ? 'f9fafb' : 'ffffff';
      const locName = loc.location_name || 'Unknown';
      locRows.push([
        { text: (idx + 1).toString(), options: { fill: { color: rc }, align: 'center' as const } },
        { text: locName.length > 30 ? locName.slice(0, 30) + '...' : locName, options: { fill: { color: rc }, fontSize: 9 } },
        { text: n(loc.visit_count), options: { fill: { color: rc }, align: 'center' as const } },
        { text: formatMinutes(loc.total_duration_minutes || 0), options: { fill: { color: rc }, align: 'right' as const } },
      ]);
    });
    profileSlide.addTable(locRows, { x: 6.8, y: 1.45, w: 6.0, rowH: 0.35, fontSize: 10, border: { type: 'solid', pt: 1, color: 'e5e7eb' }, colW: [0.5, 3.2, 0.8, 1.5] });
  }

  // Activity heatmap at bottom (active hours)
  if ((profile.active_hours || []).length > 0) {
    profileSlide.addText('ACTIVE HOURS', { x: 0.5, y: 6.2, w: 4.0, h: 0.25, fontSize: 10, bold: true, color: colors.primary });
    // Ensure exactly 24 hours
    const hours24 = Array.from({ length: 24 }, (_, i) => (profile.active_hours || [])[i] || 0);
    const maxActivity = Math.max(...hours24, 1);
    const accentR = parseInt(colors.accent.slice(0, 2), 16);
    const accentG = parseInt(colors.accent.slice(2, 4), 16);
    const accentB = parseInt(colors.accent.slice(4, 6), 16);

    const hourCells = hours24.map((count, h) => {
      const intensity = count / maxActivity;
      let cellColor = 'f3f4f6';
      if (count > 0) {
        const r = Math.round(240 + (accentR - 240) * intensity);
        const g = Math.round(240 + (accentG - 240) * intensity);
        const b = Math.round(240 + (accentB - 240) * intensity);
        cellColor = r.toString(16).padStart(2, '0') + g.toString(16).padStart(2, '0') + b.toString(16).padStart(2, '0');
      }
      const lum = count > 0 ? (0.299 * (240 + (accentR - 240) * intensity) + 0.587 * (240 + (accentG - 240) * intensity) + 0.114 * (240 + (accentB - 240) * intensity)) / 255 : 1;
      return { text: formatHour12(h), options: { fontSize: 6, color: lum < 0.5 ? 'ffffff' : colors.text, fill: { color: cellColor }, align: 'center' as const, valign: 'middle' as const } };
    });
    profileSlide.addTable([[...hourCells]], { x: 0.5, y: 6.5, w: 12.3, rowH: 0.3, fontSize: 6, border: { type: 'solid', pt: 0.5, color: 'ffffff' }, colW: Array(24).fill(12.3 / 24) });
  }

  // ── SLIDE 3: MAP + AI SUMMARY ──
  if (mapScreenshot) {
    const mapSlide = addContentSlide(`Location Activity: ${entityId}`);

    // Map on the left
    const mapMaxW = 7.0, mapMaxH = 5.5;
    const ar = mapScreenshot.aspectRatio;
    let imgW = mapMaxW, imgH = mapMaxW / ar;
    if (imgH > mapMaxH) { imgH = mapMaxH; imgW = mapMaxH * ar; }
    const imgX = 0.5, imgY = 1.1 + (mapMaxH - imgH) / 2;
    mapSlide.addImage({ data: mapScreenshot.dataUrl, x: imgX, y: imgY, w: imgW, h: imgH });

    // AI summary on the right
    const rightX = 7.8, rightW = 5.0;
    // Location stats
    const locStats = [
      `Unique Locations: ${profile.unique_locations || 0}`,
      `Total Dwells: ${n(profile.total_dwells)}`,
      `Total Dwell Time: ${formatMinutes(profile.total_dwell_duration_minutes || 0)}`,
      `Distance Traveled: ${n(profile.total_distance_km || 0, 1)} mi`,
      `Routes: ${profile.total_routes || 0}`,
    ].join('\n');
    mapSlide.addText('LOCATION SUMMARY', { x: rightX, y: 1.1, w: rightW, h: 0.3, fontSize: 11, bold: true, color: colors.primary });
    mapSlide.addText(locStats, { x: rightX, y: 1.4, w: rightW, h: 1.35, fontSize: 10, color: colors.text, valign: 'top', lineSpacing: 16, margin: 0.08 });
    mapSlide.addText('Totals are the sum of all devices for this student (phone, watch, and vehicle).', {
      x: rightX, y: 2.75, w: rightW, h: 0.45, fontSize: 8, italic: true, color: colors.secondary,
    });

    // Top locations mini-list
    if ((profile.top_locations || []).length > 0) {
      mapSlide.addText('TOP LOCATIONS', { x: rightX, y: 3.25, w: rightW, h: 0.25, fontSize: 11, bold: true, color: colors.primary });
      const topLocs = (profile.top_locations || []).slice(0, 5).map((l, i) =>
        `${i + 1}. ${l.location_name || 'Unknown'}`
      ).join('\n');
      mapSlide.addText(topLocs, { x: rightX, y: 3.5, w: rightW, h: 1.7, fontSize: 10, color: colors.text, valign: 'top', lineSpacing: 16, margin: 0.08 });
    }

    // AI insight snippet if available
    if (aiNarrative) {
      const snippet = sanitizeAiNarrative(aiNarrative).split(/\n{2,}/)[0]?.slice(0, 400) || '';
      if (snippet) {
        mapSlide.addText('AI INSIGHT', { x: rightX, y: 5.5, w: rightW, h: 0.25, fontSize: 10, bold: true, color: colors.primary });
        mapSlide.addText(snippet + (snippet.length >= 400 ? '...' : ''), {
          x: rightX, y: 5.8, w: rightW, h: 1.2, fontSize: 8, color: colors.text,
          valign: 'top', lineSpacing: 11, margin: 0.08,
          fill: { color: 'f8f9fa' },
        });
      }
    }
  }

  // ── SLIDE 4: PATTERNS & ROUTES ──
  if (patterns.length > 0 || routes.length > 0) {
    const prSlide = addContentSlide('Patterns & Routes');

    if (patterns.length > 0) {
      prSlide.addText(`RECURRING PATTERNS (${patterns.length})`, { x: 0.5, y: 1.1, w: 6.0, h: 0.3, fontSize: 12, bold: true, color: colors.primary });
      const patRows: any[] = [[
        { text: 'Type', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
        { text: 'Location', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
        { text: 'Frequency', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
        { text: 'Conf.', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
      ]];
      patterns.slice(0, 12).forEach((p, idx) => {
        const rc = idx % 2 === 0 ? 'f9fafb' : 'ffffff';
        const locName = p.location_name || 'Unknown';
        patRows.push([
          { text: p.pattern_type || 'N/A', options: { fill: { color: rc }, fontSize: 9 } },
          { text: locName.length > 25 ? locName.slice(0, 25) + '...' : locName, options: { fill: { color: rc }, fontSize: 9 } },
          { text: p.frequency || 'N/A', options: { fill: { color: rc }, fontSize: 9 } },
          { text: `${Math.round((p.confidence_score || 0) * 100)}%`, options: { fill: { color: rc }, align: 'center' as const, fontSize: 9 } },
        ]);
      });
      prSlide.addTable(patRows, { x: 0.5, y: 1.45, w: 6.0, rowH: 0.35, fontSize: 10, border: { type: 'solid', pt: 1, color: 'e5e7eb' }, colW: [1.2, 2.6, 1.2, 1.0] });
    }

    if (routes.length > 0) {
      prSlide.addText(`ROUTE PATTERNS (${routes.length})`, { x: 6.8, y: 1.1, w: 6.0, h: 0.3, fontSize: 12, bold: true, color: colors.primary });
      const rtRows: any[] = [[
        { text: 'Origin → Destination', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
        { text: 'Usage', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
        { text: 'Avg Time', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'right' as const } },
      ]];
      routes.slice(0, 12).forEach((r, idx) => {
        const rc = idx % 2 === 0 ? 'f9fafb' : 'ffffff';
        const routeLabel = `${(r.origin_name || 'Unknown').slice(0, 18)} → ${(r.destination_name || 'Unknown').slice(0, 18)}`;
        rtRows.push([
          { text: routeLabel, options: { fill: { color: rc }, fontSize: 9 } },
          { text: `${r.usage_count || 0}x`, options: { fill: { color: rc }, align: 'center' as const, fontSize: 9 } },
          { text: formatMinutes((r.avg_duration_seconds || 0) / 60), options: { fill: { color: rc }, align: 'right' as const, fontSize: 9 } },
        ]);
      });
      prSlide.addTable(rtRows, { x: 6.8, y: 1.45, w: 6.0, rowH: 0.35, fontSize: 10, border: { type: 'solid', pt: 1, color: 'e5e7eb' }, colW: [3.5, 1.0, 1.5] });
    }
  }

  // ── SLIDE 5: CORRELATIONS ──
  if (correlations.length > 0) {
    const corrSlide = addContentSlide('Entity Correlations');
    corrSlide.addText(`Entities correlated with ${entityId}`, { x: 0.5, y: 0.95, w: 12.0, h: 0.3, fontSize: 12, color: colors.lightText });

    const corrRows: any[] = [[
      { text: 'Entity', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
      { text: 'Type', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
      { text: 'Strength', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
      { text: 'Occurrences', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
      { text: 'Shared Locations', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
    ]];
    correlations.slice(0, 15).forEach((c, idx) => {
      const rc = idx % 2 === 0 ? 'f9fafb' : 'ffffff';
      const sharedStr = (c.shared_locations || []).slice(0, 3).map(l => l.name || '').join(', ');
      corrRows.push([
        { text: c.other_entity_id || 'Unknown', options: { fill: { color: rc }, bold: true, color: colors.accent, fontSize: 10 } },
        { text: c.correlation_type || 'N/A', options: { fill: { color: rc }, fontSize: 10 } },
        { text: `${Math.round((c.correlation_strength || 0) * 100)}%`, options: { fill: { color: rc }, align: 'center' as const, fontSize: 10 } },
        { text: n(c.occurrence_count), options: { fill: { color: rc }, align: 'center' as const, fontSize: 10 } },
        { text: sharedStr.length > 40 ? sharedStr.slice(0, 40) + '...' : sharedStr, options: { fill: { color: rc }, fontSize: 9 } },
      ]);
    });
    corrSlide.addTable(corrRows, { x: 0.5, y: 1.4, w: 12.3, rowH: 0.38, fontSize: 10, border: { type: 'solid', pt: 1, color: 'e5e7eb' }, colW: [2.0, 1.8, 1.2, 1.3, 6.0] });
  }

  // ── SLIDE 6: ANOMALIES ──
  if (anomalies.length > 0) {
    const anomSlide = addContentSlide('Anomalies & Deviations');
    anomSlide.addText(
      `${anomalies.length} anomalies detected for ${entityId}`,
      { x: 0.5, y: 0.95, w: 12.0, h: 0.3, fontSize: 12, color: colors.lightText }
    );

    // Anomaly type breakdown badge row — quick-glance distribution across detector
    // categories so analysts can see which behavioral signal drove the count.
    const breakdownEntries = Object.entries(anomalyTypeBreakdown)
      .filter(([, v]) => (v || 0) > 0)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);
    let tableY = 1.4;
    if (breakdownEntries.length > 0) {
      anomSlide.addText('TYPE BREAKDOWN', {
        x: 0.5, y: 1.3, w: 3.0, h: 0.25, fontSize: 9, bold: true, color: colors.lightText,
      });
      const badgeW = 1.95;
      const badgeH = 0.45;
      const startX = 0.5;
      const startY = 1.55;
      breakdownEntries.forEach(([type, count], i) => {
        const col = i % 6;
        anomSlide.addText(
          [
            { text: `${count}`, options: { bold: true, fontSize: 14, color: colors.accent } },
            { text: `\n${formatPolAnomalyType(type)}`, options: { fontSize: 8, color: colors.text } },
          ],
          {
            x: startX + col * (badgeW + 0.1),
            y: startY,
            w: badgeW,
            h: badgeH,
            align: 'center',
            valign: 'middle',
            fill: { color: 'f3f4f6' },
            line: { color: 'e5e7eb', width: 0.75 },
          }
        );
      });
      tableY = 2.15;
    }

    const anomRows: any[] = [[
      { text: 'Severity', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
      { text: 'Type', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
      { text: 'Expected', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
      { text: 'Actual', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
      { text: 'Detected', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
    ]];
    // Trim row count if breakdown row consumed vertical space
    const rowLimit = breakdownEntries.length > 0 ? 11 : 14;
    anomalies.slice(0, rowLimit).forEach((a, idx) => {
      const rc = idx % 2 === 0 ? 'f9fafb' : 'ffffff';
      const sColor = POL_SEVERITY_COLOR[(a.severity || '').toLowerCase()] || colors.secondary;
      let dateStr = '';
      try { dateStr = a.detected_at ? new Date(a.detected_at).toLocaleDateString() : ''; } catch { /* invalid date */ }
      anomRows.push([
        { text: (a.severity || 'N/A').toUpperCase(), options: { fill: { color: rc }, color: sColor, bold: true, align: 'center' as const, fontSize: 9 } },
        { text: formatPolAnomalyType(a.anomaly_type || ''), options: { fill: { color: rc }, fontSize: 9, bold: true } },
        { text: (a.expected_behavior || '').slice(0, 60), options: { fill: { color: rc }, fontSize: 8 } },
        { text: (a.actual_behavior || '').slice(0, 60), options: { fill: { color: rc }, fontSize: 8 } },
        { text: dateStr, options: { fill: { color: rc }, fontSize: 9 } },
      ]);
    });
    anomSlide.addTable(anomRows, { x: 0.5, y: tableY, w: 12.3, rowH: 0.38, fontSize: 10, border: { type: 'solid', pt: 1, color: 'e5e7eb' }, colW: [1.2, 2.2, 3.4, 3.4, 2.1] });
  }

  // ── SLIDE 6b: DAILY ATYPICALITY (TRIAGE) ──
  // Surfaces the highest-scoring (entity, day) rollups computed by pipeline Step 12.
  // The atypicality_score blends mobility metrics with detector hit-counts so analysts
  // can prioritise which days deserve the deepest review.
  if (topAtypicalDays.length > 0) {
    const triageSlide = addContentSlide('Daily Atypicality · Outlier Rankings');
    triageSlide.addText(
      'Days ranked by composite atypicality score (mobility + detector signals)',
      { x: 0.5, y: 0.95, w: 12.0, h: 0.3, fontSize: 12, color: colors.lightText }
    );

    // Severity-band counts: High >=0.7, Elevated 0.5-0.7, Moderate 0.3-0.5, Low <0.3
    const high = topAtypicalDays.filter((d) => (d.atypicality_score || 0) >= 0.7).length;
    const elev = topAtypicalDays.filter((d) => (d.atypicality_score || 0) >= 0.5 && (d.atypicality_score || 0) < 0.7).length;
    const mod = topAtypicalDays.filter((d) => (d.atypicality_score || 0) >= 0.3 && (d.atypicality_score || 0) < 0.5).length;
    const low = topAtypicalDays.filter((d) => (d.atypicality_score || 0) < 0.3).length;
    const bands: Array<[string, number, string]> = [
      ['High >=0.7', high, POL_SEVERITY_COLOR.critical],
      ['Elevated 0.5-0.7', elev, POL_SEVERITY_COLOR.high],
      ['Moderate 0.3-0.5', mod, POL_SEVERITY_COLOR.medium],
      ['Low <0.3', low, POL_SEVERITY_COLOR.low],
    ];
    bands.forEach(([label, count, c], i) => {
      triageSlide.addText(
        [
          { text: `${count}`, options: { bold: true, fontSize: 22, color: c } },
          { text: `\n${label}`, options: { fontSize: 9, color: colors.text } },
        ],
        {
          x: 0.5 + i * 3.05,
          y: 1.3,
          w: 2.95,
          h: 0.85,
          align: 'center',
          valign: 'middle',
          fill: { color: 'f9fafb' },
          line: { color: 'e5e7eb', width: 0.75 },
        }
      );
    });

    // Detail table — the actual ranked days
    const triageRows: any[] = [[
      { text: 'Score', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
      { text: 'Date', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
      { text: 'Anomalies', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
      { text: 'Dwells', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
      { text: 'Locations', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
      { text: 'Distance (km)', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
      { text: 'Detectors Fired', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
    ]];

    const scoreColor = (s: number): string => {
      if (s >= 0.7) return POL_SEVERITY_COLOR.critical;
      if (s >= 0.5) return POL_SEVERITY_COLOR.high;
      if (s >= 0.3) return POL_SEVERITY_COLOR.medium;
      return POL_SEVERITY_COLOR.low;
    };

    topAtypicalDays.slice(0, 12).forEach((d, idx) => {
      const rc = idx % 2 === 0 ? 'f9fafb' : 'ffffff';
      const score = d.atypicality_score || 0;
      const sColor = scoreColor(score);
      let dateStr = '';
      try { dateStr = d.date ? new Date(d.date).toLocaleDateString() : ''; } catch { /* invalid */ }

      // Build a compact detector-fired summary, e.g., "time_shift x2, route_deviation x1"
      let detectorSummary = '';
      if (d.anomaly_breakdown) {
        const fired = Object.entries(d.anomaly_breakdown)
          .filter(([, v]) => (v || 0) > 0)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([k, v]) => `${formatPolAnomalyType(k)} x${v}`);
        detectorSummary = fired.length > 0 ? fired.join(', ') : 'Mobility-driven (no detectors)';
      }

      triageRows.push([
        { text: `${(score * 100).toFixed(0)}%`, options: { fill: { color: rc }, color: sColor, bold: true, align: 'center' as const, fontSize: 11 } },
        { text: dateStr, options: { fill: { color: rc }, fontSize: 10, bold: true } },
        { text: n(d.total_anomaly_count ?? 0), options: { fill: { color: rc }, align: 'center' as const, fontSize: 10 } },
        { text: n(d.total_dwells ?? 0), options: { fill: { color: rc }, align: 'center' as const, fontSize: 10 } },
        { text: n(d.unique_locations ?? 0), options: { fill: { color: rc }, align: 'center' as const, fontSize: 10 } },
        { text: (d.total_distance_km ?? 0).toFixed(1), options: { fill: { color: rc }, align: 'center' as const, fontSize: 10 } },
        { text: detectorSummary || '-', options: { fill: { color: rc }, fontSize: 8, italic: !d.anomaly_breakdown } },
      ]);
    });

    triageSlide.addTable(triageRows, {
      x: 0.5, y: 2.3, w: 12.3, rowH: 0.36, fontSize: 10,
      border: { type: 'solid', pt: 1, color: 'e5e7eb' },
      colW: [1.0, 1.4, 1.2, 1.0, 1.2, 1.4, 5.1],
    });
  }

  // ── SLIDE 7: DIGITAL PATTERNS ──
  if (digitalPatterns.length > 0) {
    const digSlide = addContentSlide('Digital Activity Profile');
    digSlide.addText(`DIGITAL PATTERNS (${digitalPatterns.length})`, { x: 0.5, y: 1.1, w: 6.0, h: 0.3, fontSize: 12, bold: true, color: colors.primary });
    const dpRows: any[] = [[
      { text: 'Category', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
      { text: 'Name', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
      { text: 'Detail', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
      { text: 'Count', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
      { text: 'Conf.', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
    ]];
    digitalPatterns.slice(0, 16).forEach((dp, idx) => {
      const rc = idx % 2 === 0 ? 'f9fafb' : 'ffffff';
      dpRows.push([
        { text: dp.category || 'N/A', options: { fill: { color: rc }, fontSize: 9 } },
        { text: dp.name || 'Unknown', options: { fill: { color: rc }, fontSize: 9, bold: true } },
        { text: (dp.description || '').slice(0, 30), options: { fill: { color: rc }, fontSize: 8 } },
        { text: n(dp.occurrence_count), options: { fill: { color: rc }, align: 'center' as const, fontSize: 9 } },
        { text: `${Math.round((dp.confidence || 0) * 100)}%`, options: { fill: { color: rc }, align: 'center' as const, fontSize: 9 } },
      ]);
    });
    digSlide.addTable(dpRows, { x: 0.5, y: 1.45, w: 12.3, rowH: 0.33, fontSize: 10, border: { type: 'solid', pt: 1, color: 'e5e7eb' }, colW: [1.8, 2.0, 4.5, 1.0, 1.0] });
  }

  // ── SLIDE 7b: DIGITAL FINGERPRINTS (always own slide) ──
  if (digitalFingerprints.length > 0) {
    const fpSlide = addContentSlide('Digital Fingerprint');
    fpSlide.addText(`Top behavioral signals that characterize ${entityId}`, { x: 0.5, y: 0.95, w: 12.0, h: 0.3, fontSize: 12, color: colors.lightText });

    const fpRows: any[] = [[
      { text: 'Category', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
      { text: 'Signal', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
      { text: 'Description', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
      { text: 'Confidence', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
    ]];
    digitalFingerprints.slice(0, 15).forEach((fp, idx) => {
      const rc = idx % 2 === 0 ? 'f9fafb' : 'ffffff';
      fpRows.push([
        { text: fp.category || 'N/A', options: { fill: { color: rc }, fontSize: 10 } },
        { text: fp.name || 'Unknown', options: { fill: { color: rc }, fontSize: 10, bold: true } },
        { text: fp.description || '', options: { fill: { color: rc }, fontSize: 9 } },
        { text: `${Math.round((fp.confidence || 0) * 100)}%`, options: { fill: { color: rc }, align: 'center' as const, fontSize: 10, bold: true, color: (fp.confidence || 0) >= 0.9 ? '3fb950' : (fp.confidence || 0) >= 0.7 ? '58a6ff' : colors.secondary } },
      ]);
    });
    fpSlide.addTable(fpRows, { x: 0.5, y: 1.4, w: 12.3, rowH: 0.38, fontSize: 10, border: { type: 'solid', pt: 1, color: 'e5e7eb' }, colW: [1.8, 2.5, 6.0, 2.0] });
  }

  // ── SLIDE 8: AI ANALYSIS (optional) ──
  if (aiNarrative) {
    const cleanNarrative = sanitizeAiNarrative(aiNarrative);

    // First slide: analyst comments + start of AI analysis
    const aiSlide1 = addContentSlide('AI-Generated Analysis');
    aiSlide1.addText('ANALYST COMMENTS', { x: 0.5, y: 1.1, w: 12.3, h: 0.3, fontSize: 11, bold: true, color: colors.primary });
    aiSlide1.addText('[Enter analyst observations and assessments here]', { x: 0.5, y: 1.45, w: 12.3, h: 0.7, fontSize: 11, italic: true, color: '888888', line: { color: 'cccccc', width: 1 }, margin: 0.1, valign: 'top' });

    // Split narrative into paragraphs and use bullet formatting
    const paragraphs = cleanNarrative.split(/\n+/).map(p => p.trim()).filter(Boolean);
    const firstSlideChars = 1800;
    let charCount = 0;
    let firstSlideParas: string[] = [];
    let remainingParas: string[] = [];
    for (const p of paragraphs) {
      if (charCount + p.length <= firstSlideChars) {
        firstSlideParas.push(p);
        charCount += p.length;
      } else {
        remainingParas.push(p);
      }
    }

    aiSlide1.addText('AI-GENERATED ANALYSIS', { x: 0.5, y: 2.3, w: 12.3, h: 0.3, fontSize: 11, bold: true, color: colors.primary });
    if (firstSlideParas.length > 0) {
      aiSlide1.addText(firstSlideParas.join('\n\n'), {
        x: 0.5, y: 2.7, w: 12.3, h: 4.2, fontSize: 10, color: colors.text,
        valign: 'top', lineSpacing: 14, margin: 0.1, paraSpaceAfter: 8,
      });
    }

    // Continuation slides for remaining content
    if (remainingParas.length > 0) {
      const contCharsPerSlide = 3000;
      let contSlideParas: string[][] = [];
      let current: string[] = [];
      let cc = 0;
      for (const p of remainingParas) {
        if (cc + p.length > contCharsPerSlide && current.length > 0) {
          contSlideParas.push(current);
          current = [];
          cc = 0;
        }
        current.push(p);
        cc += p.length;
      }
      if (current.length > 0) contSlideParas.push(current);

      for (const slideParas of contSlideParas) {
        const contSlide = addContentSlide('AI-Generated Analysis (continued)');
        contSlide.addText(slideParas.join('\n\n'), {
          x: 0.5, y: 1.1, w: 12.3, h: 6.0, fontSize: 10, color: colors.text,
          valign: 'top', lineSpacing: 14, margin: 0.1, paraSpaceAfter: 8,
        });
      }
    }
  }

  // ── SLIDE 9: OBSERVATIONS & RECOMMENDATIONS ──
  const obsSlide = addContentSlide('Observations and Recommendations');
  obsSlide.addText('ANALYST COMMENTS', { x: 0.5, y: 1.2, w: 12.3, h: 0.3, fontSize: 11, bold: true, color: colors.primary });
  obsSlide.addText('[Enter analyst observations and assessments here]', { x: 0.5, y: 1.55, w: 12.3, h: 0.8, fontSize: 11, italic: true, color: '888888', line: { color: 'cccccc', width: 1 }, margin: 0.1, valign: 'top' });

  const bullets = aiNarrative ? narrativeToBullets(aiNarrative, 10) : [];
  const keyFindings = bullets.slice(0, 5);
  const actionable = bullets.slice(5, 10);

  obsSlide.addText('AI GENERATED KEY FINDINGS', { x: 0.5, y: 2.6, w: 5.9, h: 0.35, fontSize: 12, bold: true, color: colors.primary });
  obsSlide.addText((keyFindings.length ? keyFindings : ['[Add key findings from analysis]']).join('\n'), {
    x: 0.5, y: 3.0, w: 5.9, h: 4.0, fontSize: 10, color: keyFindings.length ? colors.text : '888888',
    valign: 'top', bullet: keyFindings.length ? { indent: 14 } : undefined, lineSpacing: 13, margin: 0.08, italic: !keyFindings.length,
  });
  obsSlide.addText('AI GENERATED ACTIONABLE CONCLUSIONS', { x: 6.8, y: 2.6, w: 5.9, h: 0.35, fontSize: 12, bold: true, color: colors.primary });
  obsSlide.addText((actionable.length ? actionable : ['[Add recommended actions]']).join('\n'), {
    x: 6.8, y: 3.0, w: 5.9, h: 4.0, fontSize: 10, color: actionable.length ? colors.text : '888888',
    valign: 'top', bullet: actionable.length ? { indent: 14 } : undefined, lineSpacing: 13, margin: 0.08, italic: !actionable.length,
  });

  const filename = `pol-report-${entityId}-${new Date().toISOString().split('T')[0]}.pptx`;
  await pptx.writeFile({ fileName: filename });
};

// =====================================================================
// Outlier Rankings Brief Export
// ---------------------------------------------------------------------
// Focused, narrative-driven companion to exportPOLToPowerPoint that
// covers ONLY the selected individual's atypical-day picture. Used by
// the "Export Brief" button on the Outlier Rankings tab (previously
// labeled "Triage" / "Daily Review") — built for a quick read, not a
// comprehensive briefing.
//
// Renamed-friendly: the public label is driven by `briefLabel` on the
// options object, so the section name can shift again without changing
// this code or the public function name (kept as `exportTriageBrief...`
// for backward compatibility with existing imports).
// =====================================================================

/** Per-day deep dive payload — one of these per slide after the summary. */
export interface POLTriageBriefDayDetail {
  /** ISO date or YYYY-MM-DD; both render via toLocaleDateString. */
  date: string;
  atypicality_score: number;
  /** Headline atypicality stats for this day (same shape as POLDailyAtypicalSummary). */
  total_anomaly_count?: number;
  total_dwells?: number;
  unique_locations?: number;
  total_distance_km?: number;
  total_dwell_minutes?: number;
  max_distance_from_home_km?: number;
  track_point_count?: number;
  anomaly_breakdown?: Record<string, number>;
  /**
   * Actual events that fired on this day. Anomalies are already filtered to
   * the entity + day pair, sorted ascending by detected_at on the caller.
   */
  anomalies: POLAnomalySummary[];
}

export interface POLTriageBriefOptions {
  entityId: string;
  /** Human label for the section ("Outlier Rankings", "Daily Review", etc.). */
  briefLabel?: string;
  daysBack: number;
  courseName?: string;
  theme?: PowerPointTheme;
  /** Ranked top-atypical days (already filtered to this entity). */
  topAtypicalDays: POLDailyAtypicalSummary[];
  /** Per-day deep-dive panels; usually the top 3-5 days from topAtypicalDays. */
  dayDetails: POLTriageBriefDayDetail[];
  /** Plain-prose AI analysis explaining "what this all means". */
  aiNarrative?: string;
}

const briefScoreColor = (s: number): string => {
  if (s >= 0.7) return POL_SEVERITY_COLOR.critical;
  if (s >= 0.5) return POL_SEVERITY_COLOR.high;
  if (s >= 0.3) return POL_SEVERITY_COLOR.medium;
  return POL_SEVERITY_COLOR.low;
};

const briefScoreBand = (s: number): string => {
  if (s >= 0.7) return 'High';
  if (s >= 0.5) return 'Elevated';
  if (s >= 0.3) return 'Moderate';
  return 'Low';
};

const briefFormatDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return iso; }
};

const briefFormatTime = (iso: string): string => {
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

export const exportTriageBriefToPowerPoint = async (
  options: POLTriageBriefOptions
): Promise<void> => {
  const {
    entityId,
    briefLabel = 'Outlier Rankings',
    daysBack,
    courseName,
    theme = 'standard',
    topAtypicalDays,
    dayDetails,
    aiNarrative,
  } = options;

  const PptxGenJS = await loadPptxGenJS();
  const pptx = new PptxGenJS();
  pptx.layout = 'LAYOUT_WIDE';
  pptx.author = 'Pattern of Life Analysis System';
  pptx.company = 'Quiet Professionals';
  pptx.subject = `${briefLabel} Brief — ${entityId}`;
  pptx.title = `${briefLabel} Brief: ${entityId}`;

  const colors = THEME_DEFINITIONS[theme].colors;
  const titleBgDataUrl = generateGradientBackground(1920, 1080, 'title', theme);
  const contentBgDataUrl = generateGradientBackground(1920, 1080, 'content', theme);

  let logoDataUrl = '';
  try { logoDataUrl = await fetchAsDataUrl(TEMPLATE_ASSETS.logo); } catch { /* optional */ }

  const FOOTER = `${briefLabel.toUpperCase()} BRIEF • UNCLASSIFIED`;

  const addContentSlide = (titleText: string, subtitle?: string) => {
    const slide = pptx.addSlide();
    if (contentBgDataUrl) addFullBleedBackground(slide, contentBgDataUrl);
    else slide.background = { color: colors.contentBg };
    addFooter(slide, FOOTER, colors.lightText);
    if (logoDataUrl) addCornerLogo(slide, logoDataUrl);
    slide.addText(titleText, {
      x: 0.5, y: 0.4, w: 12.0, h: 0.55, fontSize: 28, bold: true, color: colors.primary,
    });
    if (subtitle) {
      slide.addText(subtitle, {
        x: 0.5, y: 0.95, w: 12.0, h: 0.3, fontSize: 12, color: colors.lightText,
      });
    }
    return slide;
  };

  // Pre-compute band tallies once — shared across the cover and exec summary slides.
  const bandHigh = topAtypicalDays.filter((d) => (d.atypicality_score || 0) >= 0.7).length;
  const bandElev = topAtypicalDays.filter((d) => (d.atypicality_score || 0) >= 0.5 && (d.atypicality_score || 0) < 0.7).length;
  const bandMod = topAtypicalDays.filter((d) => (d.atypicality_score || 0) >= 0.3 && (d.atypicality_score || 0) < 0.5).length;
  const bandLow = topAtypicalDays.filter((d) => (d.atypicality_score || 0) < 0.3).length;
  const peakDay = topAtypicalDays.slice().sort((a, b) => (b.atypicality_score || 0) - (a.atypicality_score || 0))[0];
  const peakScore = peakDay?.atypicality_score || 0;
  const totalEvents = topAtypicalDays.reduce((acc, d) => acc + (d.total_anomaly_count || 0), 0);

  // ────────────────────────────────────────────────────────────────────
  // SLIDE 1: COVER
  // ────────────────────────────────────────────────────────────────────
  const titleSlide = pptx.addSlide();
  if (titleBgDataUrl) addFullBleedBackground(titleSlide, titleBgDataUrl);
  else titleSlide.background = { color: colors.titleBg };
  if (logoDataUrl) {
    addCornerLogo(titleSlide, logoDataUrl, { x: SLIDE_W - 1.5, y: SLIDE_H - 1.5, w: 1.1, h: 1.1 });
  }

  titleSlide.addText(`${briefLabel} Brief`, {
    x: 0.75, y: 1.6, w: SLIDE_W - 1.5, h: 0.8, fontSize: 40, bold: true, color: colors.titleText,
  });
  titleSlide.addText(entityId, {
    x: 0.75, y: 2.55, w: SLIDE_W - 1.5, h: 0.7, fontSize: 32, color: colors.accent, bold: true,
  });
  if (courseName) {
    titleSlide.addText(courseName, {
      x: 0.75, y: 3.35, w: SLIDE_W - 1.5, h: 0.4, fontSize: 18, color: colors.titleText,
    });
  }
  titleSlide.addText(`Analysis Period: Last ${daysBack} days`, {
    x: 0.75, y: 4.0, w: SLIDE_W - 1.5, h: 0.35, fontSize: 14, color: colors.titleText,
  });

  // Headline score banner: makes the peak day visible from the cover.
  if (peakDay) {
    titleSlide.addText(
      [
        { text: 'PEAK ATYPICALITY  ', options: { fontSize: 11, color: colors.titleText, bold: true } },
        { text: `${(peakScore * 100).toFixed(0)}%`, options: { fontSize: 36, color: briefScoreColor(peakScore), bold: true } },
        { text: `  ${briefScoreBand(peakScore).toUpperCase()}`, options: { fontSize: 13, color: briefScoreColor(peakScore), bold: true } },
        { text: `\n${briefFormatDate(peakDay.date)}`, options: { fontSize: 14, color: colors.titleText } },
      ],
      {
        x: 0.75, y: 4.6, w: 6.5, h: 1.4, valign: 'middle',
        fill: { color: 'ffffff', transparency: 88 },
        line: { color: briefScoreColor(peakScore), width: 1.5 },
      }
    );
  }

  titleSlide.addText(
    `Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`,
    { x: 0.75, y: SLIDE_H - 0.7, w: SLIDE_W - 1.5, h: 0.3, fontSize: 11, color: colors.titleText }
  );

  // ────────────────────────────────────────────────────────────────────
  // SLIDE 2: EXECUTIVE SUMMARY — "WHAT THIS MEANS"
  // ────────────────────────────────────────────────────────────────────
  const summarySlide = addContentSlide(
    `${briefLabel}: What This Means`,
    `AI-generated interpretation of ${entityId}'s atypical-day pattern over the last ${daysBack} days`
  );

  // Severity-band KPI strip at the top.
  const bands: Array<[string, number, string]> = [
    ['High >= 0.70', bandHigh, POL_SEVERITY_COLOR.critical],
    ['Elevated 0.50-0.69', bandElev, POL_SEVERITY_COLOR.high],
    ['Moderate 0.30-0.49', bandMod, POL_SEVERITY_COLOR.medium],
    ['Low < 0.30', bandLow, POL_SEVERITY_COLOR.low],
  ];
  bands.forEach(([label, count, c], i) => {
    summarySlide.addText(
      [
        { text: `${count}`, options: { bold: true, fontSize: 24, color: c } },
        { text: `\n${label}`, options: { fontSize: 9, color: colors.text } },
      ],
      {
        x: 0.5 + i * 3.05, y: 1.35, w: 2.95, h: 0.95,
        align: 'center', valign: 'middle',
        fill: { color: 'f9fafb' }, line: { color: 'e5e7eb', width: 0.75 },
      }
    );
  });

  // Headline numbers strip beneath the band KPIs.
  const headlineCells: Array<[string, string]> = [
    ['Days Reviewed', `${topAtypicalDays.length}`],
    ['Peak Score', `${(peakScore * 100).toFixed(0)}%`],
    ['Peak Day', peakDay ? briefFormatDate(peakDay.date) : '—'],
    ['Total Events', `${totalEvents}`],
  ];
  headlineCells.forEach(([label, value], i) => {
    summarySlide.addText(
      [
        { text: value, options: { bold: true, fontSize: 20, color: colors.primary } },
        { text: `\n${label.toUpperCase()}`, options: { fontSize: 9, color: colors.lightText, bold: true } },
      ],
      {
        x: 0.5 + i * 3.05, y: 2.5, w: 2.95, h: 0.85,
        align: 'center', valign: 'middle',
        fill: { color: 'ffffff' }, line: { color: 'e5e7eb', width: 0.75 },
      }
    );
  });

  // AI narrative occupies the full lower half — this is the "what it means" payoff.
  summarySlide.addText('AI ANALYSIS', {
    x: 0.5, y: 3.55, w: 12.3, h: 0.3, fontSize: 11, bold: true, color: colors.primary,
  });

  const narrativeText = aiNarrative
    ? sanitizeAiNarrative(aiNarrative)
    : 'AI narrative unavailable for this brief. Review the ranked days and per-day deep dives that follow.';

  summarySlide.addText(narrativeText, {
    x: 0.5, y: 3.9, w: 12.3, h: 3.0, fontSize: 10, color: colors.text,
    valign: 'top', lineSpacing: 14, margin: 0.1, paraSpaceAfter: 6,
  });

  // ────────────────────────────────────────────────────────────────────
  // SLIDE 3: RANKED DAYS TABLE
  // ────────────────────────────────────────────────────────────────────
  if (topAtypicalDays.length > 0) {
    const rankedSlide = addContentSlide(
      `${briefLabel}: Ranked Days`,
      'Days sorted by composite atypicality score (mobility deviation + detector hits)'
    );

    const rows: any[] = [[
      { text: 'Rank', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
      { text: 'Score', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
      { text: 'Band', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
      { text: 'Date', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
      { text: 'Events', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
      { text: 'Dwells', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
      { text: 'Locations', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
      { text: 'Distance (km)', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
      { text: 'Detectors Fired', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
    ]];

    topAtypicalDays.slice(0, 14).forEach((d, idx) => {
      const rc = idx % 2 === 0 ? 'f9fafb' : 'ffffff';
      const score = d.atypicality_score || 0;
      const sColor = briefScoreColor(score);

      let detectorSummary = '';
      if (d.anomaly_breakdown) {
        const fired = Object.entries(d.anomaly_breakdown)
          .filter(([, v]) => (v || 0) > 0)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([k, v]) => `${formatPolAnomalyType(k)} x${v}`);
        detectorSummary = fired.length > 0 ? fired.join(', ') : 'Mobility-driven (no detectors)';
      }

      rows.push([
        { text: `${idx + 1}`, options: { fill: { color: rc }, align: 'center' as const, fontSize: 10, bold: true } },
        { text: `${(score * 100).toFixed(0)}%`, options: { fill: { color: rc }, color: sColor, bold: true, align: 'center' as const, fontSize: 11 } },
        { text: briefScoreBand(score), options: { fill: { color: rc }, color: sColor, align: 'center' as const, fontSize: 9, bold: true } },
        { text: briefFormatDate(d.date), options: { fill: { color: rc }, fontSize: 10 } },
        { text: `${d.total_anomaly_count ?? 0}`, options: { fill: { color: rc }, align: 'center' as const, fontSize: 10 } },
        { text: `${d.total_dwells ?? 0}`, options: { fill: { color: rc }, align: 'center' as const, fontSize: 10 } },
        { text: `${d.unique_locations ?? 0}`, options: { fill: { color: rc }, align: 'center' as const, fontSize: 10 } },
        { text: (d.total_distance_km ?? 0).toFixed(1), options: { fill: { color: rc }, align: 'center' as const, fontSize: 10 } },
        { text: detectorSummary || '—', options: { fill: { color: rc }, fontSize: 9, italic: !d.anomaly_breakdown } },
      ]);
    });

    rankedSlide.addTable(rows, {
      x: 0.5, y: 1.35, w: 12.3, rowH: 0.34, fontSize: 10,
      border: { type: 'solid', pt: 1, color: 'e5e7eb' },
      colW: [0.7, 1.0, 1.0, 1.6, 1.0, 1.0, 1.2, 1.4, 3.4],
    });
  }

  // ────────────────────────────────────────────────────────────────────
  // SLIDE 4+: PER-DAY DEEP DIVE (one slide per detail entry)
  // ────────────────────────────────────────────────────────────────────
  dayDetails.forEach((detail, idx) => {
    const score = detail.atypicality_score || 0;
    const sColor = briefScoreColor(score);
    const slide = addContentSlide(
      `Day ${idx + 1} of ${dayDetails.length}: ${briefFormatDate(detail.date)}`,
      `Atypicality ${(score * 100).toFixed(0)}% (${briefScoreBand(score)}) · ${detail.total_anomaly_count ?? detail.anomalies.length} events fired`
    );

    // Score chip — sits beside the title for quick visual triage.
    slide.addText(`${(score * 100).toFixed(0)}%`, {
      x: 11.5, y: 0.35, w: 1.3, h: 0.65, fontSize: 24, bold: true,
      color: sColor, align: 'center', valign: 'middle',
      fill: { color: 'ffffff' }, line: { color: sColor, width: 1.5 },
    });

    // Day stats grid (left column).
    const statRows: any[][] = [
      [
        { text: 'Events Fired', options: { bold: true, color: colors.text, fill: { color: 'f3f4f6' } } },
        { text: `${detail.total_anomaly_count ?? detail.anomalies.length}`, options: { color: colors.secondary, align: 'right' as const, fill: { color: 'f3f4f6' } } },
      ],
      [
        { text: 'Dwells', options: { bold: true, color: colors.text, fill: { color: 'ffffff' } } },
        { text: `${detail.total_dwells ?? 0}`, options: { color: colors.secondary, align: 'right' as const, fill: { color: 'ffffff' } } },
      ],
      [
        { text: 'Unique Locations', options: { bold: true, color: colors.text, fill: { color: 'f3f4f6' } } },
        { text: `${detail.unique_locations ?? 0}`, options: { color: colors.secondary, align: 'right' as const, fill: { color: 'f3f4f6' } } },
      ],
      [
        { text: 'Distance Traveled', options: { bold: true, color: colors.text, fill: { color: 'ffffff' } } },
        { text: `${(detail.total_distance_km ?? 0).toFixed(1)} km`, options: { color: colors.secondary, align: 'right' as const, fill: { color: 'ffffff' } } },
      ],
      [
        { text: 'Time at Dwells', options: { bold: true, color: colors.text, fill: { color: 'f3f4f6' } } },
        { text: formatMinutes(detail.total_dwell_minutes ?? 0), options: { color: colors.secondary, align: 'right' as const, fill: { color: 'f3f4f6' } } },
      ],
      [
        { text: 'Max Dist from Home', options: { bold: true, color: colors.text, fill: { color: 'ffffff' } } },
        { text: `${(detail.max_distance_from_home_km ?? 0).toFixed(1)} km`, options: { color: colors.secondary, align: 'right' as const, fill: { color: 'ffffff' } } },
      ],
      [
        { text: 'Track Points', options: { bold: true, color: colors.text, fill: { color: 'f3f4f6' } } },
        { text: `${detail.track_point_count ?? 0}`, options: { color: colors.secondary, align: 'right' as const, fill: { color: 'f3f4f6' } } },
      ],
    ];

    slide.addText('DAY METRICS', { x: 0.5, y: 1.4, w: 4.0, h: 0.3, fontSize: 11, bold: true, color: colors.primary });
    slide.addTable(statRows, {
      x: 0.5, y: 1.75, w: 4.0, rowH: 0.38, fontSize: 10,
      border: { type: 'solid', pt: 1, color: 'e5e7eb' },
      colW: [2.4, 1.6],
    });

    // Detector mix beneath the stats grid — shows which detectors fired and how often.
    if (detail.anomaly_breakdown) {
      const fired = Object.entries(detail.anomaly_breakdown)
        .filter(([, v]) => (v || 0) > 0)
        .sort((a, b) => b[1] - a[1]);
      if (fired.length > 0) {
        slide.addText('DETECTOR MIX', { x: 0.5, y: 4.55, w: 4.0, h: 0.3, fontSize: 11, bold: true, color: colors.primary });
        const detectorBullets = fired.map(([k, v]) => `${formatPolAnomalyType(k)} (${v})`);
        slide.addText(detectorBullets.join('\n'), {
          x: 0.5, y: 4.85, w: 4.0, h: 2.0, fontSize: 10, color: colors.text,
          valign: 'top', bullet: { indent: 12 }, lineSpacing: 14, margin: 0.05,
        });
      }
    }

    // Anomaly event list (right column) — the "what happened" detail.
    slide.addText('EVENTS THAT FIRED', { x: 4.8, y: 1.4, w: 8.0, h: 0.3, fontSize: 11, bold: true, color: colors.primary });

    if (detail.anomalies.length === 0) {
      slide.addText(
        'No discrete events on this day — atypicality was driven by mobility/dwell deviation from the entity baseline.',
        {
          x: 4.8, y: 1.75, w: 8.0, h: 0.6, fontSize: 11, italic: true, color: colors.lightText,
          line: { color: 'e5e7eb', width: 1 }, fill: { color: 'f9fafb' }, margin: 0.12, valign: 'middle',
        }
      );
    } else {
      const evRows: any[] = [[
        { text: 'Time', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
        { text: 'Type', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
        { text: 'Severity', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
        { text: 'Expected', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
        { text: 'Actual', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
      ]];

      // Cap at 14 rows so we never overflow the slide; analysts can see the full
      // list back in the UI when they need every event.
      detail.anomalies.slice(0, 14).forEach((a, i) => {
        const rc = i % 2 === 0 ? 'f9fafb' : 'ffffff';
        const sevColor = POL_SEVERITY_COLOR[(a.severity || '').toLowerCase()] || colors.secondary;
        evRows.push([
          { text: briefFormatTime(a.detected_at || ''), options: { fill: { color: rc }, align: 'center' as const, fontSize: 9 } },
          { text: formatPolAnomalyType(a.anomaly_type || ''), options: { fill: { color: rc }, fontSize: 9, bold: true } },
          { text: (a.severity || 'N/A').toUpperCase(), options: { fill: { color: rc }, color: sevColor, bold: true, align: 'center' as const, fontSize: 8 } },
          { text: (a.expected_behavior || '').slice(0, 70), options: { fill: { color: rc }, fontSize: 8 } },
          { text: (a.actual_behavior || '').slice(0, 70), options: { fill: { color: rc }, fontSize: 8 } },
        ]);
      });

      slide.addTable(evRows, {
        x: 4.8, y: 1.75, w: 8.0, rowH: 0.34, fontSize: 9,
        border: { type: 'solid', pt: 1, color: 'e5e7eb' },
        colW: [0.9, 1.8, 0.9, 2.2, 2.2],
      });

      if (detail.anomalies.length > 14) {
        slide.addText(`+ ${detail.anomalies.length - 14} more events — see Anomalies tab in the dashboard.`, {
          x: 4.8, y: 6.7, w: 8.0, h: 0.3, fontSize: 9, italic: true, color: colors.lightText,
        });
      }
    }
  });

  // ────────────────────────────────────────────────────────────────────
  // FINAL SLIDE: AI KEY FINDINGS & RECOMMENDED ACTIONS
  // ────────────────────────────────────────────────────────────────────
  const obsSlide = addContentSlide(
    `${briefLabel}: Findings & Next Steps`,
    'AI-extracted highlights and recommended actions'
  );

  const bullets = aiNarrative ? narrativeToBullets(aiNarrative, 10) : [];
  const keyFindings = bullets.slice(0, 5);
  const actionable = bullets.slice(5, 10);

  obsSlide.addText('AI GENERATED KEY FINDINGS', {
    x: 0.5, y: 1.4, w: 5.9, h: 0.35, fontSize: 12, bold: true, color: colors.primary,
  });
  obsSlide.addText(
    (keyFindings.length ? keyFindings : ['[AI narrative unavailable — add key findings from review]']).join('\n'),
    {
      x: 0.5, y: 1.8, w: 5.9, h: 3.0, fontSize: 10,
      color: keyFindings.length ? colors.text : '888888',
      valign: 'top', bullet: keyFindings.length ? { indent: 14 } : undefined,
      lineSpacing: 13, margin: 0.08, italic: !keyFindings.length,
    }
  );

  obsSlide.addText('AI GENERATED RECOMMENDED ACTIONS', {
    x: 6.8, y: 1.4, w: 5.9, h: 0.35, fontSize: 12, bold: true, color: colors.primary,
  });
  obsSlide.addText(
    (actionable.length ? actionable : ['[Add recommended next steps]']).join('\n'),
    {
      x: 6.8, y: 1.8, w: 5.9, h: 3.0, fontSize: 10,
      color: actionable.length ? colors.text : '888888',
      valign: 'top', bullet: actionable.length ? { indent: 14 } : undefined,
      lineSpacing: 13, margin: 0.08, italic: !actionable.length,
    }
  );

  // ────────────────────────────────────────────────────────────────────
  // SAVE
  // ────────────────────────────────────────────────────────────────────
  const safeLabel = briefLabel.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'brief';
  const filename = `${safeLabel}-${entityId}-${new Date().toISOString().split('T')[0]}.pptx`;
  await pptx.writeFile({ fileName: filename });
};


// ===========================================================================
// Day Ahead Brief (Phase 1 prediction export)
//
// Mirrors the Outlier Rankings brief but is forward-looking: it renders the
// /predict/expected-day response for a single entity + target date as a
// 5-slide PowerPoint. The visual language is intentionally the same
// (cover -> AI summary -> data slides -> findings) so the briefs stack
// as a pair: one explains the past, one anticipates the future.
//
// No new dependencies on the synthesizer dataclasses — this function takes
// the already-fetched API payload shape and renders it.
// ===========================================================================

export interface DayAheadBriefVisit {
  pattern_id: number | null;
  location_id: string | null;
  location_name: string | null;
  start_time: string;
  end_time: string;
  frequency: string | null;
  confidence: number;
  occurrence_count: number;
  avg_duration_minutes: number | null;
  avg_arrival_variance_minutes: number | null;
}

export interface DayAheadBriefTransit {
  route_id: string | null;
  route_name: string | null;
  origin_name: string | null;
  destination_name: string | null;
  peak_hour: number | null;
  avg_duration_minutes: number | null;
  avg_distance_km: number | null;
  consistency: number;
  confidence: number;
  usage_count: number;
  behavioral_tags: string[];
}

export interface DayAheadBriefCoPresence {
  other_entity_id: string;
  correlation_type: string;
  correlation_strength: number;
  confidence: number;
  active_hours: number[];
  avg_overlap_minutes: number | null;
}

export interface DayAheadBriefDigital {
  pattern_category: string;
  pattern_name: string;
  pattern_description: string | null;
  peak_hour: number | null;
  confidence: number;
  usage_frequency: number | null;
  days_active: number | null;
}

export interface DayAheadBriefSlot {
  hour: number;
  presence_probability: number;
  primary_activity: string | null;
  activity_kind: string;
  confidence: number;
}

export interface DayAheadBriefOptions {
  entityId: string;
  courseName?: string;
  forecastDate: string;        // YYYY-MM-DD
  dayOfWeek: string;
  predictionConfidence: number;  // 0..1
  dataMaturity: number;          // 0..1
  predictedActiveHours: number;
  predictedTotalDwells: number;
  predictedDistanceKm: number;
  predictedPlacesCount: number;
  slots: DayAheadBriefSlot[];
  expectedVisits: DayAheadBriefVisit[];
  expectedTransits: DayAheadBriefTransit[];
  expectedCoPresence: DayAheadBriefCoPresence[];
  expectedDigital: DayAheadBriefDigital[];
  sourcesUsed: Record<string, number>;
  notes: string[];
  /** Plain-prose AI narrative explaining what to expect. */
  aiNarrative?: string;
  theme?: PowerPointTheme;
}

// Confidence bands shared by the timeline cells and the chips on the cover.
const DAY_AHEAD_KIND_COLOR: Record<string, string> = {
  visit: '4CAF50',
  transit: '2196F3',
  digital: '9C27B0',
  copresence: 'FF9800',
  mixed: '673AB7',
  idle: 'BDBDBD',
};

const dayAheadConfidenceBand = (c: number): { label: string; color: string } => {
  if (c >= 0.7) return { label: 'High', color: POL_SEVERITY_COLOR.critical };
  if (c >= 0.4) return { label: 'Moderate', color: POL_SEVERITY_COLOR.medium };
  if (c >= 0.2) return { label: 'Low', color: POL_SEVERITY_COLOR.low };
  return { label: 'Very low', color: '9E9E9E' };
};

const dayAheadMaturityLabel = (m: number): string => {
  if (m >= 0.8) return 'Mature';
  if (m >= 0.5) return 'Developing';
  if (m >= 0.25) return 'Sparse';
  return 'New';
};

const dayAheadFormatDate = (iso: string): string => {
  try {
    return new Date(`${iso}T12:00:00Z`).toLocaleDateString(undefined, {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
    });
  } catch { return iso; }
};

export const exportDayAheadBriefToPowerPoint = async (
  options: DayAheadBriefOptions
): Promise<void> => {
  const {
    entityId,
    courseName,
    forecastDate,
    dayOfWeek,
    predictionConfidence,
    dataMaturity,
    predictedActiveHours,
    predictedTotalDwells,
    predictedDistanceKm,
    predictedPlacesCount,
    slots,
    expectedVisits,
    expectedTransits,
    expectedCoPresence,
    expectedDigital,
    sourcesUsed,
    notes,
    aiNarrative,
    theme = 'standard',
  } = options;

  const briefLabel = 'Day Ahead';
  const PptxGenJS = await loadPptxGenJS();
  const pptx = new PptxGenJS();
  pptx.layout = 'LAYOUT_WIDE';
  pptx.author = 'Pattern of Life Analysis System';
  pptx.company = 'Quiet Professionals';
  pptx.subject = `${briefLabel} Brief — ${entityId}`;
  pptx.title = `${briefLabel} Brief: ${entityId}`;

  const colors = THEME_DEFINITIONS[theme].colors;
  const titleBgDataUrl = generateGradientBackground(1920, 1080, 'title', theme);
  const contentBgDataUrl = generateGradientBackground(1920, 1080, 'content', theme);

  let logoDataUrl = '';
  try { logoDataUrl = await fetchAsDataUrl(TEMPLATE_ASSETS.logo); } catch { /* optional */ }

  const FOOTER = `${briefLabel.toUpperCase()} BRIEF • UNCLASSIFIED`;

  const addContentSlide = (titleText: string, subtitle?: string) => {
    const slide = pptx.addSlide();
    if (contentBgDataUrl) addFullBleedBackground(slide, contentBgDataUrl);
    else slide.background = { color: colors.contentBg };
    addFooter(slide, FOOTER, colors.lightText);
    if (logoDataUrl) addCornerLogo(slide, logoDataUrl);
    slide.addText(titleText, {
      x: 0.5, y: 0.4, w: 12.0, h: 0.55, fontSize: 28, bold: true, color: colors.primary,
    });
    if (subtitle) {
      slide.addText(subtitle, {
        x: 0.5, y: 0.95, w: 12.0, h: 0.3, fontSize: 12, color: colors.lightText,
      });
    }
    return slide;
  };

  const confBand = dayAheadConfidenceBand(predictionConfidence);
  const maturityLabel = dayAheadMaturityLabel(dataMaturity);

  // ───────────── COVER SLIDE ─────────────
  const titleSlide = pptx.addSlide();
  if (titleBgDataUrl) addFullBleedBackground(titleSlide, titleBgDataUrl);
  else titleSlide.background = { color: colors.titleBg };
  if (logoDataUrl) {
    addCornerLogo(titleSlide, logoDataUrl, { x: SLIDE_W - 1.5, y: SLIDE_H - 1.5, w: 1.1, h: 1.1 });
  }

  titleSlide.addText(`${briefLabel} Brief`, {
    x: 0.75, y: 1.6, w: SLIDE_W - 1.5, h: 0.8, fontSize: 40, bold: true, color: colors.titleText,
  });
  titleSlide.addText(entityId, {
    x: 0.75, y: 2.55, w: SLIDE_W - 1.5, h: 0.7, fontSize: 32, color: colors.accent, bold: true,
  });
  if (courseName) {
    titleSlide.addText(courseName, {
      x: 0.75, y: 3.35, w: SLIDE_W - 1.5, h: 0.4, fontSize: 18, color: colors.titleText,
    });
  }
  titleSlide.addText(`Forecast for ${dayAheadFormatDate(forecastDate)}`, {
    x: 0.75, y: 4.0, w: SLIDE_W - 1.5, h: 0.4, fontSize: 16, color: colors.titleText,
  });

  // Confidence + maturity callout block (mirrors the peak-atypicality block on the Outlier brief)
  titleSlide.addText(
    [
      { text: 'PREDICTION CONFIDENCE  ', options: { fontSize: 11, color: colors.titleText, bold: true } },
      { text: `${(predictionConfidence * 100).toFixed(0)}%`, options: { fontSize: 36, color: confBand.color, bold: true } },
      { text: `  ${confBand.label.toUpperCase()}`, options: { fontSize: 13, color: confBand.color, bold: true } },
      { text: `\nBaseline maturity: ${maturityLabel} (${(dataMaturity * 100).toFixed(0)}%)`, options: { fontSize: 13, color: colors.titleText } },
    ],
    {
      x: 0.75, y: 4.7, w: 6.5, h: 1.4, valign: 'middle',
      fill: { color: 'ffffff', transparency: 88 },
      line: { color: confBand.color, width: 1.5 },
    }
  );

  titleSlide.addText(
    `Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`,
    { x: 0.75, y: SLIDE_H - 0.7, w: SLIDE_W - 1.5, h: 0.3, fontSize: 11, color: colors.titleText }
  );

  // ───────────── AI SUMMARY SLIDE ─────────────
  const summarySlide = addContentSlide(
    `${briefLabel}: What to Expect`,
    `Forward-looking forecast of ${entityId} for ${dayOfWeek}, ${forecastDate}, synthesized from established baselines`
  );

  // KPI strip
  const kpis: Array<[string, string]> = [
    ['Active Hours', `${predictedActiveHours}h`],
    ['Expected Stops', `${predictedTotalDwells}`],
    ['Unique Places', `${predictedPlacesCount}`],
    ['Predicted Distance', `${predictedDistanceKm.toFixed(1)} km`],
  ];
  kpis.forEach(([label, value], i) => {
    summarySlide.addText(
      [
        { text: value, options: { bold: true, fontSize: 22, color: colors.primary } },
        { text: `\n${label.toUpperCase()}`, options: { fontSize: 9, color: colors.lightText, bold: true } },
      ],
      {
        x: 0.5 + i * 3.05, y: 1.35, w: 2.95, h: 0.95,
        align: 'center', valign: 'middle',
        fill: { color: 'ffffff' }, line: { color: 'e5e7eb', width: 0.75 },
      }
    );
  });

  summarySlide.addText('AI FORECAST NARRATIVE', {
    x: 0.5, y: 2.55, w: 12.3, h: 0.3, fontSize: 11, bold: true, color: colors.primary,
  });

  const narrativeText = aiNarrative
    ? sanitizeAiNarrative(aiNarrative)
    : `Forecast for ${entityId} on ${dayOfWeek} synthesized from recurring patterns, route patterns, correlations and digital baselines. Review the timeline and structured lists on the next slides for what is expected hour-by-hour.`;

  summarySlide.addText(narrativeText, {
    x: 0.5, y: 2.9, w: 12.3, h: 3.8, fontSize: 10, color: colors.text,
    valign: 'top', lineSpacing: 14, margin: 0.1, paraSpaceAfter: 6,
  });

  // ───────────── 24-HOUR TIMELINE SLIDE ─────────────
  const timelineSlide = addContentSlide(
    `${briefLabel}: 24-Hour Forecast Timeline`,
    'One cell per hour. Color = dominant activity kind. Opacity = confidence.'
  );

  // Render 24 hour cells as a horizontal strip
  const stripX = 0.5;
  const stripY = 1.5;
  const stripW = 12.3;
  const cellW = stripW / 24;
  const cellH = 1.2;

  slots.forEach((slot) => {
    const color = DAY_AHEAD_KIND_COLOR[slot.activity_kind] || DAY_AHEAD_KIND_COLOR.idle;
    // Map confidence (0..1) to transparency (0..70). Higher confidence -> more opaque.
    const intensity = slot.activity_kind === 'idle' ? 0.3 : 0.35 + slot.confidence * 0.6;
    const transparency = Math.round((1 - intensity) * 100);
    timelineSlide.addText(
      slot.hour % 3 === 0 ? String(slot.hour).padStart(2, '0') : '',
      {
        x: stripX + slot.hour * cellW, y: stripY, w: cellW - 0.02, h: cellH,
        fontSize: 9, color: 'ffffff', bold: true, align: 'center', valign: 'bottom',
        fill: { color, transparency },
        line: { color: 'e5e7eb', width: 0.5 },
      }
    );
  });

  // Hour ticks underneath — one label per 3-hour mark, aligned to its cell
  for (let h = 0; h <= 24; h += 3) {
    timelineSlide.addText(`${String(h).padStart(2, '0')}:00`, {
      x: stripX + h * cellW, y: stripY + cellH + 0.05, w: cellW * 3, h: 0.3,
      fontSize: 9, color: colors.lightText, align: 'left',
    });
  }

  // Legend
  const legendItems: Array<[string, string]> = [
    ['Visit', DAY_AHEAD_KIND_COLOR.visit],
    ['Transit', DAY_AHEAD_KIND_COLOR.transit],
    ['Digital', DAY_AHEAD_KIND_COLOR.digital],
    ['Co-presence', DAY_AHEAD_KIND_COLOR.copresence],
    ['Mixed', DAY_AHEAD_KIND_COLOR.mixed],
    ['Idle', DAY_AHEAD_KIND_COLOR.idle],
  ];
  legendItems.forEach(([label, color], i) => {
    timelineSlide.addText('', {
      x: 0.5 + i * 2.1, y: 3.25, w: 0.25, h: 0.25,
      fill: { color }, line: { color: 'e5e7eb', width: 0.5 },
    });
    timelineSlide.addText(label, {
      x: 0.85 + i * 2.1, y: 3.20, w: 1.8, h: 0.3, fontSize: 10, color: colors.text,
    });
  });

  // Activity ribbon underneath - top events listed in time order
  timelineSlide.addText('NOTABLE HOURS', {
    x: 0.5, y: 3.85, w: 12.3, h: 0.3, fontSize: 11, bold: true, color: colors.primary,
  });
  const notable = slots
    .filter((s) => s.activity_kind !== 'idle' && s.confidence >= 0.3)
    .slice(0, 12);
  if (notable.length === 0) {
    timelineSlide.addText('No high-confidence hourly expectations on this day.', {
      x: 0.5, y: 4.2, w: 12.3, h: 0.6, fontSize: 11, italic: true, color: colors.lightText,
    });
  } else {
    const notableRows: any[] = [[
      { text: 'Hour', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
      { text: 'Activity', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
      { text: 'Kind', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
      { text: 'Presence', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
      { text: 'Confidence', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
    ]];
    notable.forEach((s, i) => {
      const rc = i % 2 === 0 ? 'f9fafb' : 'ffffff';
      notableRows.push([
        { text: `${String(s.hour).padStart(2, '0')}:00`, options: { fill: { color: rc }, align: 'center' as const, fontSize: 10, bold: true } },
        { text: s.primary_activity || '—', options: { fill: { color: rc }, fontSize: 10 } },
        { text: s.activity_kind, options: { fill: { color: rc }, align: 'center' as const, fontSize: 9, color: DAY_AHEAD_KIND_COLOR[s.activity_kind] || colors.text } },
        { text: `${(s.presence_probability * 100).toFixed(0)}%`, options: { fill: { color: rc }, align: 'center' as const, fontSize: 10 } },
        { text: `${(s.confidence * 100).toFixed(0)}%`, options: { fill: { color: rc }, align: 'center' as const, fontSize: 10 } },
      ]);
    });
    timelineSlide.addTable(notableRows, {
      x: 0.5, y: 4.2, w: 12.3, rowH: 0.32, fontSize: 10,
      border: { type: 'solid', pt: 1, color: 'e5e7eb' },
      colW: [1.2, 5.5, 1.6, 1.8, 2.2],
    });
  }

  // ───────────── EXPECTED VISITS + TRANSITS SLIDE ─────────────
  const visitsSlide = addContentSlide(
    `${briefLabel}: Expected Visits & Transits`,
    'Recurring location visits and route patterns aligned to this day of week'
  );

  // Left column: visits
  visitsSlide.addText(`EXPECTED VISITS (${expectedVisits.length})`, {
    x: 0.5, y: 1.4, w: 6.0, h: 0.3, fontSize: 11, bold: true, color: colors.primary,
  });
  if (expectedVisits.length === 0) {
    visitsSlide.addText('No recurring visits match this day of week.', {
      x: 0.5, y: 1.75, w: 6.0, h: 0.6, fontSize: 11, italic: true, color: colors.lightText,
    });
  } else {
    const visitRows: any[] = [[
      { text: 'Location', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
      { text: 'Window', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
      { text: 'Conf.', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
    ]];
    expectedVisits.slice(0, 12).forEach((v, i) => {
      const rc = i % 2 === 0 ? 'f9fafb' : 'ffffff';
      const cBand = dayAheadConfidenceBand(v.confidence);
      visitRows.push([
        { text: v.location_name || v.location_id || 'Unnamed', options: { fill: { color: rc }, fontSize: 9, bold: true } },
        { text: `${v.start_time}–${v.end_time}`, options: { fill: { color: rc }, align: 'center' as const, fontSize: 9 } },
        { text: `${(v.confidence * 100).toFixed(0)}%`, options: { fill: { color: rc }, color: cBand.color, bold: true, align: 'center' as const, fontSize: 9 } },
      ]);
    });
    visitsSlide.addTable(visitRows, {
      x: 0.5, y: 1.75, w: 6.0, rowH: 0.32, fontSize: 9,
      border: { type: 'solid', pt: 1, color: 'e5e7eb' },
      colW: [3.2, 1.8, 1.0],
    });
  }

  // Right column: transits
  visitsSlide.addText(`EXPECTED TRANSITS (${expectedTransits.length})`, {
    x: 6.8, y: 1.4, w: 6.0, h: 0.3, fontSize: 11, bold: true, color: colors.primary,
  });
  if (expectedTransits.length === 0) {
    visitsSlide.addText('No route patterns match this day of week.', {
      x: 6.8, y: 1.75, w: 6.0, h: 0.6, fontSize: 11, italic: true, color: colors.lightText,
    });
  } else {
    const transitRows: any[] = [[
      { text: 'Route', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
      { text: 'Peak', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
      { text: 'Conf.', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
    ]];
    expectedTransits.slice(0, 12).forEach((t, i) => {
      const rc = i % 2 === 0 ? 'f9fafb' : 'ffffff';
      const cBand = dayAheadConfidenceBand(t.confidence);
      const label = t.route_name || `${t.origin_name ?? '?'} → ${t.destination_name ?? '?'}`;
      const peakStr = t.peak_hour !== null && t.peak_hour !== undefined ? `${String(t.peak_hour).padStart(2, '0')}:00` : '—';
      transitRows.push([
        { text: label, options: { fill: { color: rc }, fontSize: 9, bold: true } },
        { text: peakStr, options: { fill: { color: rc }, align: 'center' as const, fontSize: 9 } },
        { text: `${(t.confidence * 100).toFixed(0)}%`, options: { fill: { color: rc }, color: cBand.color, bold: true, align: 'center' as const, fontSize: 9 } },
      ]);
    });
    visitsSlide.addTable(transitRows, {
      x: 6.8, y: 1.75, w: 6.0, rowH: 0.32, fontSize: 9,
      border: { type: 'solid', pt: 1, color: 'e5e7eb' },
      colW: [3.2, 1.8, 1.0],
    });
  }

  // ───────────── CO-PRESENCE + DIGITAL SLIDE ─────────────
  const social = addContentSlide(
    `${briefLabel}: Co-Presence & Digital`,
    'Expected co-presence partners and digital activity for this day'
  );

  social.addText(`EXPECTED CO-PRESENCE (${expectedCoPresence.length})`, {
    x: 0.5, y: 1.4, w: 6.0, h: 0.3, fontSize: 11, bold: true, color: colors.primary,
  });
  if (expectedCoPresence.length === 0) {
    social.addText('No correlations active on this day of week.', {
      x: 0.5, y: 1.75, w: 6.0, h: 0.6, fontSize: 11, italic: true, color: colors.lightText,
    });
  } else {
    expectedCoPresence.slice(0, 8).forEach((c, i) => {
      const cBand = dayAheadConfidenceBand(c.confidence);
      const hoursStr = c.active_hours.slice(0, 4).map((h) => String(h).padStart(2, '0')).join(', ') +
        (c.active_hours.length > 4 ? '...' : '');
      social.addText(
        [
          { text: `${c.other_entity_id}  `, options: { fontSize: 10, bold: true, color: colors.text } },
          { text: `${(c.confidence * 100).toFixed(0)}%`, options: { fontSize: 10, color: cBand.color, bold: true } },
          { text: `\n${c.correlation_type.replace(/_/g, ' ')}${hoursStr ? ` · hours: ${hoursStr}` : ''}`,
            options: { fontSize: 9, color: colors.lightText } },
        ],
        {
          x: 0.5, y: 1.75 + i * 0.55, w: 6.0, h: 0.5, valign: 'top',
          fill: { color: 'f9fafb' }, line: { color: 'e5e7eb', width: 0.5 }, margin: 0.06,
        }
      );
    });
  }

  social.addText(`EXPECTED DIGITAL (${expectedDigital.length})`, {
    x: 6.8, y: 1.4, w: 6.0, h: 0.3, fontSize: 11, bold: true, color: colors.primary,
  });
  if (expectedDigital.length === 0) {
    social.addText('No high-confidence digital patterns established yet.', {
      x: 6.8, y: 1.75, w: 6.0, h: 0.6, fontSize: 11, italic: true, color: colors.lightText,
    });
  } else {
    expectedDigital.slice(0, 8).forEach((d, i) => {
      const cBand = dayAheadConfidenceBand(d.confidence);
      const detail = `${d.pattern_category}${d.peak_hour !== null && d.peak_hour !== undefined ? ` · peak ${String(d.peak_hour).padStart(2, '0')}:00` : ''}${d.days_active ? ` · ${d.days_active}d active` : ''}`;
      social.addText(
        [
          { text: `${d.pattern_name}  `, options: { fontSize: 10, bold: true, color: colors.text } },
          { text: `${(d.confidence * 100).toFixed(0)}%`, options: { fontSize: 10, color: cBand.color, bold: true } },
          { text: `\n${detail}`, options: { fontSize: 9, color: colors.lightText } },
        ],
        {
          x: 6.8, y: 1.75 + i * 0.55, w: 6.0, h: 0.5, valign: 'top',
          fill: { color: 'f9fafb' }, line: { color: 'e5e7eb', width: 0.5 }, margin: 0.06,
        }
      );
    });
  }

  // ───────────── NOTES & SOURCES SLIDE ─────────────
  const notesSlide = addContentSlide(
    `${briefLabel}: Forecast Notes & Sources`,
    'Caveats, data maturity warnings, and which baselines fed this forecast'
  );

  // AI bullets (re-uses narrativeToBullets so the wording is consistent across briefs)
  const aiBullets = aiNarrative ? narrativeToBullets(aiNarrative, 8) : [];
  const findings = aiBullets.slice(0, 4);
  const actions = aiBullets.slice(4, 8);

  notesSlide.addText('AI KEY EXPECTATIONS', {
    x: 0.5, y: 1.4, w: 5.9, h: 0.35, fontSize: 12, bold: true, color: colors.primary,
  });
  notesSlide.addText(
    (findings.length ? findings : ['[AI narrative unavailable — populate from review]']).join('\n'),
    {
      x: 0.5, y: 1.8, w: 5.9, h: 2.5, fontSize: 10,
      color: findings.length ? colors.text : '888888',
      valign: 'top', bullet: findings.length ? { indent: 14 } : undefined,
      lineSpacing: 13, margin: 0.08, italic: !findings.length,
    }
  );

  notesSlide.addText('AI RECOMMENDED COLLECTION', {
    x: 6.8, y: 1.4, w: 5.9, h: 0.35, fontSize: 12, bold: true, color: colors.primary,
  });
  notesSlide.addText(
    (actions.length ? actions : ['[Define which expected events to confirm]']).join('\n'),
    {
      x: 6.8, y: 1.8, w: 5.9, h: 2.5, fontSize: 10,
      color: actions.length ? colors.text : '888888',
      valign: 'top', bullet: actions.length ? { indent: 14 } : undefined,
      lineSpacing: 13, margin: 0.08, italic: !actions.length,
    }
  );

  // Notes block + sources
  notesSlide.addText('FORECAST NOTES', {
    x: 0.5, y: 4.5, w: 12.3, h: 0.3, fontSize: 11, bold: true, color: colors.primary,
  });
  if (notes.length === 0) {
    notesSlide.addText('No forecast notes recorded.', {
      x: 0.5, y: 4.85, w: 12.3, h: 0.5, fontSize: 10, italic: true, color: colors.lightText,
    });
  } else {
    notesSlide.addText(notes.join('\n'), {
      x: 0.5, y: 4.85, w: 12.3, h: 1.3, fontSize: 10, color: colors.text,
      valign: 'top', bullet: { indent: 14 }, lineSpacing: 14, margin: 0.08,
    });
  }

  const sourcesStr = Object.entries(sourcesUsed)
    .map(([k, v]) => `${k.replace(/_/g, ' ')}=${v}`)
    .join(' · ');
  notesSlide.addText(`SOURCES USED: ${sourcesStr || 'none'}`, {
    x: 0.5, y: 6.4, w: 12.3, h: 0.35, fontSize: 9, italic: true, color: colors.lightText,
  });

  // ───────────── SAVE ─────────────
  const filename = `day-ahead-${entityId}-${forecastDate}.pptx`;
  await pptx.writeFile({ fileName: filename });
};

// ============================================================================
// PATTERN OF LIFE REPORT DECK (pol_report analysis jobs)
// ============================================================================

/**
 * Render every repeated route onto a Mapbox basemap and return a data URL.
 *
 * The Static Images geojson overlay is URL-capped, so only a bounding-box
 * basemap is fetched. Routes are drawn on a canvas afterward, which keeps
 * all of them regardless of geometry size.
 */
async function buildPolStaticMapDataUrl(routeRows: any[], dwellRows: any[]): Promise<string | null> {
  const token = process.env.REACT_APP_MAPBOX_TOKEN || '';
  if (!token) return null;

  const simplify = (coords: number[][], maxPoints = 48) => {
    if (coords.length <= maxPoints) return coords;
    const step = Math.max(1, Math.ceil((coords.length - 1) / (maxPoints - 1)));
    const reduced = coords.filter((_, i) => i % step === 0);
    const last = coords[coords.length - 1];
    if (reduced[reduced.length - 1] !== last) reduced.push(last);
    return reduced;
  };
  const mercator = (lon: number, lat: number): [number, number] => {
    const clamped = Math.max(-85.05112878, Math.min(85.05112878, lat));
    return [
      (lon * Math.PI) / 180,
      Math.log(Math.tan(Math.PI / 4 + (clamped * Math.PI) / 360)),
    ];
  };

  const lines = routeRows
    .map((r) => {
      const geom: any[] = Array.isArray(r.route_geometry) ? r.route_geometry : [];
      const coords = geom
        .map((c: any) => [Number(c?.[0]), Number(c?.[1])])
        .filter((c: number[]) => Number.isFinite(c[0]) && Number.isFinite(c[1]));
      return { usage: Number(r.usage_count) || 0, coords: simplify(coords) };
    })
    .filter((line) => line.coords.length >= 2)
    .sort((a, b) => a.usage - b.usage);
  const markers = [...dwellRows]
    .map((d) => ({
      lon: Number(d.lon ?? d.longitude),
      lat: Number(d.lat ?? d.latitude),
      visits: Number(d.visit_count) || 1,
    }))
    .filter((d) => Number.isFinite(d.lon) && Number.isFinite(d.lat))
    .sort((a, b) => b.visits - a.visits)
    .slice(0, 12);
  if (lines.length === 0) return null;

  const samples = [
    ...lines.flatMap((line) => line.coords.map((c) => mercator(c[0], c[1]))),
    ...markers.map((d) => mercator(d.lon, d.lat)),
  ];
  let minx = Math.min(...samples.map((p) => p[0]));
  let maxx = Math.max(...samples.map((p) => p[0]));
  let miny = Math.min(...samples.map((p) => p[1]));
  let maxy = Math.max(...samples.map((p) => p[1]));
  const padX = Math.max(maxx - minx, 1e-6) * 0.08;
  const padY = Math.max(maxy - miny, 1e-6) * 0.08;
  minx -= padX; maxx += padX; miny -= padY; maxy += padY;
  const W = 1280;
  const H = 640;
  const dx = maxx - minx;
  const dy = maxy - miny;
  if (W / H > dx / dy) {
    const extra = dy * (W / H) - dx;
    minx -= extra / 2;
    maxx += extra / 2;
  } else {
    const extra = dx / (W / H) - dy;
    miny -= extra / 2;
    maxy += extra / 2;
  }
  const invLat = (y: number) => (2 * Math.atan(Math.exp(y)) - Math.PI / 2) * (180 / Math.PI);
  const bbox = `[${(minx * 180 / Math.PI).toFixed(5)},${invLat(miny).toFixed(5)},${(maxx * 180 / Math.PI).toFixed(5)},${invLat(maxy).toFixed(5)}]`;
  const url =
    `https://api.mapbox.com/styles/v1/mapbox/dark-v11/static/` +
    `${encodeURIComponent(bbox)}/${W}x${H}?access_token=${token}`;

  let basemap: string;
  try {
    basemap = await fetchAsDataUrl(url);
  } catch {
    return null;
  }

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return basemap;
  const img = new Image();
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('basemap decode failed'));
    img.src = basemap;
  }).catch(() => null);
  if (!img.complete || img.naturalWidth === 0) return basemap;
  ctx.drawImage(img, 0, 0, W, H);

  const project = (lon: number, lat: number): [number, number] => {
    const [x, y] = mercator(lon, lat);
    return [
      ((x - minx) / (maxx - minx)) * W,
      ((maxy - y) / (maxy - miny)) * H,
    ];
  };
  const maxUsage = Math.max(...lines.map((line) => line.usage), 1);
  lines.forEach((line) => {
    ctx.beginPath();
    line.coords.forEach((c, i) => {
      const [px, py] = project(c[0], c[1]);
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    });
    ctx.strokeStyle = 'rgba(76, 154, 255, 0.9)';
    ctx.lineWidth = 2.5 + 3 * (line.usage / maxUsage);
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.stroke();
  });
  const maxVisits = Math.max(...markers.map((d) => d.visits), 1);
  markers.forEach((d) => {
    const [px, py] = project(d.lon, d.lat);
    const radius = 5 + 7 * Math.sqrt(d.visits / maxVisits);
    ctx.beginPath();
    ctx.arc(px, py, radius, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(224, 49, 49, 0.92)';
    ctx.fill();
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = '#ffffff';
    ctx.stroke();
  });
  return canvas.toDataURL('image/png');
}

export type PolReportDeckTemplate =
  | 'executive_summary'
  | 'full_report'
  | 'entity_annex'
  | 'anomaly_digest';

const POL_DECK_TEMPLATE_TITLES: Record<PolReportDeckTemplate, string> = {
  executive_summary: 'Executive Summary',
  full_report: 'Full Intelligence Report',
  entity_annex: 'Per-Entity Annex',
  anomaly_digest: 'Anomaly Digest',
};

const POL_DECK_SECTION_LABELS: Record<string, string> = {
  repeated_routes: 'Repeated Routes',
  dwells: 'Dwells & Top Locations',
  anomalies: 'Anomalies & Detector Events',
  patterns: 'Recurring Patterns',
  correlations: 'Entity Correlations',
  sequences: 'Location Sequences',
  digital: 'Digital Patterns',
  atypicality: 'Daily Atypicality',
};

export interface PolReportDeckOptions {
  jobId: number;
  jobName: string;
  template: PolReportDeckTemplate;
  /** The pol_report payload from analysis_jobs.results_summary. */
  report: {
    generated_at: string;
    course_id?: string;
    date_range: { start: string; end: string };
    analysis_types: string[];
    entities: any[];
    sections: Record<string, any[]>;
    section_counts: Record<string, number>;
    warnings: string[];
  };
  theme?: PowerPointTheme;
}

const POL_DECK_ROWS_PER_SLIDE = 13;

export const exportPolReportDeck = async (options: PolReportDeckOptions): Promise<void> => {
  const { jobId, jobName, template, report, theme = 'standard' } = options;

  const PptxGenJS = await loadPptxGenJS();
  const pptx = new PptxGenJS();
  pptx.layout = 'LAYOUT_WIDE';
  pptx.author = 'Pattern of Life Analysis System';
  pptx.company = 'Quiet Professionals';
  pptx.subject = `Pattern of Life ${POL_DECK_TEMPLATE_TITLES[template]}`;
  pptx.title = jobName;

  const colors = THEME_DEFINITIONS[theme].colors;
  const titleBgDataUrl = generateGradientBackground(1920, 1080, 'title', theme);
  const contentBgDataUrl = generateGradientBackground(1920, 1080, 'content', theme);
  let logoDataUrl = '';
  try { logoDataUrl = await fetchAsDataUrl(TEMPLATE_ASSETS.logo); } catch { /* optional */ }

  const FOOTER = 'PATTERN OF LIFE • UNCLASSIFIED';

  const fmtDay = (iso?: string) =>
    iso ? new Date(iso).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const fmtDt = (iso?: string) => (iso ? new Date(iso).toLocaleString() : '—');
  const fmtDuration = (seconds?: number) => {
    if (!seconds || seconds <= 0) return '—';
    const h = Math.floor(seconds / 3600);
    const m = Math.round((seconds % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };
  const fmtMiles = (meters?: number) => {
    if (!meters || meters <= 0) return '—';
    return `${(meters / 1609.344).toFixed(1)} mi`;
  };
  const pct = (v?: number) => `${Math.round((v || 0) * 100)}%`;
  const truncate = (s: string | undefined | null, len: number) => {
    const str = s || '';
    return str.length > len ? `${str.slice(0, len - 1)}…` : str;
  };

  const addContentSlide = (titleText: string) => {
    const slide = pptx.addSlide();
    if (contentBgDataUrl) addFullBleedBackground(slide, contentBgDataUrl);
    else slide.background = { color: colors.contentBg };
    addFooter(slide, FOOTER, colors.lightText);
    if (logoDataUrl) addCornerLogo(slide, logoDataUrl);
    slide.addText(titleText, { x: 0.5, y: 0.4, w: 12.0, h: 0.6, fontSize: 26, bold: true, color: colors.primary });
    return slide;
  };

  const headerCell = (text: string, align: 'left' | 'center' | 'right' = 'left') => ({
    text,
    options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: align as any, fontSize: 10 },
  });
  const bodyCell = (
    text: string,
    idx: number,
    align: 'left' | 'center' | 'right' = 'left',
    fontSize = 9
  ) => ({
    text,
    options: {
      color: colors.text,
      fill: { color: idx % 2 === 0 ? 'f7f9fb' : 'ffffff' },
      align: align as any,
      fontSize,
    },
  });

  /** Add one or more slides for a table, chunking long row sets. */
  const addTableSlides = (
    titleText: string,
    header: Array<{ label: string; align?: 'left' | 'center' | 'right' }>,
    rows: string[][],
    colW: number[]
  ) => {
    if (rows.length === 0) {
      const slide = addContentSlide(titleText);
      slide.addText('No records in this reporting window.', {
        x: 0.5, y: 1.4, w: 12.0, h: 0.4, fontSize: 12, italic: true, color: colors.lightText,
      });
      return;
    }
    const chunks: string[][][] = [];
    for (let i = 0; i < rows.length; i += POL_DECK_ROWS_PER_SLIDE) {
      chunks.push(rows.slice(i, i + POL_DECK_ROWS_PER_SLIDE));
    }
    chunks.forEach((chunk, ci) => {
      const suffix = chunks.length > 1 ? ` (${ci + 1}/${chunks.length})` : '';
      const slide = addContentSlide(`${titleText}${suffix}`);
      const tableRows: any[] = [header.map((h) => headerCell(h.label, h.align))];
      chunk.forEach((r, ri) => {
        tableRows.push(r.map((cell, colIdx) => bodyCell(cell, ri, header[colIdx]?.align)));
      });
      slide.addTable(tableRows, {
        x: 0.5, y: 1.25, w: 12.3, rowH: 0.36, fontSize: 9,
        border: { type: 'solid', pt: 1, color: 'e5e7eb' }, colW,
      });
    });
  };

  const included = Object.keys(POL_DECK_SECTION_LABELS).filter((sid) =>
    (report.analysis_types || []).includes(sid)
  );
  const sections = report.sections || {};

  // ── TITLE SLIDE ──
  const titleSlide = pptx.addSlide();
  if (titleBgDataUrl) addFullBleedBackground(titleSlide, titleBgDataUrl);
  else titleSlide.background = { color: colors.titleBg };
  if (logoDataUrl) addCornerLogo(titleSlide, logoDataUrl, { x: SLIDE_W - 1.5, y: SLIDE_H - 1.5, w: 1.1, h: 1.1 });

  titleSlide.addText('Pattern of Life', { x: 0.75, y: 1.7, w: SLIDE_W - 1.5, h: 0.8, fontSize: 38, bold: true, color: colors.titleText });
  titleSlide.addText(POL_DECK_TEMPLATE_TITLES[template], { x: 0.75, y: 2.6, w: SLIDE_W - 1.5, h: 0.7, fontSize: 28, color: colors.accent, bold: true });
  if (report.course_id) {
    titleSlide.addText(report.course_id, { x: 0.75, y: 3.5, w: SLIDE_W - 1.5, h: 0.4, fontSize: 18, color: colors.titleText });
  }
  titleSlide.addText(
    `Reporting Window: ${fmtDay(report.date_range?.start)} – ${fmtDay(report.date_range?.end)}`,
    { x: 0.75, y: 4.2, w: SLIDE_W - 1.5, h: 0.35, fontSize: 14, color: colors.titleText }
  );
  titleSlide.addText(
    `${report.entities.length} entities · ${included.length} analysis types · Job #${jobId}`,
    { x: 0.75, y: 4.7, w: SLIDE_W - 1.5, h: 0.3, fontSize: 13, color: colors.titleText }
  );
  titleSlide.addText(`Generated: ${fmtDt(report.generated_at)}`, {
    x: 0.75, y: 5.5, w: SLIDE_W - 1.5, h: 0.3, fontSize: 12, color: colors.titleText,
  });

  // ───────────── DERIVED INSIGHTS (charts + findings source data) ─────────────
  const anomalyRows: any[] = sections.anomalies || [];
  const atypRows: any[] = sections.atypicality || [];
  const dwellRows: any[] = sections.dwells || [];
  const routeRows: any[] = sections.repeated_routes || [];
  const patternRows: any[] = sections.patterns || [];
  const corrRows: any[] = sections.correlations || [];
  const digitalRows: any[] = sections.digital || [];

  const SEV_RANK: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1 };
  const highSevCount = anomalyRows.filter(
    (a) => a.severity === 'high' || a.severity === 'critical'
  ).length;
  const breakingCount = patternRows.filter(
    (p) => String(p.status || '').toLowerCase() === 'breaking'
  ).length;
  const withConf = report.entities.filter((e: any) => e.data_confidence != null);
  const avgConfidence = withConf.length
    ? withConf.reduce((acc: number, e: any) => acc + (e.data_confidence || 0), 0) / withConf.length
    : null;

  // Daily buckets: atypicality trend + detector-event counts.
  const dayBuckets = new Map<string, { scores: number[]; max: number; events: number }>();
  const dayBucket = (iso: string) => {
    const key = iso.slice(0, 10);
    let b = dayBuckets.get(key);
    if (!b) {
      b = { scores: [], max: 0, events: 0 };
      dayBuckets.set(key, b);
    }
    return b;
  };
  atypRows.forEach((m) => {
    if (!m.day) return;
    const b = dayBucket(m.day);
    b.scores.push(m.atypicality_score || 0);
    b.max = Math.max(b.max, m.atypicality_score || 0);
  });
  anomalyRows.forEach((a) => {
    if (a.anomaly_time) dayBucket(a.anomaly_time).events += 1;
  });
  const timelineDays = Array.from(dayBuckets.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, b]) => ({
      label: new Date(`${day}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      avg: Math.round((b.scores.length ? b.scores.reduce((x, y) => x + y, 0) / b.scores.length : 0) * 100) / 100,
      max: Math.round(b.max * 100) / 100,
      events: b.events,
    }));

  // Severity + anomaly-type distributions.
  const sevCounts: Record<string, number> = {};
  const typeCounts: Record<string, number> = {};
  anomalyRows.forEach((a) => {
    const sev = String(a.severity || 'medium').toLowerCase();
    sevCounts[sev] = (sevCounts[sev] || 0) + 1;
    const t = String(a.anomaly_type || 'other').replace(/_/g, ' ');
    typeCounts[t] = (typeCounts[t] || 0) + 1;
  });
  const topTypes = Object.entries(typeCounts).sort((a, b) => b[1] - a[1]).slice(0, 6);

  // Hour-of-day / day-of-week activity weights (same sources as the web dashboard).
  const DAY_LABELS_PPT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const hourWeights = new Array(24).fill(0);
  const dowWeights = new Array(7).fill(0);
  const dayIdxOf = (v: any): number | null => {
    if (v == null) return null;
    if (typeof v === 'number' && Number.isFinite(v)) return v >= 0 && v <= 6 ? Math.round(v) : null;
    const idx = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].indexOf(
      String(v).trim().toLowerCase().slice(0, 3)
    );
    return idx >= 0 ? idx : null;
  };
  routeRows.forEach((r) => {
    const weight = Math.max(1, r.usage_count || 1);
    const hours: number[] = (r.typical_hours || [])
      .map((h: any) => (typeof h === 'number' ? h : parseInt(h, 10)))
      .filter((h: number) => Number.isFinite(h) && h >= 0 && h <= 23);
    (hours.length ? hours : r.peak_hour != null ? [r.peak_hour] : []).forEach((h: number) => {
      hourWeights[h] += weight;
    });
    const days = (r.typical_days || []).map(dayIdxOf).filter((d: number | null) => d != null);
    (days.length ? days : [dayIdxOf(r.peak_day)]).forEach((d: number | null) => {
      if (d != null) dowWeights[d] += weight;
    });
  });
  patternRows.forEach((p) => {
    const weight = Math.max(1, p.occurrence_count || 1);
    const startH = parseInt(String(p.time_window_start || '').split(':')[0], 10);
    if (Number.isFinite(startH) && startH >= 0 && startH <= 23) hourWeights[startH] += weight;
    (p.days_of_week || []).map(dayIdxOf).forEach((d: number | null) => {
      if (d != null) dowWeights[d] += weight;
    });
  });
  digitalRows.forEach((p) => {
    if (p.peak_hour != null && p.peak_hour >= 0 && p.peak_hour <= 23) {
      hourWeights[p.peak_hour] += Math.max(1, p.occurrence_count || 1);
    }
    const d = dayIdxOf(p.peak_day);
    if (d != null) dowWeights[d] += Math.max(1, p.occurrence_count || 1);
  });
  anomalyRows.forEach((a) => {
    if (!a.anomaly_time) return;
    const t = new Date(a.anomaly_time);
    hourWeights[t.getHours()] += 2;
    dowWeights[(t.getDay() + 6) % 7] += 2;
  });
  const hasRhythm = hourWeights.some((w) => w > 0);

  // Top locations by visits.
  const locAgg = new Map<string, { visits: number; hours: number }>();
  dwellRows.forEach((d) => {
    const name = d.location_name || 'Unknown location';
    const cur = locAgg.get(name) || { visits: 0, hours: 0 };
    cur.visits += d.visit_count || 0;
    cur.hours += (d.total_duration_seconds || 0) / 3600;
    locAgg.set(name, cur);
  });
  const topLocations = Array.from(locAgg.entries())
    .map(([name, v]) => ({ name, visits: v.visits, hours: Math.round(v.hours * 10) / 10 }))
    .sort((a, b) => b.visits - a.visits)
    .slice(0, 10);

  // Rule-generated key findings (mirrors the web Intelligence Summary).
  const FINDING_COLORS = { critical: 'c0392b', notable: 'd97706', insight: '2563eb', baseline: '2e7d32' };
  const findings: Array<{ color: string; tag: string; title: string; detail: string }> = [];
  if (highSevCount > 0) {
    const worst = [...anomalyRows].sort(
      (a, b) =>
        (SEV_RANK[b.severity] || 0) - (SEV_RANK[a.severity] || 0) ||
        (b.deviation_score || 0) - (a.deviation_score || 0)
    )[0];
    findings.push({
      color: FINDING_COLORS.critical,
      tag: 'CRITICAL',
      title: `${highSevCount} high-severity anomal${highSevCount === 1 ? 'y' : 'ies'} detected`,
      detail: worst
        ? `Most significant: ${String(worst.anomaly_type || '').replace(/_/g, ' ')} by ${worst.entity_id} on ${fmtDt(worst.anomaly_time)}.`
        : 'See the anomaly section for details.',
    });
  }
  const topAtyp = [...atypRows].sort(
    (a, b) => (b.atypicality_score || 0) - (a.atypicality_score || 0)
  )[0];
  if (topAtyp && (topAtyp.atypicality_score || 0) >= 0.5) {
    findings.push({
      color: FINDING_COLORS.notable,
      tag: 'NOTABLE',
      title: `${topAtyp.entity_id} had a highly atypical day`,
      detail: `${fmtDay(topAtyp.day)} scored ${pct(topAtyp.atypicality_score)} atypical with ${topAtyp.total_anomaly_count || 0} detector events.`,
    });
  }
  if (breakingCount > 0) {
    findings.push({
      color: FINDING_COLORS.notable,
      tag: 'NOTABLE',
      title: `${breakingCount} established routine${breakingCount === 1 ? ' is' : 's are'} breaking`,
      detail: 'Departure from a stable pattern often precedes a significant event — see Recurring Patterns.',
    });
  }
  const topCorr = corrRows[0];
  if (topCorr && (topCorr.correlation_strength || 0) >= 0.5) {
    findings.push({
      color: FINDING_COLORS.insight,
      tag: 'INSIGHT',
      title: `Strong association: ${topCorr.entity_a_id} ↔ ${topCorr.entity_b_id}`,
      detail: `${pct(topCorr.correlation_strength)} correlation across ${topCorr.occurrence_count || 0} co-occurrences.`,
    });
  }
  const sharedRoute = routeRows.find((r) => (r.shared_with_entities || []).length > 0);
  if (sharedRoute) {
    findings.push({
      color: FINDING_COLORS.insight,
      tag: 'INSIGHT',
      title: 'Shared movement corridor identified',
      detail: `${sharedRoute.entity_id}'s ${sharedRoute.route_name || 'route'} is shared with ${(sharedRoute.shared_with_entities || []).length} other entit${(sharedRoute.shared_with_entities || []).length === 1 ? 'y' : 'ies'}.`,
    });
  }
  if (routeRows[0] && findings.length < 5) {
    const r = routeRows[0];
    findings.push({
      color: FINDING_COLORS.baseline,
      tag: 'BASELINE',
      title: `Dominant route: ${truncate(r.route_name || 'unnamed route', 60)}`,
      detail: `${r.entity_id} made ${r.usage_count || 0} trips at ${pct(r.consistency_score)} consistency.`,
    });
  }
  if (dwellRows[0] && findings.length < 5) {
    const d = dwellRows[0];
    findings.push({
      color: FINDING_COLORS.baseline,
      tag: 'BASELINE',
      title: `Anchor location: ${truncate(d.location_name || 'unnamed location', 60)}`,
      detail: `${d.visit_count || 0} visits totaling ${fmtDuration(d.total_duration_seconds)} by ${d.entity_id}.`,
    });
  }
  const topFindings = findings.slice(0, 5);

  // ───────────── VISUAL SLIDES ─────────────
  const CT = (pptx as any).ChartType || { bar: 'bar', line: 'line', doughnut: 'doughnut' };
  const chartAxisOpts = {
    catAxisLabelFontSize: 9,
    valAxisLabelFontSize: 9,
    catAxisLabelColor: colors.lightText,
    valAxisLabelColor: colors.lightText,
    legendFontSize: 10,
  };

  const addStatBox = (
    slide: any,
    x: number,
    y: number,
    w: number,
    h: number,
    value: string,
    label: string,
    accent: string
  ) => {
    slide.addShape('roundRect', {
      x, y, w, h,
      fill: { color: 'ffffff' },
      line: { color: 'e5e7eb', width: 1 },
      rectRadius: 0.05,
      shadow: { type: 'outer', blur: 4, offset: 1, angle: 90, color: 'c8ccd4', opacity: 0.35 },
    });
    slide.addShape('rect', { x, y, w: 0.08, h, fill: { color: accent } });
    slide.addText(
      [
        { text: value, options: { fontSize: 26, bold: true, color: colors.primary, breakLine: true } },
        { text: label.toUpperCase(), options: { fontSize: 9, color: colors.lightText, charSpacing: 1 } },
      ],
      { x: x + 0.18, y: y + 0.12, w: w - 0.3, h: h - 0.24, valign: 'middle' }
    );
  };

  const includeOverviewVisuals = template !== 'entity_annex';

  if (includeOverviewVisuals) {
    // ── AT A GLANCE (KPI board) ──
    const glance = addContentSlide('At a Glance');
    const kpis: Array<{ value: string; label: string; accent: string }> = [
      { value: String(report.entities.length), label: 'Entities analyzed', accent: colors.accent },
      {
        value: String(report.section_counts?.anomalies ?? anomalyRows.length),
        label: `Anomalies (${highSevCount} high severity)`,
        accent: highSevCount > 0 ? FINDING_COLORS.critical : FINDING_COLORS.baseline,
      },
      { value: String(report.section_counts?.dwells ?? dwellRows.length), label: 'Locations tracked', accent: colors.accent },
      { value: String(report.section_counts?.repeated_routes ?? routeRows.length), label: 'Recurring routes', accent: colors.accent },
      {
        value: String(report.section_counts?.patterns ?? patternRows.length),
        label: `Patterns (${breakingCount} breaking)`,
        accent: breakingCount > 0 ? FINDING_COLORS.notable : colors.accent,
      },
      {
        value: avgConfidence != null ? pct(avgConfidence) : '—',
        label: 'Avg data confidence',
        accent: avgConfidence != null && avgConfidence >= 0.7 ? FINDING_COLORS.baseline : FINDING_COLORS.notable,
      },
    ];
    const boxW = 3.95;
    const boxH = 1.35;
    kpis.forEach((k, i) => {
      const col = i % 3;
      const row = Math.floor(i / 3);
      addStatBox(glance, 0.55 + col * (boxW + 0.25), 1.5 + row * (boxH + 0.3), boxW, boxH, k.value, k.label, k.accent);
    });
    glance.addText(
      `Reporting window ${fmtDay(report.date_range?.start)} – ${fmtDay(report.date_range?.end)} · ${included.length} analysis types included`,
      { x: 0.55, y: 4.8, w: 12.2, h: 0.35, fontSize: 11, italic: true, color: colors.lightText }
    );

    // ── KEY FINDINGS ──
    if (topFindings.length > 0) {
      const kf = addContentSlide('Key Findings');
      topFindings.forEach((f, i) => {
        const y = 1.3 + i * 1.08;
        kf.addShape('rect', { x: 0.55, y, w: 0.09, h: 0.92, fill: { color: f.color } });
        kf.addText(
          [
            { text: `${i + 1}.  ${f.title}   `, options: { fontSize: 14, bold: true, color: colors.text } },
            { text: f.tag, options: { fontSize: 9, bold: true, color: f.color, charSpacing: 1 } },
          ],
          { x: 0.8, y, w: 12.0, h: 0.42, valign: 'middle' }
        );
        kf.addText(f.detail, {
          x: 0.8, y: y + 0.42, w: 12.0, h: 0.5, fontSize: 11, color: colors.lightText, valign: 'top',
        });
      });
    }

    // ── ACTIVITY TIMELINE (atypicality trend + detector events) ──
    if (timelineDays.length > 1) {
      const tl = addContentSlide('Activity Timeline');
      const labels = timelineDays.map((d) => d.label);
      tl.addChart(
        CT.line,
        [
          { name: 'Avg atypicality', labels, values: timelineDays.map((d) => d.avg) },
          { name: 'Peak atypicality', labels, values: timelineDays.map((d) => d.max) },
        ],
        {
          x: 0.5, y: 1.35, w: 6.1, h: 4.6,
          chartColors: [colors.accent, FINDING_COLORS.notable],
          showLegend: true, legendPos: 'b',
          lineSize: 2, lineSmooth: true,
          valAxisMinVal: 0, valAxisMaxVal: 1,
          ...chartAxisOpts,
        }
      );
      tl.addChart(
        CT.bar,
        [{ name: 'Detector events', labels, values: timelineDays.map((d) => d.events) }],
        {
          x: 6.9, y: 1.35, w: 5.9, h: 4.6,
          chartColors: [FINDING_COLORS.critical],
          showLegend: true, legendPos: 'b',
          ...chartAxisOpts,
        }
      );
      tl.addText('Daily behavioral atypicality (0–100%) against rule/ML detector event volume.', {
        x: 0.5, y: 6.15, w: 12.3, h: 0.3, fontSize: 10, italic: true, color: colors.lightText,
      });
    }

    // ── ANOMALY PROFILE (severity + type distribution) ──
    if (anomalyRows.length > 0) {
      const ap = addContentSlide('Anomaly Profile');
      const sevOrder = ['critical', 'high', 'medium', 'low'].filter((s) => sevCounts[s]);
      ap.addChart(
        CT.doughnut,
        [{
          name: 'Severity',
          labels: sevOrder.map((s) => s.toUpperCase()),
          values: sevOrder.map((s) => sevCounts[s]),
        }],
        {
          x: 0.5, y: 1.35, w: 5.4, h: 4.6,
          chartColors: sevOrder.map((s) =>
            s === 'critical' ? '8e1600' : s === 'high' ? FINDING_COLORS.critical : s === 'medium' ? FINDING_COLORS.notable : '2563eb'
          ),
          holeSize: 60, showPercent: true, showLegend: true, legendPos: 'b',
          dataLabelFontSize: 10, legendFontSize: 10,
        }
      );
      if (topTypes.length > 0) {
        ap.addChart(
          CT.bar,
          [{ name: 'Events', labels: topTypes.map(([t]) => t), values: topTypes.map(([, n]) => n) }],
          {
            x: 6.3, y: 1.35, w: 6.5, h: 4.6,
            barDir: 'bar',
            chartColors: [colors.accent],
            showLegend: false, showValue: true, dataLabelFontSize: 9,
            ...chartAxisOpts,
          }
        );
      }
      ap.addText(`${anomalyRows.length} detector events in window · ${highSevCount} high severity`, {
        x: 0.5, y: 6.15, w: 12.3, h: 0.3, fontSize: 10, italic: true, color: colors.lightText,
      });
    }

    // ── BEHAVIORAL RHYTHM (full report only) ──
    if (template === 'full_report' && hasRhythm) {
      const br = addContentSlide('Behavioral Rhythm');
      br.addChart(
        CT.bar,
        [{
          name: 'Activity weight',
          labels: Array.from({ length: 24 }, (_, h) => `${h}`),
          values: hourWeights.map((w) => Math.round(w * 10) / 10),
        }],
        {
          x: 0.5, y: 1.35, w: 7.3, h: 4.6,
          chartColors: [colors.accent],
          showLegend: false, catAxisTitle: 'Hour of day', showCatAxisTitle: true, catAxisTitleFontSize: 10,
          ...chartAxisOpts,
        }
      );
      br.addChart(
        CT.bar,
        [{
          name: 'Activity weight',
          labels: DAY_LABELS_PPT,
          values: dowWeights.map((w) => Math.round(w * 10) / 10),
        }],
        {
          x: 8.1, y: 1.35, w: 4.7, h: 4.6,
          chartColors: [colors.primary],
          showLegend: false,
          ...chartAxisOpts,
        }
      );
      br.addText('Composite rhythm from route schedules, recurring patterns, digital activity, and detector events.', {
        x: 0.5, y: 6.15, w: 12.3, h: 0.3, fontSize: 10, italic: true, color: colors.lightText,
      });
    }

    // ── TOP LOCATIONS (exec + full) ──
    if (template !== 'anomaly_digest' && topLocations.length > 0) {
      const tls = addContentSlide('Visit Frequency — Top Locations');
      tls.addChart(
        CT.bar,
        [{
          name: 'Visits',
          labels: topLocations.map((l) => truncate(l.name, 34)),
          values: topLocations.map((l) => l.visits),
        }],
        {
          x: 0.5, y: 1.35, w: 12.3, h: 4.9,
          barDir: 'bar',
          chartColors: [colors.accent],
          showLegend: false, showValue: true, dataLabelFontSize: 9,
          ...chartAxisOpts,
        }
      );
    }
  }

  // ── GEOSPATIAL OVERVIEW (static map — all templates when geodata exists) ──
  const mapDataUrl = await buildPolStaticMapDataUrl(routeRows, dwellRows);
  if (mapDataUrl) {
    const ms = addContentSlide('Geospatial Overview');
    ms.addImage({ data: mapDataUrl, x: 0.5, y: 1.3, w: 12.3, h: 5.3, sizing: { type: 'contain', w: 12.3, h: 5.3 } });
    ms.addText(
      `All recurring routes (blue, thicker = more trips) and most-visited locations (red) · ${routeRows.length} routes, ${dwellRows.length} dwell locations in window`,
      { x: 0.5, y: 6.7, w: 12.3, h: 0.3, fontSize: 10, italic: true, color: colors.lightText }
    );
  }

  // Scores follow the analysis types in this export. Social is digital
  // activity. Anomaly is the detector / atypicality sections.
  const showRoutine = included.some((s) => ['dwells', 'repeated_routes', 'patterns', 'sequences'].includes(s));
  const showMobility = included.some((s) => ['dwells', 'repeated_routes'].includes(s));
  const showSocial = included.includes('digital');
  const showAnomaly = included.some((s) => ['anomalies', 'atypicality'].includes(s));
  const showDwells = included.includes('dwells');
  const showLocations = included.some((s) => ['dwells', 'sequences'].includes(s));
  const showRoutes = included.includes('repeated_routes');
  const showContacts = included.includes('digital');
  const showAnomalyCount = showAnomaly;

  const scopeAssessment = (text: string) => text.split('|').map((part) => part.trim()).filter((part) => {
    const low = part.toLowerCase();
    if (low.startsWith('communication:') && !showSocial) return false;
    if (low.includes('anomal') && !showAnomaly) return false;
    return part.length > 0;
  }).join(' | ');

  if (showRoutine || showMobility || showSocial || showAnomaly) {
    const sk = addContentSlide('How to Read the Scores');
    sk.addText(
      'Only the scores that belong to the analysis types in this export are shown. Each one describes the entity for the reporting window.',
      { x: 0.5, y: 1.25, w: 12.3, h: 0.6, fontSize: 14, color: colors.text }
    );
    const scoreLines: any[] = [];
    if (showRoutine) {
      scoreLines.push(
        { text: 'Routine. ', options: { bold: true } },
        { text: 'How concentrated the stops are. Higher means the same places are visited again and again. It is 1 minus distinct locations divided by dwells. For example, 308 dwells at 182 locations scores 41%. On a dwell export, this is the score taken from that list.', options: { breakLine: true } },
      );
    }
    if (showMobility) {
      scoreLines.push(
        { text: 'Mobility. ', options: { bold: true } },
        { text: 'How much ground the entity covered. It reaches 100% at about 60 miles of travel. A large set of distinct stop locations raises it as well.', options: { breakLine: true } },
      );
    }
    if (showSocial) {
      scoreLines.push(
        { text: 'Social. ', options: { bold: true } },
        { text: 'Calls, texts, and distinct contacts from Digital Patterns. For example, 11 contacts and 11 calls scores about 14%.', options: { breakLine: true } },
      );
    }
    if (showAnomaly) {
      scoreLines.push(
        { text: 'Anomaly. ', options: { bold: true } },
        { text: 'How far this window departs from the entity\'s own baseline. Shown because this export includes anomalies or daily atypicality.', options: { breakLine: true } },
      );
    }
    sk.addText(scoreLines, {
      x: 0.5, y: 2.05, w: 12.3, h: 4.5, fontSize: 15, color: colors.text, paraSpaceAfter: 14, valign: 'top',
    });

    const scoreCols: Array<{ label: string; align?: 'left' | 'right'; width: number; value: (e: any) => string }> = [
      { label: 'Entity', width: 2.5, value: (e) => e.entity_id },
    ];
    if (showRoutine) scoreCols.push({ label: 'Routine', align: 'right', width: 1.1, value: (e) => pct(e.routine_score) });
    if (showMobility) scoreCols.push({ label: 'Mobility', align: 'right', width: 1.1, value: (e) => pct(e.mobility_score) });
    if (showSocial) scoreCols.push({ label: 'Social', align: 'right', width: 1.1, value: (e) => pct(e.social_score) });
    if (showAnomaly) scoreCols.push({ label: 'Anomaly', align: 'right', width: 1.1, value: (e) => pct(e.anomaly_score) });
    if (showDwells) scoreCols.push({ label: 'Dwells', align: 'right', width: 1.0, value: (e) => String(e.total_dwells ?? 0) });
    if (showLocations) scoreCols.push({ label: 'Locations', align: 'right', width: 1.2, value: (e) => String(e.unique_locations ?? 0) });
    if (showRoutes) scoreCols.push({ label: 'Routes', align: 'right', width: 1.0, value: (e) => String(e.total_routes ?? 0) });
    if (showContacts) scoreCols.push({ label: 'Contacts', align: 'right', width: 1.1, value: (e) => String(e.unique_contacts ?? 0) });
    if (showAnomalyCount) scoreCols.push({ label: 'Anomalies', align: 'right', width: 1.1, value: (e) => String(e.anomaly_count ?? 0) });

    addTableSlides(
      'Entity Behavioral Scores',
      scoreCols.map((c) => ({ label: c.label, align: c.align })),
      report.entities.map((e: any) => scoreCols.map((c) => c.value(e))),
      scoreCols.map((c) => c.width),
    );
  }

  // Behavioral assessments (AI summaries)
  const assessed = report.entities.filter((e: any) => e.ai_summary);
  if (assessed.length > 0 && template !== 'anomaly_digest') {
    addTableSlides(
      'Behavioral Assessments',
      [{ label: 'Entity' }, { label: 'Assessment' }],
      assessed.map((e: any) => [e.entity_id, truncate(scopeAssessment(e.ai_summary), 220)]),
      [2.3, 10.0]
    );
  }

  // Section row builders (shared by full_report / digest / executive)
  const sectionRowBuilders: Record<
    string,
    { header: Array<{ label: string; align?: 'left' | 'center' | 'right' }>; colW: number[]; toRow: (r: any) => string[] }
  > = {
    repeated_routes: {
      header: [
        { label: 'Entity' }, { label: 'Route' }, { label: 'Trips', align: 'right' },
        { label: 'Consistency', align: 'right' }, { label: 'Peak' },
        { label: 'Distance', align: 'right' }, { label: 'Avg Duration', align: 'right' },
      ],
      colW: [1.6, 4.6, 0.9, 1.3, 1.6, 1.2, 1.1],
      toRow: (r) => [
        r.entity_id,
        truncate(r.route_name || `${r.origin?.name || 'Origin'} → ${r.destination?.name || 'Destination'}`, 60),
        String(r.usage_count ?? 0),
        pct(r.consistency_score),
        `${r.peak_day || ''} ${r.peak_hour != null ? `${r.peak_hour}:00` : ''}`.trim() || '—',
        fmtMiles(r.avg_distance_meters),
        fmtDuration(r.avg_duration_seconds),
      ],
    },
    dwells: {
      header: [
        { label: 'Entity' }, { label: 'Location' }, { label: 'Type' },
        { label: 'Visits', align: 'right' }, { label: 'Total Time', align: 'right' },
        { label: 'Avg Stay', align: 'right' }, { label: 'Device' },
      ],
      colW: [1.6, 4.4, 1.6, 0.9, 1.3, 1.2, 1.3],
      toRow: (d) => [
        d.entity_id, truncate(d.location_name, 55), d.place_type || '—',
        String(d.visit_count ?? 0), fmtDuration(d.total_duration_seconds),
        fmtDuration(d.avg_duration_seconds), d.device_type || '—',
      ],
    },
    anomalies: {
      header: [
        { label: 'Entity' }, { label: 'Type' }, { label: 'Severity' },
        { label: 'When' }, { label: 'Expected' }, { label: 'Actual' },
      ],
      colW: [1.5, 1.9, 1.0, 1.8, 3.0, 3.1],
      toRow: (a) => [
        a.entity_id,
        (a.anomaly_type || '').replace(/_/g, ' '),
        (a.severity || 'medium').toUpperCase(),
        fmtDt(a.anomaly_time),
        truncate(a.expected_behavior, 70),
        truncate(a.actual_behavior, 70),
      ],
    },
    patterns: {
      header: [
        { label: 'Entity' }, { label: 'Type' }, { label: 'Pattern' }, { label: 'Frequency' },
        { label: 'Window' }, { label: 'Seen', align: 'right' }, { label: 'Confidence', align: 'right' },
      ],
      colW: [1.5, 1.6, 3.6, 1.5, 1.6, 0.9, 1.6],
      toRow: (p) => [
        p.entity_id,
        (p.pattern_type || '').replace(/_/g, ' '),
        truncate(p.location_name || p.route_name, 45),
        String(p.frequency || '').toLowerCase(),
        p.time_window_start ? `${p.time_window_start}–${p.time_window_end}` : '—',
        String(p.occurrence_count ?? 0),
        p.confidence_score != null ? pct(p.confidence_score) : '—',
      ],
    },
    correlations: {
      header: [
        { label: 'Entities' }, { label: 'Type' }, { label: 'Strength', align: 'right' },
        { label: 'Occurrences', align: 'right' }, { label: 'Shared Locations', align: 'right' },
        { label: 'Last Observed' },
      ],
      colW: [3.6, 2.0, 1.4, 1.5, 1.9, 1.9],
      toRow: (c) => [
        `${c.entity_a_id} ↔ ${c.entity_b_id}`,
        (c.correlation_type || '').replace(/_/g, ' '),
        pct(c.correlation_strength),
        String(c.occurrence_count ?? 0),
        String((c.shared_locations || []).length),
        fmtDay(c.last_observed),
      ],
    },
    sequences: {
      header: [
        { label: 'Entity' }, { label: 'Sequence' }, { label: 'Occurrences', align: 'right' },
        { label: 'Typical Start' }, { label: 'Typical Days' },
      ],
      colW: [1.6, 6.0, 1.4, 1.5, 1.8],
      toRow: (s) => [
        s.entity_id,
        truncate((s.locations || []).map((l: any) => l.name || l.id || '?').join(' → '), 80),
        String(s.occurrence_count ?? 0),
        s.typical_start_hour != null ? `${s.typical_start_hour}:00` : '—',
        (s.typical_days || []).join(', ') || '—',
      ],
    },
    digital: {
      header: [
        { label: 'Entity' }, { label: 'Category' }, { label: 'Pattern' },
        { label: 'Peak' }, { label: 'Occurrences', align: 'right' }, { label: 'Confidence', align: 'right' },
      ],
      colW: [1.6, 1.8, 5.0, 1.5, 1.3, 1.1],
      toRow: (p) => [
        p.entity_id,
        (p.category || '').replace(/_/g, ' '),
        truncate(p.name || p.description, 60),
        `${p.peak_day || ''} ${p.peak_hour != null ? `${p.peak_hour}:00` : ''}`.trim() || '—',
        String(p.occurrence_count ?? 0),
        pct(p.confidence),
      ],
    },
    atypicality: {
      header: [
        { label: 'Entity' }, { label: 'Day' }, { label: 'Atypicality', align: 'right' },
        { label: 'Detector Events' }, { label: 'Distance', align: 'right' },
        { label: 'Places', align: 'right' }, { label: 'Dwells', align: 'right' },
      ],
      colW: [1.6, 1.6, 1.3, 4.6, 1.2, 1.0, 1.0],
      toRow: (m) => [
        m.entity_id,
        fmtDay(m.day),
        pct(m.atypicality_score),
        truncate(
          Object.entries(m.anomaly_breakdown || {})
            .filter(([, n]) => (n as number) > 0)
            .map(([t, n]) => `${t.replace(/_/g, ' ')}: ${n}`)
            .join(' · ') || '—',
          75
        ),
        `${(m.distance_traveled_km || 0).toFixed(1)} km`,
        String(m.unique_places_visited ?? 0),
        String(m.dwell_count ?? 0),
      ],
    },
  };

  const addSectionSlides = (sid: string, rows: any[], titleSuffix = '') => {
    const builder = sectionRowBuilders[sid];
    if (!builder) return;
    addTableSlides(
      `${POL_DECK_SECTION_LABELS[sid]}${titleSuffix}`,
      builder.header,
      rows.map(builder.toRow),
      builder.colW
    );
  };

  // ── TEMPLATE-SPECIFIC BODY ──
  if (template === 'executive_summary') {
    if (included.includes('anomalies')) {
      addSectionSlides('anomalies', (sections.anomalies || []).slice(0, POL_DECK_ROWS_PER_SLIDE), ' — Top Events');
    }
    if (included.includes('atypicality')) {
      addSectionSlides('atypicality', (sections.atypicality || []).slice(0, POL_DECK_ROWS_PER_SLIDE), ' — Most Atypical Days');
    }
  } else if (template === 'full_report') {
    included.forEach((sid) => addSectionSlides(sid, sections[sid] || []));
  } else if (template === 'entity_annex') {
    report.entities.forEach((e: any) => {
      const eid = String(e.entity_id);
      included.forEach((sid) => {
        const rows = (sections[sid] || []).filter((r: any) =>
          sid === 'correlations'
            ? String(r.entity_a_id) === eid || String(r.entity_b_id) === eid
            : String(r.entity_id) === eid
        );
        if (rows.length > 0) addSectionSlides(sid, rows, ` — ${eid}`);
      });
    });
  } else if (template === 'anomaly_digest') {
    if (included.includes('atypicality')) {
      addSectionSlides('atypicality', sections.atypicality || [], ' — Flagged Days');
    }
    addSectionSlides('anomalies', sections.anomalies || []);
  }

  // ── SAVE ──
  const stamp = new Date().toISOString().slice(0, 10);
  await pptx.writeFile({ fileName: `pol_${template}_job${jobId}_${stamp}.pptx` });
};
